import React, { useContext, useEffect, useState } from "react";
import SaveAndNavigate from "../../Components/SaveAndNavigate";
import {
  HTTPResponse,
  PageDesignConstants,
  RRMMCohortTreatment,
  SideMenuItems,
  ToastMessages,
  ToastMessageType,
} from "../../Helpers/Enums";
import SideBarMenu from "../../Components/SideBarMenu";
import InfoPopUp from "../../Components/InfoPopUp";
import {
  AdverseEvents_Msgs,
  CommonError_Msgs,
  hyperlinkTextAEReporting,
  TTCharacteristics_MSGS,
} from "../../Helpers/HelperText";
import PatientDetails from "../../Components/PatientInformation";
import { AppContext } from "../../Contexts/AppContextProvider";
import ErrorField from "../../Components/ErrorField";
import { Collapse } from "react-bootstrap";
import {
  CollapsibleSectionHeader,
  useCollapsibleGroups,
  AllowNumericWithDecimal,
  AllowOnlNumeric,
  DateDisplayFormat,
  GenerateKey,
  getCohortTreatmentDisplayName,
  GetEarliestDate,
  GetLocalStorageData,
  GetLoggedInUserID,
  GetOrdinal,
  IsAdmin,
  IsDateAfter,
  IsDateValid,
  isStringEmpty,
} from "../../Helpers/Utilities";
import { AdverseEventsService } from "../../WebApiServices/AdverseEvents.service";
import DatePickerComponent from "../../Components/DatePickerComponent";
import dayjs from "dayjs";

const AdverseEvents = () => {
  const navigatePaths = {
    prevPage: "/TreatmentResponseProgression",
    currPage: "/AdverseEvents",
    nextPage: "/AEReporting",
  };

  const pid = GetLocalStorageData("patientId") ?? 0;
  let patientData = GetLocalStorageData("patient");
  patientData = patientData && JSON.parse(patientData);
  const isPatientLocked = patientData?.locked ?? false;

  const { isCollapsed: isCohortCollapsed, toggleGroup: toggleCohortCollapse } =
    useCollapsibleGroups();
  // AE groups are per cohort treatment + lot, so warnings are keyed the same way.
  const AeKey = (ae) => `${ae?.cohortTreatmentId}_${ae?.lineOfTherapy ?? 0}`;
  // Row-level validation flags are named inconsistently (...err / ...Error / ...invalid / ...WarningMsg).
  const AE_FLAG_PATTERN = /(err|error|invalid|warningmsg)$/i;
  // True if the lot group has a validation error or a blocking warning anywhere in Q53-Q67.
  const AeRowHasErrors = (aeRow) => {
    const key = AeKey(aeRow);
    const hasRowFlag = Object.values(aeRow || {}).some(
      (list) =>
        Array.isArray(list) &&
        list.some((item) =>
          Object.entries(item || {}).some(
            ([flag, value]) => value === true && AE_FLAG_PATTERN.test(flag),
          ),
        ),
    );
    if (hasRowFlag) return true;
    const hasFlagValue = (flags) =>
      Object.values(flags || {}).some((v) => v === true);
    return (
      Object.values(aeQ26Warnings ?? {}).some((perQ) =>
        hasFlagValue(perQ?.[key]),
      ) ||
      hasFlagValue(q65Warnings?.[key]) ||
      hasFlagValue(q66Warnings?.[key]) ||
      hasFlagValue(q67Warnings?.[key]) ||
      hasFlagValue(q571BWarnings?.[key]) ||
      (Q63SubtypeWarnings?.[key] ?? []).length > 0 ||
      (Q63SubtypeWarningsFromQ26?.[key] ?? []).length > 0 ||
      (Q63SubtypeWarningsFromQ38?.[key] ?? []).length > 0
    );
  };
  const {
    ShowToast,
    ToggleLoader,
    HandleSessionTimeout,
    isSideMenuActive,
    setIsSideMenuActive,
  } = useContext(AppContext);
  const [showTopErrMsg, setShowTopErrMsg] = useState(false);
  const [
    showHideHyperlinkModalAEReporting,
    setShowHideHyperlinkModalAEReporting,
  ] = useState(false);
  // QAQC-043 - per-cohort Q36.1 CRS/ICANS hospitalization selections
  const [Q36CrsSelectionList, setQ36CrsSelectionList] = useState([]);
  const [Q36IcansSelectionList, setQ36IcansSelectionList] = useState([]);
  // QAQC-044 - per-cohort Q39.1.A/Q39.1.C/Q39.2/Q39.2.C/Q43.1.A CRS
  // treatment/supportive-care selections
  const [Q53CrsTreatmentSelectionList, setQ53CrsTreatmentSelectionList] =
    useState([]);
  // QAQC-045 Rule A - same 5 sources, Neurologic event
  const [
    Q63NeurotoxicityTreatmentSelectionList,
    setQ63NeurotoxicityTreatmentSelectionList,
  ] = useState([]);
  // QAQC-045 Rule B - per-cohort list of Q43.2.A neurologic-event subtype
  // codes (matching Q63's rbl_D values) that need a matching Q63 row
  const [
    Q63NeurotoxicitySubtypeSelectionList,
    setQ63NeurotoxicitySubtypeSelectionList,
  ] = useState([]);
  // QAQC-045 Rule B - per-cohort list of subtype codes selected in Q43.2.A
  // with NO matching Q63 row of that same subtype (i.e. the warning list)
  const [Q63SubtypeWarnings, setQ63SubtypeWarnings] = useState({});
  // QAQC-045 Rule C - per-cohort list of Q26/Q26.1 discontinuation-reason
  // neurologic-event subtype codes (matching Q63's rbl_D values) that need
  // a matching Q63 row
  const [
    Q63NeurotoxicitySubtypeSelectionListFromQ26,
    setQ63NeurotoxicitySubtypeSelectionListFromQ26,
  ] = useState([]);
  // QAQC-045 Rule C - per-cohort list of subtype codes selected in Q26/Q26.1
  // with NO matching Q63 row of that same subtype (i.e. the warning list)
  const [Q63SubtypeWarningsFromQ26, setQ63SubtypeWarningsFromQ26] = useState(
    {},
  );
  // QAQC-045 Rule D - per-cohort list of Q38.1.A/Q38.2.A dose-change-reason
  // neurologic-event subtype codes (matching Q63's rbl_D values) that need
  // a matching Q63 row
  const [
    Q63NeurotoxicitySubtypeSelectionListFromQ38,
    setQ63NeurotoxicitySubtypeSelectionListFromQ38,
  ] = useState([]);
  // QAQC-045 Rule D - per-cohort list of subtype codes selected in
  // Q38.1.A/Q38.2.A with NO matching Q63 row of that same subtype (i.e. the
  // warning list)
  const [Q63SubtypeWarningsFromQ38, setQ63SubtypeWarningsFromQ38] = useState(
    {},
  );

  const [adverseEventWarningTec, setAdverseEventWarningTec] = useState(false);
  const [adverseEventWarningTal, setAdverseEventWarningTal] = useState(false);
  const [adverseEventWarningElr, setAdverseEventWarningElr] = useState(false);
  const [adverseEventWarningLin, setAdverseEventWarningLin] = useState(false);
  const [adverseEventWarningCarvykti, setAdverseEventWarningCarvykti] =
    useState(false);
  const [adverseEventWarningAbecma, setAdverseEventWarningAbecma] =
    useState(false);
  const [endOfFollowUpDate, setEndOfFollowUpDate] = useState("");
  const [mmDiagnosisDate, setMmDiagnosisDate] = useState("");
  const [isNewEcrfA, setIsNewEcrfA] = useState(false);
  const [isNewEcrfB, setIsNewEcrfB] = useState(false);
  const [isNewEcrfC, setIsNewEcrfC] = useState(false);

  const q53_crs_row = {
    crsId: null,
    rbl_B: null,
    date_B: null,
    rbl_C: null,

    rbl_resolution: null,
    date_resolution: null,

    resolutionemptyerr: false,
    resolutiondateemptyerr: false,
    resolutiondateinvalid: false,
    resolutiondaterangeerr: false, // Validates: index date ≤ onset date ≤ resolution date ≤ AE end date

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false, //Date before Index
    B_date_after_err: false, //Date after AE End
    B_date_equal_prev_ep_err: false, //Date same as previous episode
    B_date_invalid: false,
    B_date_after_eof: false,
    Q53q36WarningMsg: false,
    Q53q39WarningMsg: false,
    C_empty_err: false,
    ae_ManagementAeWarningTec: false,
  };
  const q54_icans_row = {
    icansId: null,
    rbl_B: null,
    date_B: null,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,
    Q54q36WarningMsg: false,
    // C_empty_err: false

    //  SEVERITY FIELD
    rblC: null,
    Cemptyerr: false,

    //RESOLUTION FIELDS (for isNewEcrfA only)
    rblresolution: null,
    dateresolution: null,
    resolutionemptyerr: false,
    resolutiondateemptyerr: false,
    resolutiondateinvalid: false,
    resolutiondaterangeerr: false,
  };

  const q55_neutropenia_row = {
    neutropeniaId: null,
    rbl_B: null,
    date_B: null,
    // Date of resolution [C] retired
    // rbl_C: null,
    // date_C: null,

    //severity - Update A
    rblD: null,
    Demptyerr: false,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    // Date of resolution [C] retired
    // C_empty_err: false,
    // C_date_empty_err: false,
    // C_date_before_index_err: false, //Date before Index Date
    // C_date_before_onset_err: false, //Date before Onset Date
    // C_date_after_err: false, //Date after AE End Date
    // C_date_invalid: false,
    // C_date_before_eof: false,
  };
  const q56_cytopenia_row = {
    cytopeniaId: null,
    rbl_B: null,
    date_B: null,
    // Date of resolution [C] retired
    // rbl_C: null,
    // date_C: null,

    // Severity - Update A
    rblD: null,
    Demptyerr: false,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    // Date of resolution [C] retired
    // C_empty_err: false,
    // C_date_empty_err: false,
    // C_date_before_index_err: false, //Date before Index Date
    // C_date_before_onset_err: false, //Date before Onset Date
    // C_date_after_err: false, //Date after AE End Date
    // C_date_invalid: false,
    // C_date_before_eof: false,
  };
  const q57_infection_row = {
    infectionId: null,
    infectionKey: null,
    rbl_B: null,
    date_B: null,

    // Date of resolution (Element C only)
    rbl_resolution: null,
    date_resolution: null,
    resolutionemptyerr: false,
    resolutiondateemptyerr: false,
    resolutiondateinvalid: false,
    resolutiondaterangeerr: false,

    chk_C_1: false,
    chk_C_2: false,
    chk_C_3: false,
    rb_C: false,
    // Infection organism [D] retired
    // chk_D_1: false,
    // chk_D_2: false,
    // chk_D_3: false,
    // chk_D_4: false,
    // chk_D_5: false,
    // chk_D_6: false,
    // chk_D_7: false,
    // chk_D_8: false,
    // chk_D_9: false,
    // chk_D_10: false,
    // chk_D_11: false,
    // chk_D_12: false,
    // chk_D_13: false,
    // chk_D_14: false,
    // chk_D_15: false,
    // txt_D_oth: "",
    // rb_D: false,
    rbl_E: null,
    rbl_F: null,

    chk_G_1: false,
    chk_G_2: false,
    chk_G_3: false,
    chk_G_4: false,
    chk_G_5: false,
    rb_G_unk: null,
    txt_G_oth: "",

    // Date of most recent IgG assessment prior to infection (Element C only)
    rbl_H: null,
    date_H: null,
    H_empty_err: false,
    H_date_empty_err: false,
    H_date_invalid: false,
    H_date_before_diagnosis_err: false,
    H_date_after_onset_err: false,

    // IgG level (Element C only)
    rbl_I: null,
    unit_I: "",
    I_empty_err: false,
    I_unit_empty_err: false,
    I_unit_invalid_err: false,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_before_diagnosis_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    C_empty_err: false,
    // Infection organism [D] retired
    // D_empty_err: false,
    // D_txt_empty_err: false,
    E_empty_err: false,
    F_empty_err: false,
    G_empty_err: false,
    G_txt_empty_err: false,
  };
  // Q58.C - Weight trajectory (Tec/Tal only, shown when Element C is enabled).
  // Rows are fully auto-generated (see BuildQ58Meta) from the Bispecifics/CAR-T
  // index date - the abstractor cannot add or remove rows.
  const q58_weight_row = {
    weightRowKey: null,
    aE_WeightTrajectoryId: 0, // DB primary key (0 = not yet saved), matched by targetDate_B on load
    rowNumber: null, // "N" - 1-based row number used to compute targetDate_B (not displayed)
    targetDate_B: null, // Target date for assessment (auto-populated, MM/DD/YYYY string)
    windowDays: null, // 45 for Tec, 15 for Tal
    windowStartDays: null, // window lower bound: 45 for Tec, 14 for Tal
    periodEndDate: null, // MIN(index date + 365, subsequent LOT start, end of follow-up)
    cohortIndexDateStr: null,
    touched: false, // set once the abstractor interacts with this row's column C

    rbl_C: null, // 1 = not assessed within window, 2 = date entered, 99 = date unknown
    date_C: null,

    rbl_D: null, // 1 = kg, 2 = lbs, 99 = unknown
    weightKg_D: "",
    weightLbs_D: "",

    C_empty_err: false,
    C_date_empty_err: false,
    C_date_invalid: false,
    C_date_before_index_err: false,
    C_date_after_err: false,
    C_date_not_increasing_err: false,
    C_date_outside_window_err: false,

    D_empty_err: false,
    D_kg_range_err: false,
    D_lbs_range_err: false,
  };

  // Q58.C row/date auto-generation. Dates are plain "MM/DD/YYYY" strings parsed/
  // formatted with native Date (matching this file's existing date-handling
  // convention, e.g. Utilities.js `parseDate`) rather than dayjs, since dayjs
  // isn't configured here with the customParseFormat plugin that strict
  // format-string parsing/comparison would require.
  const ParseMDY = (dateString) => {
    if (isStringEmpty(dateString)) return null;
    const parts = String(dateString).split("/");
    if (parts.length !== 3) return null;
    const [month, day, year] = parts;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  };

  const FormatMDY = (date) =>
    `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
      .getDate()
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;

  const AddDaysToDate = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const DiffInDays = (laterDate, earlierDate) =>
    Math.round(
      (laterDate.getTime() - earlierDate.getTime()) / (1000 * 60 * 60 * 24),
    );

  // "days in assessment period" = MIN(index date + 365 days, end date of LOT in
  // Q26.2, start date of subsequent LOT if reported in Q26/Q26.1/Q26.2/Q27/Q31, end
  // of follow-up) - index date. That MIN(...) is computed server-side per cohort
  // (AdverseEventsService -> CommonService.CalculateAEEndDate, checking the cohort's
  // own Q26.2 LOT end date, the next-LOT-start date resolved from Q26/26.1/26.2/27/31,
  // and end of follow-up) and passed down as `backendPeriodEndDate`; index date + 365
  // is only recomputed here as a fallback if that value is missing.
  // Returns null when the question isn't applicable to this cohort (not Tec/Tal,
  // or no valid index date); otherwise the cohort-level period info (used in the
  // question description regardless of row count) plus the generated `rows`
  // (empty when "days in assessment period" < the interval).
  const BuildQ58Meta = (
    cohortIndexDate,
    backendPeriodEndDate,
    isTec,
    isTal,
  ) => {
    if (!isTec && !isTal) return null;

    const indexDate = ParseMDY(cohortIndexDate);
    if (!indexDate) return null;

    const cap365 = AddDaysToDate(indexDate, 365);
    const backendBoundary = ParseMDY(backendPeriodEndDate);
    // Backend already computes the full MIN(...) including index date + 365, so
    // backendBoundary should never be later than cap365 - the comparison here is
    // just a defensive fallback in case that value is ever missing.
    const periodEnd =
      backendBoundary && backendBoundary.getTime() < cap365.getTime()
        ? backendBoundary
        : cap365;

    const daysInPeriod = DiffInDays(periodEnd, indexDate);
    const intervalDays = isTec ? 91 : 30;
    const windowDays = isTec ? 45 : 15;
    // Tal's lower bound is 14 days (not 15) so consecutive windows don't overlap.
    const windowStartDays = isTec ? 45 : 14;
    const indexDateStr = FormatMDY(indexDate);
    const periodEndStr = FormatMDY(periodEnd);

    const rowCount = Math.floor(daysInPeriod / intervalDays);
    const rows = [];
    for (let n = 1; n <= rowCount; n++) {
      const targetDate = AddDaysToDate(indexDate, intervalDays * n);
      rows.push({
        ...q58_weight_row,
        weightRowKey: GenerateKey(),
        rowNumber: n,
        targetDate_B: FormatMDY(targetDate),
        windowDays,
        windowStartDays,
        periodEndDate: periodEndStr,
        cohortIndexDateStr: indexDateStr,
      });
    }

    return {
      rows,
      indexDateStr,
      periodEndStr,
      windowDays,
      intervalLabel: isTec ? "3-month interval" : "1-month interval",
    };
  };

  // Assessment window for a Q58 row's target date [B]: [targetDate - windowDays,
  // MIN(targetDate + windowDays, periodEndDate)]. Shared by the column B display
  // text and by ValidateQ58Rows' date_C bounds check.
  const GetQ58Window = (row) => {
    const targetDate = ParseMDY(row.targetDate_B);
    if (!targetDate) return null;
    const periodEnd = ParseMDY(row.periodEndDate);
    const windowStart = AddDaysToDate(
      targetDate,
      -(row.windowStartDays ?? row.windowDays),
    );
    const windowEndCap = AddDaysToDate(targetDate, row.windowDays);
    const windowEnd =
      periodEnd && periodEnd.getTime() < windowEndCap.getTime()
        ? periodEnd
        : windowEndCap;
    return { windowStart, windowEnd };
  };

  // Validates date_C for a Q58 row: required (unless "not assessed"/"unknown"),
  // valid, on/after the cohort index date, on/before the period end date, within
  // [targetDate - windowDays, MIN(targetDate + windowDays, periodEndDate)], and
  // on/after the previous row's date_C (monotonically increasing across rows).
  const ValidateQ58Rows = (rows) => {
    let prevDate = null;
    return rows.map((row) => {
      let C_empty_err = false;
      let C_date_empty_err = false;
      let C_date_invalid = false;
      let C_date_before_index_err = false;
      let C_date_after_err = false;
      let C_date_outside_window_err = false;
      let C_date_not_increasing_err = false;

      if (row.touched && row.rbl_C === null) {
        C_empty_err = true;
      } else if (row.rbl_C === 2) {
        if (isStringEmpty(row.date_C)) {
          C_date_empty_err = true;
        } else {
          const parsedDate = ParseMDY(row.date_C);
          if (!parsedDate) {
            C_date_invalid = true;
          } else {
            const indexDate = ParseMDY(row.cohortIndexDateStr);
            const periodEnd = ParseMDY(row.periodEndDate);
            const window = GetQ58Window(row);

            if (indexDate && parsedDate.getTime() < indexDate.getTime()) {
              C_date_before_index_err = true;
            }
            if (periodEnd && parsedDate.getTime() > periodEnd.getTime()) {
              C_date_after_err = true;
            }
            // window.windowEnd/windowStart are already clamped to periodEnd/
            // indexDate, so a date outside those bounds is always also outside
            // the window - only surface the more specific before-index/after-
            // period error in that case, not both.
            if (window && !C_date_before_index_err && !C_date_after_err) {
              if (
                parsedDate.getTime() < window.windowStart.getTime() ||
                parsedDate.getTime() > window.windowEnd.getTime()
              ) {
                C_date_outside_window_err = true;
              }
            }
            if (prevDate && parsedDate.getTime() < prevDate.getTime()) {
              C_date_not_increasing_err = true;
            }
            if (!C_date_before_index_err && !C_date_invalid) {
              prevDate = parsedDate;
            }
          }
        }
      }

      return {
        ...row,
        C_empty_err,
        C_date_empty_err,
        C_date_invalid,
        C_date_before_index_err,
        C_date_after_err,
        C_date_outside_window_err,
        C_date_not_increasing_err,
      };
    });
  };

  const q59_dysgeusia_row = {
    dysgeusiaId: null,
    rbl_B: null,
    date_B: null,
    rbl_C: null,
    date_C: null,

    chk_CC_1: false,
    chk_CC_1_P: false,
    chk_CC_1_T: false,
    chk_CC_2: false,
    chk_CC_2_P: false,
    chk_CC_2_T: false,
    chk_CC_3: false,
    chk_CC_3_P: false,
    chk_CC_3_T: false,
    chk_CC_4: false,
    chk_CC_4_P: false,
    chk_CC_4_T: false,
    chk_CC_5: false,
    chk_CC_5_P: false,
    chk_CC_5_T: false,
    chk_CC_6: false,
    chk_CC_6_P: false,
    chk_CC_6_T: false,
    chk_CC_7: false,
    chk_CC_7_P: false,
    chk_CC_7_T: false,
    txt_CC_oth: "",
    rb_CC: false,

    rbl_D: null,
    date_D: null,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    CC_1_empty_err: false,
    CC_2_empty_err: false,
    CC_3_empty_err: false,
    CC_4_empty_err: false,
    CC_5_empty_err: false,
    CC_6_empty_err: false,
    CC_7_empty_err: false,
    CC_empty_err: false,
    CC_txt_empty_err: false,

    C_empty_err: false,
    C_date_empty_err: false,
    C_date_before_index_err: false,
    C_date_before_onset_err: false,
    C_date_after_err: false,
    C_date_invalid: false,
    C_date_before_eof: false,

    D_empty_err: false,
    D_date_empty_err: false,
    D_date_before_index_err: false,
    D_date_before_onset_err: false,
    D_date_before_impr_err: false,
    D_date_after_err: false,
    D_date_invalid: false,
    D_date_before_eof: false,
  };
  const q60_nail_row = {
    nailDisorderId: null,
    rbl_B: null,
    date_B: null,
    rbl_C: null,
    date_C: null,
    rbl_D: null,
    date_D: null,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    C_empty_err: false,
    C_date_empty_err: false,
    C_date_before_index_err: false,
    C_date_before_onset_err: false,
    C_date_after_err: false,
    C_date_invalid: false,
    C_date_before_eof: false,

    D_empty_err: false,
    D_date_empty_err: false,
    D_date_before_index_err: false,
    D_date_before_onset_err: false,
    D_date_before_impr_err: false,
    D_date_after_err: false,
    D_date_invalid: false,
    D_date_before_eof: false,
  };
  const q61_skin_row = {
    skinDisorderId: null,
    rbl_B: null,
    date_B: null,
    rbl_C: null,
    date_C: null,
    rbl_D: null,
    date_D: null,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_before_eof: false,

    C_empty_err: false,
    C_date_empty_err: false,
    C_date_before_index_err: false,
    C_date_before_onset_err: false,
    C_date_after_err: false,
    C_date_invalid: false,
    C_date_before_eof: false,

    D_empty_err: false,
    D_date_empty_err: false,
    D_date_before_index_err: false,
    D_date_before_onset_err: false,
    D_date_before_impr_err: false,
    D_date_after_err: false,
    D_date_invalid: false,
    D_date_before_eof: false,
  };
  const q62_spm_row = {
    cohortTreatmentId: null,
    spmId: null,
    rbl_B: null,
    date_B: null,
    q62_rbl_C: null,
    q62_date_C: null,
    q62_severity: null,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,

    //C
    q62CEmptyError: false,
    q62CDateEmptyError: false,
    q62CDateInvalidError: false,
    q62CDateBeforeIndexError: false,
    q62CDateBeforeOnsetError: false,
    q62CDateAfterAEDateError: false,

    //D
    q62DEmptyError: false,
  };

  const q63_neurotoxicity_row = {
    neurotoxicityId: null,
    rbl_B: null,
    date_B: null,
    rbl_C: null,
    date_C: null,
    rbl_D: null,
    txt_D_oth: "",

    rbl_E: null,
    chk_F_1: false, // outpatient
    chk_F_2: false,
    rb_F: false,
    chk_G_1: false,
    chk_G_2: false,
    chk_G_3: false,
    chk_G_4: false,
    chk_G_5: false,
    rb_G: false,

    B_empty_err: false,
    B_date_empty_err: false,
    B_date_before_err: false,
    B_date_after_err: false,
    B_date_equal_prev_ep_err: false,
    B_date_invalid: false,
    B_date_after_eof: false,
    Q63q39WarningMsg: false,

    C_empty_err: false,
    C_date_empty_err: false,
    C_date_before_index_err: false, //Date before Index Date
    C_date_before_onset_err: false, //Date before Onset Date
    C_date_after_err: false, //Date after AE End Date
    C_date_invalid: false,
    C_date_before_eof: false,

    D_empty_err: false,
    D_txt_empty_err: false,

    E_empty_err: false,
    F_empty_err: false,
    G_empty_err: false,

    // Column F - Management Setting (UPDATED)
    chkFInpatient: false,
    chkFEmergencyRoom: false,
    chkFIntensiveCareUnit: false,
    chkFUrgentCareVisit: false,
    chkFOther: false,

    // Column H - Earliest Date of Improvement
    rblH: null,
    dateH: null,

    // Column H validation errors (NEW)
    Hemptyerr: false,
    Hdateemptyerr: false,
    Hdateinvalid: false,
    Hdatebeforeindexerr: false,
    Hdatebeforeonseterr: false,
    Hdatebeforereserr: false,
    Hdateaftererr: false,
    Hdatebeforeeof: false,

    isAutoPopulated: false,
    sourceQ54Date: null,
    sourceQ54IcansId: null,
    sourceQ54Index: null,
  };

  //Q64
  const q64_enterocolitis_row = {
    adverseevententerocolitisid: null,
    patientId: null,
    cohortTreatmentId: null,
    q64_rbl_B: null,
    q64_date_B: null,
    q64_rbl_C: null,
    q64_date_C: null,
    q64_rbl_D: null,
    q64_date_D: null,
    q64_chk_E_1: false,
    q64_chk_E_2: false,
    q64_chk_E_3: false,
    q64_chk_E_4: false,
    q64_chk_E_5: false,
    q64_chk_E_othTxt: "",
    q64_E_unk: false,
    q64_severity: null,

    //B Column
    q64BEmptyError: false,
    q64BDateEmptyError: false,
    q64BDateInvalidError: false,
    q64BDateEqualPrevError: false,
    q64BDateBeforeIndexDateError: false,
    q64BDateAfterCDateError: false,
    q64BDateAfterResolutionDateError: false,
    q64BDateAfterAEDateError: false,

    //C Column
    q64CEmptyError: false,
    q64CDateEmptyError: false,
    q64CDateInvalidError: false,
    q64CDateBeforeError: false,
    q64CDateAfterResolutionDateError: false,
    q64CDateAfterAEDateError: false,

    //D Column
    q64DEmptyError: false,
    q64DDateEmptyError: false,
    q64DDateInvalidError: false,
    q64DDateBeforeError: false,
    q64DDateAfterAEDateError: false,

    //E Column
    q64EEmptyError: false,
    q64EOtherTxtEmptyError: false,

    //F
    q64FEmptyError: false,
  };

  //Q65
  const q65_lymphopenia_row = {
    adverseeventlymphopeniaid: null,
    patientId: null,
    cohortTreatmentId: null,
    q65_rbl_B: null,
    q65_date_B: null,
    // Date of resolution [C] retired
    // q65_rbl_C: null,
    // q65_date_C: null,
    q65_severity: null,

    //B Column
    q65BEmptyError: false,
    q65BDateEmptyError: false,
    q65BDateInvalidError: false,
    q65BDateEqualPrevError: false,
    q65BDateBeforeIndexDateError: false,
    q65BDateAfterAEDateError: false,

    // Date of resolution [C] retired
    //C Column
    // q65CEmptyError: false,
    // q65CDateEmptyError: false,
    // q65CDateInvalidError: false,
    // q65CDateBeforeError: false,
    // q65CDateAfterAEDateError: false,

    //D Column
    q65DEmptyError: false,
  };

  //Q66
  const q66_dizziness_row = {
    adverseeventdizzinessid: null,
    patientId: null,
    cohortTreatmentId: null,
    q66_rbl_B: null,
    q66_date_B: null,
    q66_rbl_C: null,
    q66_date_C: null,

    //B Column
    q66BEmptyError: false,
    q66BDateEmptyError: false,
    q66BDateInvalidError: false,
    q66BDateEqualPrevError: false,
    q66BDateBeforeIndexDateError: false,
    q66BDateAfterAEDateError: false,

    //C Column
    q66CEmptyError: false,
    q66CDateEmptyError: false,
    q66CDateInvalidError: false,
    q66CDateBeforeError: false,
    q66CDateAfterAEDateError: false,
  };

  //Q67
  const q67_ataxia_row = {
    adverseeventataxiaid: null,
    patientId: null,
    cohortTreatmentId: null,
    q67_rbl_B: null,
    q67_date_B: null,
    q67_rbl_C: null,
    q67_date_C: null,
    q67_severity: null,

    //Presentation of ataxia [E]
    q67_chk_E_1: false,
    q67_chk_E_2: false,
    q67_chk_E_3: false,
    q67_chk_E_4: false,
    q67_chk_E_5: false,
    q67_chk_E_6: false,
    q67_chk_E_7: false,
    q67_chk_E_8: false,
    q67_chk_E_othTxt: "",
    q67_E_unk: false,

    //Interventions for managing ataxia event [F]
    q67_chk_F_1: false,
    q67_chk_F_2: false,
    q67_chk_F_3: false,
    q67_chk_F_4: false,
    q67_chk_F_5: false,
    q67_chk_F_6: false,
    q67_chk_F_7: false,
    q67_chk_F_othTxt: "",
    q67_F_unk: false,

    //B Column
    q67BEmptyError: false,
    q67BDateEmptyError: false,
    q67BDateInvalidError: false,
    q67BDateEqualPrevError: false,
    q67BDateBeforeIndexDateError: false,
    q67BDateAfterAEDateError: false,

    //C Column
    q67CEmptyError: false,
    q67CDateEmptyError: false,
    q67CDateInvalidError: false,
    q67CDateBeforeError: false,
    q67CDateAfterAEDateError: false,

    //D Column (severity)
    q67DEmptyError: false,

    //E Column
    q67EEmptyError: false,
    q67EOtherTxtEmptyError: false,

    //F Column
    q67FEmptyError: false,
    q67FOtherTxtEmptyError: false,
    q67F1MismatchError: false,
    q67F2MismatchError: false,
    q67F3MismatchError: false,
  };

  //Q57.1.B
  const q571B_immunoglobin_row = {
    adverseeventimmunoglobinid: null,
    patientId: null,
    cohortTreatmentId: null,
    q571B_rbl_B: null,
    q571B_date_B: null,
    q571B_rbl_C: null,
    q571B_unit_C: null,
    q571B_episode_D: null,

    //B Column
    q571BEmptyError: false,
    q571BDateEmptyError: false,
    q571BDateInvalidError: false,
    q571BDateEqualPrevError: false,
    q571BDateBeforeIndexDateError: false,
    q571BDateAfterDateError: false,

    //C Column
    q571CEmptyError: false,
    q571CUnitEmptyError: false,
    q571CUnitInvalidError: false,

    //D Column
    q571DEmptyError: false,
  };

  const [q65QLymphopheniaSelectionList, setQ65LymphopheniaSelectionList] =
    useState([]);

  const [q571BWarnings, setQ571BWarnings] = useState({});

  const [q65Warnings, setQ65Warnings] = useState({});

  const [q66QDizzinessSelectionList, setQ66DizzinessSelectionList] = useState(
    [],
  );

  const [q66Warnings, setQ66Warnings] = useState({});

  const [q67QAtaxiaSelectionList, setQ67AtaxiaSelectionList] = useState([]);

  const [q67Warnings, setQ67Warnings] = useState({});

  // QAQC-045 Rule B - Q63's rbl_D subtype codes and their display labels,
  // matching the radio options rendered below (id=2/ICANS intentionally has
  // no counterpart in Q43.2.A's subtype list and is never used by this rule).
  const Q63SubtypeLabels = {
    1: "Peripheral neuropathy",
    3: "Fever",
    4: "Delayed neurotoxicity",
    5: "Parkinsonism",
    6: "Cranial nerve palsy",
    7: "Other neurologic event",
    8: "Guillain-Barre Syndrome",
  };

  // QAQC-040 - AE-vs-discontinuation-reason (Q26/Q26.1) consistency for
  // Q53/Q54/Q55/Q56/Q57/Q59/Q60/Q61/Q62/Q63/Q64. Each entry below maps a
  // question number to { listField, radioField, responseKey } so a single
  // generic validator/loader can cover all of them without duplicating the
  // per-question functions used for Q65/Q66/Q67 above.
  const AeQ26ReasonConfig = {
    53: {
      listField: "q53_crs_list",
      radioField: "rbl_B",
      responseKey: "q53CohortCrsSelections",
      msgKey: "CrsQ26WarningMessage",
      q38MsgKey: "CrsQ38WarningMessage",
      q43MsgKey: "CrsQ43WarningMessage",
    },
    54: {
      listField: "q54_icans_list",
      radioField: "rbl_B",
      responseKey: "q54CohortIcansSelections",
      msgKey: "IcansQ26WarningMessage",
      q38MsgKey: "IcansQ38WarningMessage",
      q43MsgKey: "IcansQ43WarningMessage",
    },
    55: {
      listField: "q55_neutropenia_list",
      radioField: "rbl_B",
      responseKey: "q55CohortNeutropeniaSelections",
      msgKey: "NeutQ26WarningMessage",
      q38MsgKey: "NeutQ38WarningMessage",
      q43MsgKey: "NeutQ43WarningMessage",
    },
    56: {
      listField: "q56_cytopenia_list",
      radioField: "rbl_B",
      responseKey: "q56CohortCytopeniaSelections",
      msgKey: "CytoQ26WarningMessage",
      q38MsgKey: "CytoQ38WarningMessage",
      q43MsgKey: "CytoQ43WarningMessage",
    },
    57: {
      listField: "q57_infection_list",
      radioField: "rbl_B",
      responseKey: "q57CohortInfectionSelections",
      msgKey: "InfQ26WarningMessage",
      q38MsgKey: "InfQ38WarningMessage",
    },
    59: {
      listField: "q59_dysgeusia_list",
      radioField: "rbl_B",
      responseKey: "q59CohortDysgeusiaSelections",
      msgKey: "DysQ26WarningMessage",
      q38MsgKey: "DysQ38WarningMessage",
    },
    60: {
      listField: "q60_nail_list",
      radioField: "rbl_B",
      responseKey: "q60CohortNailDisorderSelections",
      msgKey: "NailQ26WarningMessage",
      q38MsgKey: "NailQ38WarningMessage",
    },
    61: {
      listField: "q61_skin_list",
      radioField: "rbl_B",
      responseKey: "q61CohortSkinDisorderSelections",
      msgKey: "SkinQ26WarningMessage",
      q38MsgKey: "SkinQ38WarningMessage",
    },
    62: {
      listField: "q62_spm_list",
      radioField: "rbl_B",
      responseKey: "q62CohortSpmSelections",
      msgKey: "SpmQ26WarningMessage",
      q38MsgKey: "SpmQ38WarningMessage",
      q43MsgKey: "SpmQ43WarningMessage",
      // Per the eCRF, Q62 is only cross-checked against Q43.2.A (AE
      // management) - the Q26/Q26.1 discontinuation-reason and
      // Q38.1.A/Q38.2.A dose-change checks that apply to the other
      // AE questions do not apply to Q62.
      skipQ26: true,
      skipQ38: true,
    },
    63: {
      listField: "q63_neurotoxicity_list",
      radioField: "rbl_B",
      responseKey: "q63CohortNeurotoxicitySelections",
      msgKey: "NeurotoxQ26WarningMessage",
      q38MsgKey: "NeurotoxQ38WarningMessage",
      q43MsgKey: "NeurotoxQ43WarningMessage",
    },
    64: {
      listField: "q64_enterocolitis_list",
      radioField: "q64_rbl_B",
      responseKey: "q64CohortEnterocolitisSelections",
      msgKey: "EntQ26WarningMessage",
      q38MsgKey: "EntQ38WarningMessage",
      q43MsgKey: "EntQ43WarningMessage",
      // Per the eCRF, Q64 is only cross-checked against Q43.2.A (AE
      // management) - the Q26/Q26.1 discontinuation-reason and
      // Q38.1.A/Q38.2.A dose-change checks that apply to the other
      // AE questions do not apply to Q64.
      skipQ26: true,
      skipQ38: true,
    },
  };

  const [aeQ26SelectionLists, setAeQ26SelectionLists] = useState({});
  const [aeQ26Warnings, setAeQ26Warnings] = useState({});

  const AERow = {
    cohortTreatmentId: null,
    cohortTreatmentName: "[Bispecifics/CAR-T]",
    superScriptText: "",
    cohortIndexDate: "[Bispecifics/CAR-T index date]",
    aeEndDate: "[AE end date]",

    q53_crs_list: [q53_crs_row], //Remove
    q54_icans_list: [q54_icans_row], //Remove
    q55_neutropenia_list: [q55_neutropenia_row], //Remove
    q56_cytopenia_list: [q56_cytopenia_row], //Remove
    q57_infection_list: [q57_infection_row], //Remove
    q58_weight_list: [q58_weight_row],
    q58_indexDate: null,
    q58_periodEndDate: null,
    q58_windowDays: null,
    q58_intervalLabel: null,
    q59_dysgeusia_list: [q59_dysgeusia_row], //Remove
    q60_nail_list: [q60_nail_row], //Remove
    q61_skin_list: [q61_skin_row], //Remove
    q62_spm_list: [q62_spm_row], //Secondary primary malignancy  //Remove
    q63_neurotoxicity_list: [q63_neurotoxicity_row], //Remove
    q64_enterocolitis_list: [q64_enterocolitis_row],
    q65_lymphopenia_list: [q65_lymphopenia_row],
    q66_dizziness_list: [q66_dizziness_row],
    q67_ataxia_list: [q67_ataxia_row],
    q571B_immunoglobin_list: [q571B_immunoglobin_row],
    infectionEpisodeList: [],
  };
  const [aeDetails, setAEDetails] = useState([AERow]);

  const resetImmunoglobinRow = (row) => ({
    ...row,
    q571B_rbl_B: null,
    q571B_date_B: null,
    q571B_rbl_C: null,
    q571B_unit_C: null,
    q571B_episode_D: null,
    q571BEmptyError: false,
    q571BDateEmptyError: false,
    q571BDateInvalidError: false,
    q571BDateEqualPrevError: false,
    q571BDateBeforeIndexDateError: false,
    q571BDateAfterDateError: false,
    q571CEmptyError: false,
    q571CUnitEmptyError: false,
    q571CUnitInvalidError: false,
    q571DEmptyError: false,
  });

  const rebuildInfectionEpisodesAndMappings = (parentObj, innerArr, q53) => {
    const validEpisodes = innerArr
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.rbl_B != null && item.rbl_B !== 1);

    const infectionEpisodeList = validEpisodes.map(({ item }, epIdx) => {
      const onsetText = !isStringEmpty(item.date_B) ? item.date_B : "Unknown";

      return {
        infectionKey: item.infectionKey || GenerateKey(),
        episodeLabel: `Episode ${epIdx + 1} (onset date: ${onsetText})`,
      };
    });

    let q571BList = [...(parentObj.q571B_immunoglobin_list || [])];

    if (!infectionEpisodeList.length) {
      const baseRow = q571BList[0] || {};

      q571BList = [resetImmunoglobinRow(baseRow)];
    } else {
      q571BList = q571BList.map((item) => {
        const existsInQ57 = innerArr?.some(
          (ep) => ep.infectionKey === item.q571B_episode_D && ep.rbl_B !== 1,
        );

        return existsInQ57
          ? item
          : { ...item, q571B_episode_D: null, q571DEmptyError: false };
      });
    }

    return {
      ...parentObj,
      q57_infection_list: innerArr,
      infectionEpisodeList,
      q571B_immunoglobin_list: q571BList,
      ...(q53 !== undefined && { q53_crs_list: q53 }),
    };
  };

  const HandleRepeaterFieldChange = (e) => {
    const { name, id, value } = e.target;
    const value_int = Number(value);

    const lastUnderscoreIndex = name.lastIndexOf("_");
    const parentIndex = name.substring(lastUnderscoreIndex + 1);

    let updatedArray = [...aeDetails];
    let Q53 = [...(updatedArray[parentIndex]?.q53_crs_list ?? [])];
    let Q54 = [...(updatedArray[parentIndex]?.q54_icans_list ?? [])];
    let Q55 = [...(updatedArray[parentIndex]?.q55_neutropenia_list ?? [])];
    let Q56 = [...(updatedArray[parentIndex]?.q56_cytopenia_list ?? [])];
    let Q57 = [...(updatedArray[parentIndex]?.q57_infection_list ?? [])];
    let Q59 = [...(updatedArray[parentIndex]?.q59_dysgeusia_list ?? [])];
    let Q60 = [...(updatedArray[parentIndex]?.q60_nail_list ?? [])];
    let Q61 = [...(updatedArray[parentIndex]?.q61_skin_list ?? [])];
    let Q62 = [...(updatedArray[parentIndex]?.q62_spm_list ?? [])];
    let Q63 = [...(updatedArray[parentIndex]?.q63_neurotoxicity_list ?? [])];
    let Q64 = [...(updatedArray[parentIndex]?.q64_enterocolitis_list ?? [])];
    let Q65 = [...(updatedArray[parentIndex]?.q65_lymphopenia_list ?? [])];

    switch (name) {
      //Q53  rbl_B
      case "q53_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q53_crs_list];

          let rbl_C = innerArr[id].rbl_C;
          let C_empty_err = innerArr[id].C_empty_err;

          //Delete rows if "The patient did not experience CRS" is selected and reset column C of current row
          if (value_int === 1) {
            rbl_C = null;
            C_empty_err = false;

            // RESET RESOLUTION FIELDS
            innerArr[id].rbl_resolution = null;
            innerArr[id].date_resolution = null;
            innerArr[id].resolutionemptyerr = false;
            innerArr[id].resolutiondateemptyerr = false;
            innerArr[id].resolutiondateinvalid = false;
            innerArr[id].resolutiondaterangeerr = false;

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          // if(value_int==1 &&
          //   Q54.length>1 && Q54[id].rbl_B == 1 &&
          //   Q55.length>1 && Q55[id].rbl_B == 1 &&
          //   Q56.length>1 && Q56[id].rbl_B == 1 &&
          //   Q57.length>1 && Q57[id].rbl_B == 1
          //   ){
          //     innerArr[id].ae_ManagementAeWarningTec=true;
          //   }

          // Update the specific item in the inner array
          innerArr[id] = {
            ...innerArr[id],
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            rbl_C,
            C_empty_err,
          };

          RecomputeAeQ26WarningForRow(
            53,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          // QAQC-043 / QAQC-044 - these table-level CRS warnings must only
          // be shown once the abstractor clicks Validate/Next, not live as
          // radio buttons are toggled, so they are intentionally not
          // recomputed here (see Validate()).
          for (let i = 0; i < innerArr.length; i++) {
            innerArr[i] = {
              ...innerArr[i],
              Q53q36WarningMsg: false,
              Q53q39WarningMsg: false,
            };
          }

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (innerArr[id]) {
            innerArr[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TECVAYLI
                ? adverseEventWarningTec &&
                  value_int == 1 &&
                  Q54[id]?.rbl_B == 1 &&
                  Q55[id]?.rbl_B == 1 &&
                  Q56[id]?.rbl_B == 1 &&
                  Q57[id]?.rbl_B == 1 &&
                  (isNewEcrfB ? Q65[id]?.q65_rbl_B === 1 : true)
                : cohortid == RRMMCohortTreatment.TALVEY
                  ? adverseEventWarningTal &&
                    value_int == 1 &&
                    Q54[id]?.rbl_B == 1 &&
                    Q55[id]?.rbl_B == 1 &&
                    Q57[id]?.rbl_B == 1 &&
                    Q59[id]?.rbl_B == 1 &&
                    Q60[id]?.rbl_B == 1 &&
                    Q61[id]?.rbl_B == 1 &&
                    (isNewEcrfB ? Q65[id]?.q65_rbl_B === 1 : true)
                  : cohortid == RRMMCohortTreatment.ELREXFIO
                    ? adverseEventWarningElr &&
                      value_int == 1 &&
                      Q54[id]?.rbl_B == 1 &&
                      Q55[id]?.rbl_B == 1 &&
                      Q56[id]?.rbl_B == 1 &&
                      Q57[id]?.rbl_B == 1 &&
                      (isNewEcrfB ? Q65[id]?.q65_rbl_B === 1 : true)
                    : cohortid == RRMMCohortTreatment.Linvoseltamab
                      ? adverseEventWarningLin &&
                        value_int == 1 &&
                        Q54[id]?.rbl_B == 1 &&
                        Q55[id]?.rbl_B == 1 &&
                        Q56[id]?.rbl_B == 1 &&
                        Q57[id]?.rbl_B == 1 &&
                        (isNewEcrfB ? Q65[id]?.q65_rbl_B === 1 : true)
                      : cohortid == RRMMCohortTreatment.CARVYKTI ||
                          cohortid == RRMMCohortTreatment.ABECMA
                        ? (adverseEventWarningCarvykti ||
                            adverseEventWarningAbecma) &&
                          value_int == 1 &&
                          Q54[id]?.rbl_B == 1 &&
                          Q55[id]?.rbl_B == 1 &&
                          Q56[id]?.rbl_B == 1 &&
                          Q62[id]?.rbl_B == 1 &&
                          Q63[id]?.rbl_B == 1 &&
                          (isNewEcrfA ? Q64[id]?.q64_rbl_B === 1 : true) &&
                          (isNewEcrfB ? Q65[id]?.q65_rbl_B === 1 : true)
                        : false;
          }
          // Assign the updated inner array back to the outer array
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q53_crs_list: innerArr,
          };
        }
        break;
      case "q53_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q53_crs_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            C_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q53_crs_list: innerArr,
          };
        }
        break;

      case `q53rblresolution_${id}_${parentIndex}`:
        let innerArr = [...updatedArray[parentIndex].q53_crs_list];
        innerArr[id] = {
          ...innerArr[id],
          rbl_resolution: value_int,
          date_resolution: null,
          resolutionemptyerr: false,
          resolutiondateemptyerr: false,
          resolutiondateinvalid: false,
          resolutiondaterangeerr: false,
        };
        updatedArray[parentIndex] = {
          ...updatedArray[parentIndex],
          q53_crs_list: innerArr,
        };
        break;

      //Q54
      case "q54_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q54_icans_list];
          // let rbl_C = innerArr[id].rbl_C;
          // let C_empty_err = innerArr[id].C_empty_err;

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            // rbl_C,
            // C_empty_err,
          };

          //Delete rows if "The patient did not experience ICANS" is selected
          if (value_int === 1) {
            //  rbl_C = null;
            // C_empty_err = false;

            //RESET RESOLUTION FIELDS
            innerArr[id].rblresolution = null;
            innerArr[id].dateresolution = null;
            innerArr[id].resolutionemptyerr = false;
            innerArr[id].resolutiondateemptyerr = false;
            innerArr[id].resolutiondateinvalid = false;
            innerArr[id].resolutiondaterangeerr = false;
            innerArr[id].rblC = null;
            innerArr[id].Cemptyerr = false;

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          RecomputeAeQ26WarningForRow(
            54,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          // QAQC-043 - table-level check across all rows, not just this one
          {
            const cohortTreatmentIdForIcans =
              updatedArray[parentIndex].cohortTreatmentId ?? null;
            const icansSelection = Q36IcansSelectionList?.find(
              (x) => x.cohortTreatmentId === cohortTreatmentIdForIcans,
            );
            const isQ36IcansSelected = icansSelection?.selected ?? false;
            const hasIcansEvent = innerArr.some(
              (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
            );
            const q54q36Warning = isQ36IcansSelected && !hasIcansEvent;
            for (let i = 0; i < innerArr.length; i++) {
              innerArr[i] = { ...innerArr[i], Q54q36WarningMsg: q54q36Warning };
            }
          }

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TECVAYLI
                ? adverseEventWarningTec &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B == 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1
                : cohortid == RRMMCohortTreatment.TALVEY
                  ? adverseEventWarningTal &&
                    value_int == 1 &&
                    Q53.length &&
                    Q53[id]?.rbl_B == 1 &&
                    Q55.length &&
                    Q55[id]?.rbl_B == 1 &&
                    Q57.length &&
                    Q57[id]?.rbl_B == 1 &&
                    Q59.length &&
                    Q59[id]?.rbl_B == 1 &&
                    Q60.length &&
                    Q60[id]?.rbl_B == 1 &&
                    Q61.length &&
                    Q61[id]?.rbl_B == 1
                  : cohortid == RRMMCohortTreatment.ELREXFIO ||
                      cohortid == RRMMCohortTreatment.Linvoseltamab
                    ? (adverseEventWarningElr || adverseEventWarningLin) &&
                      value_int == 1 &&
                      Q53.length &&
                      Q53[id]?.rbl_B == 1 &&
                      Q55.length &&
                      Q55[id]?.rbl_B == 1 &&
                      Q56.length &&
                      Q56[id]?.rbl_B == 1 &&
                      Q57.length &&
                      Q57[id]?.rbl_B == 1
                    : cohortid == RRMMCohortTreatment.CARVYKTI ||
                        cohortid == RRMMCohortTreatment.ABECMA
                      ? (adverseEventWarningCarvykti ||
                          adverseEventWarningAbecma) &&
                        value_int == 1 &&
                        Q53.length &&
                        Q53[id]?.rbl_B == 1 &&
                        Q55.length &&
                        Q55[id]?.rbl_B == 1 &&
                        Q56.length &&
                        Q56[id]?.rbl_B == 1 &&
                        Q62.length &&
                        Q62[id]?.rbl_B == 1 &&
                        Q63.length &&
                        Q63[id]?.rbl_B === 1 &&
                        (isNewEcrfA
                          ? Q64.length && Q64[id]?.q64_rbl_B === 1
                          : true)
                      : false;
          }

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q54_icans_list: innerArr,
            q53_crs_list: q53,
          };
          updatedArray = AutoPopulateQ63FromQ54(parentIndex, updatedArray);
        }
        break;

      //  SEVERITY CASE
      case `q54rblC_${id}_${parentIndex}`:
        {
          const parentItem = updatedArray[parentIndex];
          const innerArr = [...parentItem.q54_icans_list];
          innerArr[id] = {
            ...innerArr[id],
            rblC: value_int,
            Cemptyerr: false,
          };
          updatedArray[parentIndex] = {
            ...parentItem,
            q54_icans_list: innerArr,
          };
          updatedArray = AutoPopulateQ63FromQ54(parentIndex, updatedArray);
        }
        break;

      //RESOLUTION RADIO CASE
      case `q54rblresolution_${id}_${parentIndex}`: {
        const parentItem = updatedArray[parentIndex];
        const innerArr2 = [...parentItem.q54_icans_list];

        innerArr2[id] = {
          ...innerArr2[id],
          rblresolution: value_int,
          dateresolution: null,
          resolutionemptyerr: false,
          resolutiondateemptyerr: false,
          resolutiondateinvalid: false,
          resolutiondaterangeerr: false,
        };

        updatedArray[parentIndex] = {
          ...parentItem,
          q54_icans_list: innerArr2,
        };

        updatedArray = AutoPopulateQ63FromQ54(parentIndex, updatedArray);
        break;
      }

      //Q55
      case "q55_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q55_neutropenia_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience neutropenia" is selected and reset column C of current row
          if (value_int === 1) {
            row = {
              ...row,
              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,

              rblD: null,
              Demptyerr: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            ae_ManagementAeWarningTec:
              value_int != 1 ? false : innerArr[id].ae_ManagementAeWarningTec,
          };

          RecomputeAeQ26WarningForRow(
            55,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TECVAYLI
                ? adverseEventWarningTec &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1
                : cohortid === RRMMCohortTreatment.TALVEY
                  ? adverseEventWarningTal &&
                    value_int === 1 &&
                    Q53.length &&
                    Q53[id]?.rbl_B === 1 &&
                    Q54.length &&
                    Q54[id]?.rbl_B === 1 &&
                    Q55.length &&
                    Q55[id]?.rbl_B === 1 &&
                    Q57.length &&
                    Q57[id]?.rbl_B === 1 &&
                    Q59.length &&
                    Q59[id]?.rbl_B === 1 &&
                    Q60.length &&
                    Q60[id]?.rbl_B === 1 &&
                    Q61.length &&
                    Q61[id]?.rbl_B === 1
                  : cohortid == RRMMCohortTreatment.ELREXFIO ||
                      cohortid == RRMMCohortTreatment.Linvoseltamab
                    ? (adverseEventWarningElr || adverseEventWarningLin) &&
                      value_int == 1 &&
                      Q53.length &&
                      Q53[id]?.rbl_B == 1 &&
                      Q54.length &&
                      Q54[id]?.rbl_B == 1 &&
                      Q56.length &&
                      Q56[id]?.rbl_B == 1 &&
                      Q57.length &&
                      Q57[id]?.rbl_B == 1
                    : cohortid == RRMMCohortTreatment.CARVYKTI ||
                        cohortid == RRMMCohortTreatment.ABECMA
                      ? (adverseEventWarningCarvykti ||
                          adverseEventWarningAbecma) &&
                        value_int == 1 &&
                        Q53.length &&
                        Q53[id]?.rbl_B == 1 &&
                        Q54.length &&
                        Q54[id]?.rbl_B == 1 &&
                        Q56.length &&
                        Q56[id]?.rbl_B == 1 &&
                        Q62.length &&
                        Q62[id]?.rbl_B == 1 &&
                        Q63.length &&
                        Q63[id]?.rbl_B == 1 &&
                        (isNewEcrfA
                          ? Q64.length && Q64[id]?.q64_rbl_B === 1
                          : true)
                      : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q55_neutropenia_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      // Date of resolution [C] retired
      /*
      case "q55_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q55_neutropenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q55_neutropenia_list: innerArr,
          };
        }
        break;
      */

      // SEVERITY CASE FOR Q55
      case `q55rblD_${id}_${parentIndex}`:
        {
          let innerArr = [...updatedArray[parentIndex].q55_neutropenia_list];
          innerArr[id] = {
            ...innerArr[id],
            rblD: value_int,
            Demptyerr: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q55_neutropenia_list: innerArr,
          };
        }
        break;

      //Q56
      case "q56_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q56_cytopenia_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience cytopenia" is selected and reset column C of current row
          if (value_int === 1) {
            row = {
              ...row,
              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,

              //Severity
              rblD: null,
              Demptyerr: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            ae_ManagementAeWarningTec:
              value_int != 1 ? false : innerArr[id].ae_ManagementAeWarningTec,
          };

          RecomputeAeQ26WarningForRow(
            56,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TECVAYLI
                ? adverseEventWarningTec &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1
                : // cohortid == RRMMCohortTreatment.TALVEY ?
                  // (adverseEventWarningTal && value_int == 1 &&
                  //   (Q53.length  && Q53[id]?.rbl_B == 1) &&
                  //   (Q57.length  && Q57[id]?.rbl_B == 1) &&
                  //   (Q59.length  && Q59[id]?.rbl_B == 1) &&
                  //   (Q60.length  && Q60[id]?.rbl_B == 1) &&
                  //   (Q61.length  && Q61[id]?.rbl_B == 1)
                  //   ):
                  cohortid == RRMMCohortTreatment.ELREXFIO ||
                    cohortid == RRMMCohortTreatment.Linvoseltamab
                  ? (adverseEventWarningElr || adverseEventWarningLin) &&
                    value_int == 1 &&
                    Q53.length &&
                    Q53[id]?.rbl_B == 1 &&
                    Q54.length &&
                    Q54[id]?.rbl_B == 1 &&
                    Q55.length &&
                    Q55[id]?.rbl_B == 1 &&
                    Q57.length &&
                    Q57[id]?.rbl_B == 1
                  : cohortid == RRMMCohortTreatment.CARVYKTI ||
                      cohortid == RRMMCohortTreatment.ABECMA
                    ? (adverseEventWarningCarvykti ||
                        adverseEventWarningAbecma) &&
                      value_int == 1 &&
                      Q53.length &&
                      Q53[id]?.rbl_B == 1 &&
                      Q54.length &&
                      Q54[id]?.rbl_B == 1 &&
                      Q55.length &&
                      Q55[id]?.rbl_B == 1 &&
                      Q62.length &&
                      Q62[id]?.rbl_B == 1 &&
                      Q63.length &&
                      Q63[id]?.rbl_B == 1 &&
                      (isNewEcrfA
                        ? Q64.length && Q64[id]?.q64_rbl_B === 1
                        : true)
                    : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q56_cytopenia_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      // Date of resolution [C] retired
      /*
      case "q56_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q56_cytopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q56_cytopenia_list: innerArr,
          };
        }
        break;
      */

      // ADD SEVERITY CASE FOR Q56
      case `q56rblD_${id}_${parentIndex}`:
        {
          let innerArr = [...updatedArray[parentIndex].q56_cytopenia_list];
          innerArr[id] = {
            ...innerArr[id],
            rblD: value_int,
            Demptyerr: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q56_cytopenia_list: innerArr,
          };
        }
        break;

      //Q57
      case "q57_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          let row = {
            ...innerArr[id],
            rbl_resolution: null,
            date_resolution: null,
            resolutionemptyerr: false,
            resolutiondateemptyerr: false,
            resolutiondateinvalid: false,
            resolutiondaterangeerr: false,
            chk_C_1: false,
            chk_C_2: false,
            chk_C_3: false,
            rb_C: false,
            C_empty_err: false,
            // Infection organism [D] retired
            // chk_D_1: false,
            // chk_D_2: false,
            // chk_D_3: false,
            // chk_D_4: false,
            // chk_D_5: false,
            // chk_D_6: false,
            // chk_D_7: false,
            // chk_D_8: false,
            // chk_D_9: false,
            // chk_D_10: false,
            // chk_D_11: false,
            // chk_D_12: false,
            // chk_D_13: false,
            // chk_D_14: false,
            // chk_D_15: false,
            // txt_D_oth: "",
            // rb_D: false,
            // D_empty_err: false,
            // D_txt_empty_err: false,
            rbl_E: null,
            E_empty_err: false,
            rbl_F: null,
            F_empty_err: false,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            rb_G_unk: null,
            txt_G_oth: "",
            G_empty_err: false,
            G_txt_empty_err: false,
            rbl_H: null,
            date_H: null,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
            rbl_I: null,
            unit_I: "",
            I_empty_err: false,
            I_unit_empty_err: false,
            I_unit_invalid_err: false,
          };

          //Delete subsequent episode rows if "The patient did not experience infection" is selected
          if (value_int === 1) {
            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_before_diagnosis_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          RecomputeAeQ26WarningForRow(
            57,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TECVAYLI
                ? adverseEventWarningTec &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B == 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B == 1
                : cohortid == RRMMCohortTreatment.TALVEY
                  ? adverseEventWarningTal &&
                    value_int == 1 &&
                    Q53.length &&
                    Q53[id]?.rbl_B == 1 &&
                    Q54.length &&
                    Q54[id]?.rbl_B == 1 &&
                    Q59.length &&
                    Q59[id]?.rbl_B == 1 &&
                    Q60.length &&
                    Q60[id]?.rbl_B == 1 &&
                    Q61.length &&
                    Q61[id]?.rbl_B == 1
                  : cohortid == RRMMCohortTreatment.ELREXFIO ||
                      cohortid == RRMMCohortTreatment.Linvoseltamab
                    ? (adverseEventWarningElr || adverseEventWarningLin) &&
                      value_int == 1 &&
                      Q53.length &&
                      Q53[id]?.rbl_B == 1 &&
                      Q54.length &&
                      Q54[id]?.rbl_B == 1 &&
                      Q56.length &&
                      Q56[id]?.rbl_B == 1 &&
                      Q55.length &&
                      Q55[id]?.rbl_B == 1
                    : cohortid == RRMMCohortTreatment.CARVYKTI ||
                        cohortid == RRMMCohortTreatment.ABECMA
                      ? (adverseEventWarningCarvykti ||
                          adverseEventWarningAbecma) &&
                        value_int == 1 &&
                        Q53.length &&
                        Q53[id]?.rbl_B == 1 &&
                        Q54.length &&
                        Q54[id]?.rbl_B == 1 &&
                        Q56.length &&
                        Q56[id]?.rbl_B == 1 &&
                        Q62.length &&
                        Q62[id]?.rbl_B == 1 &&
                        Q63.length &&
                        Q63[id]?.rbl_B == 1 &&
                        (isNewEcrfA
                          ? Q64.length && Q64[id]?.q64_rbl_B === 1
                          : true)
                      : false;
          }

          if (isNewEcrfB) {
            updatedArray[parentIndex] = rebuildInfectionEpisodesAndMappings(
              updatedArray[parentIndex],
              innerArr,
              q53,
            );

            const computedWarnings = validateQ571BWarnings(updatedArray);
            setQ571BWarnings(computedWarnings);
          } else {
            updatedArray[parentIndex] = {
              ...updatedArray[parentIndex],
              q57_infection_list: innerArr,
              q53_crs_list: q53,
            };
          }
        }
        break;

      // Date of resolution (Element C only)
      case `q57rblresolution_${id}_${parentIndex}`:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          innerArr[id] = {
            ...innerArr[id],
            rbl_resolution: value_int,
            date_resolution: null,
            resolutionemptyerr: false,
            resolutiondateemptyerr: false,
            resolutiondateinvalid: false,
            resolutiondaterangeerr: false,
            chk_C_1: false,
            chk_C_2: false,
            chk_C_3: false,
            rb_C: false,
            C_empty_err: false,
            rbl_E: null,
            E_empty_err: false,
            rbl_F: null,
            F_empty_err: false,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            rb_G_unk: null,
            txt_G_oth: "",
            G_empty_err: false,
            G_txt_empty_err: false,
            rbl_H: null,
            date_H: null,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
            rbl_I: null,
            unit_I: "",
            I_empty_err: false,
            I_unit_empty_err: false,
            I_unit_invalid_err: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;

      // Date of most recent IgG assessment prior to infection (Element C only)
      case "q57_rbl_H" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          const prevRow = innerArr[id];
          // IgG level only becomes not-applicable (and should only be reset)
          // when IgG was not assessed prior to infection - switching between
          // "date entered" and "date unknown" must not touch column I.
          const iggNotAssessed = value_int === 1;
          innerArr[id] = {
            ...prevRow,
            rbl_H: value_int,
            date_H: null,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
            rbl_I: iggNotAssessed ? null : prevRow.rbl_I,
            unit_I: iggNotAssessed ? "" : prevRow.unit_I,
            I_empty_err: iggNotAssessed ? false : prevRow.I_empty_err,
            I_unit_empty_err: iggNotAssessed ? false : prevRow.I_unit_empty_err,
            I_unit_invalid_err: iggNotAssessed
              ? false
              : prevRow.I_unit_invalid_err,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;

      // IgG level (Element C only)
      case "q57_rbl_I" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          innerArr[id] = {
            ...innerArr[id],
            rbl_I: value_int,
            unit_I: "",
            I_empty_err: false,
            I_unit_empty_err: false,
            I_unit_invalid_err: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;

      case "q57_chk_C_1_" + id + "_" + parentIndex:
      case "q57_chk_C_2_" + id + "_" + parentIndex:
      case "q57_chk_C_3_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_C_" + parts[3];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            rb_C: false,
            C_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_rb_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_C: true,
            chk_C_1: false,
            chk_C_2: false,
            chk_C_3: false,
            C_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      // Infection organism [D] retired - all Column D handler cases commented out below
      /*
      case "q57_chk_D_1_" + id + "_" + parentIndex:
      case "q57_chk_D_2_" + id + "_" + parentIndex:
      case "q57_chk_D_3_" + id + "_" + parentIndex:
      case "q57_chk_D_4_" + id + "_" + parentIndex:
      case "q57_chk_D_5_" + id + "_" + parentIndex:
      case "q57_chk_D_6_" + id + "_" + parentIndex:
      case "q57_chk_D_7_" + id + "_" + parentIndex:
      case "q57_chk_D_8_" + id + "_" + parentIndex:
      case "q57_chk_D_9_" + id + "_" + parentIndex:
      case "q57_chk_D_10_" + id + "_" + parentIndex:
      case "q57_chk_D_11_" + id + "_" + parentIndex:
      case "q57_chk_D_12_" + id + "_" + parentIndex:
      case "q57_chk_D_13_" + id + "_" + parentIndex:
      case "q57_chk_D_14_" + id + "_" + parentIndex:
      case "q57_chk_D_15_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_D_" + parts[3];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            txt_D_oth: ctrlId === "chk_D_15" ? "" : innerArr[id].txt_D_oth,
            rb_D: false,
            D_empty_err: false,
            D_txt_empty_err: false, // ctrlId === "chk_D_15" ? false : innerArr[id].D_txt_empty_err
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_txt_D_oth" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],
            txt_D_oth: value,
            chk_D_15: true,
            D_empty_err: false,
            D_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_rb_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_D: true,
            chk_D_1: false,
            chk_D_2: false,
            chk_D_3: false,
            chk_D_4: false,
            chk_D_5: false,
            chk_D_6: false,
            chk_D_7: false,
            chk_D_8: false,
            chk_D_9: false,
            chk_D_10: false,
            chk_D_11: false,
            chk_D_12: false,
            chk_D_13: false,
            chk_D_14: false,
            chk_D_15: false,
            txt_D_oth: "",
            D_empty_err: false,
            D_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      */
      case "q57_rbl_E" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_E: value_int,
            E_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_rbl_F" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_F: value_int,
            F_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;

      case "q57_chk_G_1_" + id + "_" + parentIndex:
      case "q57_chk_G_2_" + id + "_" + parentIndex:
      case "q57_chk_G_3_" + id + "_" + parentIndex:
      case "q57_chk_G_4_" + id + "_" + parentIndex:
      case "q57_chk_G_5_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_G_" + parts[3];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            txt_G_oth: ctrlId === "chk_G_5" ? "" : innerArr[id].txt_G_oth,
            rb_G_unk: false,
            G_empty_err: false,
            G_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_txt_G_oth" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],
            txt_G_oth: value,
            chk_G_5: true,
            G_empty_err: false,
            G_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q57_rb_G_unk" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_G_unk: true,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            txt_G_oth: "",
            G_empty_err: false,
            G_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      //Q59
      case "q59_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience dysgeusia" is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              chk_CC_1: false,
              chk_CC_1_P: false,
              chk_CC_1_T: false,
              chk_CC_2: false,
              chk_CC_2_P: false,
              chk_CC_2_T: false,
              chk_CC_3: false,
              chk_CC_3_P: false,
              chk_CC_3_T: false,
              chk_CC_4: false,
              chk_CC_4_P: false,
              chk_CC_4_T: false,
              chk_CC_5: false,
              chk_CC_5_P: false,
              chk_CC_5_T: false,
              chk_CC_6: false,
              chk_CC_6_P: false,
              chk_CC_6_T: false,
              chk_CC_7: false,
              chk_CC_7_P: false,
              chk_CC_7_T: false,
              txt_CC_oth: "",
              rb_CC: false,
              CC_1_empty_err: false,
              CC_2_empty_err: false,
              CC_3_empty_err: false,
              CC_4_empty_err: false,
              CC_5_empty_err: false,
              CC_6_empty_err: false,
              CC_7_empty_err: false,
              CC_empty_err: false,
              CC_txt_empty_err: false,

              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,
              rbl_D: null,
              date_D: null,
              D_empty_err: false,
              D_date_empty_err: false,
              D_date_before_index_err: false,
              D_date_before_onset_err: false,
              D_date_before_impr_err: false,
              D_date_after_err: false,
              D_date_invalid: false,
              D_date_before_eof: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          RecomputeAeQ26WarningForRow(
            59,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TALVEY
                ? adverseEventWarningTal &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1 &&
                  Q60.length &&
                  Q60[id]?.rbl_B == 1 &&
                  Q61.length &&
                  Q61[id]?.rbl_B == 1
                : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q59_chk_CC_1_" + id + "_" + parentIndex:
      case "q59_chk_CC_2_" + id + "_" + parentIndex:
      case "q59_chk_CC_3_" + id + "_" + parentIndex:
      case "q59_chk_CC_4_" + id + "_" + parentIndex:
      case "q59_chk_CC_5_" + id + "_" + parentIndex:
      case "q59_chk_CC_6_" + id + "_" + parentIndex:
      case "q59_chk_CC_7_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_CC_" + parts[3];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            [ctrlId + "_P"]: false,
            [ctrlId + "_T"]: false,

            txt_CC_oth: ctrlId === "chk_CC_7" ? "" : innerArr[id].txt_CC_oth,
            rb_CC: false,
            CC_empty_err: false,
            CC_txt_empty_err: false, //ctrlId === "chk_CC_7" ? false : innerArr[id].CC_txt_empty_err,
            ["CC_" + parts[3] + "_empty_err"]: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q59_txt_CC_oth" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            txt_CC_oth: value,
            chk_CC_7: true,
            CC_empty_err: false,
            CC_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q59_rb_CC" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_CC: true,
            chk_CC_1: false,
            chk_CC_1_P: false,
            chk_CC_1_T: false,
            chk_CC_2: false,
            chk_CC_2_P: false,
            chk_CC_2_T: false,
            chk_CC_3: false,
            chk_CC_3_P: false,
            chk_CC_3_T: false,
            chk_CC_4: false,
            chk_CC_4_P: false,
            chk_CC_4_T: false,
            chk_CC_5: false,
            chk_CC_5_P: false,
            chk_CC_5_T: false,
            chk_CC_6: false,
            chk_CC_6_P: false,
            chk_CC_6_T: false,
            chk_CC_7: false,
            chk_CC_7_P: false,
            chk_CC_7_T: false,
            txt_CC_oth: "",

            CC_1_empty_err: false,
            CC_2_empty_err: false,
            CC_3_empty_err: false,
            CC_4_empty_err: false,
            CC_5_empty_err: false,
            CC_6_empty_err: false,
            CC_7_empty_err: false,
            CC_empty_err: false,
            CC_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;

      case "q59_chk_CC_1_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_2_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_3_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_4_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_5_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_6_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_7_P_" + id + "_" + parentIndex:
      case "q59_chk_CC_1_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_2_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_3_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_4_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_5_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_6_T_" + id + "_" + parentIndex:
      case "q59_chk_CC_7_T_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_CC_" + parts[3] + "_" + parts[4];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            ["CC_" + parts[3] + "_empty_err"]: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;

      case "q59_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q59_rbl_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: value_int,
            date_D: null,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      //60
      case "q60_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience nail disorder" is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,

              rbl_D: null,
              date_D: null,
              D_empty_err: false,
              D_date_empty_err: false,
              D_date_before_index_err: false,
              D_date_before_onset_err: false,
              D_date_before_impr_err: false,
              D_date_after_err: false,
              D_date_invalid: false,
              D_date_before_eof: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          RecomputeAeQ26WarningForRow(
            60,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TALVEY
                ? adverseEventWarningTal &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1 &&
                  Q59.length &&
                  Q59[id]?.rbl_B == 1 &&
                  Q61.length &&
                  Q61[id]?.rbl_B == 1
                : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q60_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
          };
        }
        break;
      case "q60_rbl_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: value_int,
            date_D: null,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
          };
        }
        break;
      //61
      case "q61_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience skin disorder" is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,

              rbl_D: null,
              date_D: null,
              D_empty_err: false,
              D_date_empty_err: false,
              D_date_before_index_err: false,
              D_date_before_onset_err: false,
              D_date_before_impr_err: false,
              D_date_after_err: false,
              D_date_invalid: false,
              D_date_before_eof: false,
            };
            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_before_eof: false,
          };

          RecomputeAeQ26WarningForRow(
            61,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.TALVEY
                ? adverseEventWarningTal &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q57.length &&
                  Q57[id]?.rbl_B == 1 &&
                  Q59.length &&
                  Q59[id]?.rbl_B == 1 &&
                  Q60.length &&
                  Q60[id]?.rbl_B == 1
                : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q61_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
          };
        }
        break;
      case "q61_rbl_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: value_int,
            date_D: null,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
          };
        }
        break;
      //Q62
      case "q62_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q62_spm_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            q62_rbl_C: null,
            q62_date_C: null,
            q62_severity: null,
            q62CEmptyError: false,
            q62CDateEmptyError: false,
            q62CDateInvalidError: false,
            q62CDateBeforeIndexError: false,
            q62CDateBeforeOnsetError: false,
            q62CDateAfterAEDateError: false,
            q62DEmptyError: false,
          };

          //Delete rows if "The patient did not experience ICANS" is selected
          if (value_int === 1) {
            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          RecomputeAeQ26WarningForRow(
            62,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.CARVYKTI ||
              cohortid == RRMMCohortTreatment.ABECMA
                ? (adverseEventWarningCarvykti || adverseEventWarningAbecma) &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B == 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B == 1 &&
                  Q63.length &&
                  Q63[id]?.rbl_B == 1 &&
                  (isNewEcrfA ? Q64.length && Q64[id]?.q64_rbl_B === 1 : true)
                : false;
          }
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q62_spm_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q62_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q62_spm_list];

          innerArr[id] = {
            ...innerArr[id],
            q62_rbl_C: value_int,
            q62_date_C: null,
            q62CEmptyError: false,
            q62CDateEmptyError: false,
            q62CDateInvalidError: false,
            q62CDateBeforeIndexError: false,
            q62CDateBeforeOnsetError: false,
            q62CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q62_spm_list: innerArr,
          };
        }
        break;

      case "q62_severity" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q62_spm_list];

          innerArr[id] = {
            ...innerArr[id],
            q62_severity: value_int,
            q62DEmptyError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q62_spm_list: innerArr,
          };
        }
        break;

      //63
      case "q63_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];
          let row = innerArr[id];

          //Delete rows if "The patient did not experience neurotoxicity" is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              rbl_C: null,
              date_C: null,
              C_empty_err: false,
              C_date_empty_err: false,
              C_date_before_index_err: false,
              C_date_before_onset_err: false,
              C_date_after_err: false,
              C_date_invalid: false,
              C_date_before_eof: false,
              rbl_D: null,
              D_empty_err: false,
              rbl_E: null,
              E_empty_err: false,
              chk_F_1: false,
              chk_F_2: false,
              rb_F: false,
              F_empty_err: false,
              chk_G_1: false,
              chk_G_2: false,
              chk_G_3: false,
              chk_G_4: false,
              chk_G_5: false,
              rb_G: false,
              G_empty_err: false,

              chkFInpatient: false,
              chkFEmergencyRoom: false,
              chkFIntensiveCareUnit: false,
              chkFUrgentCareVisit: false,
              chkFOther: false,

              rblH: null,
              dateH: null,
              Hemptyerr: false,
              Hdateemptyerr: false,
              Hdateinvalid: false,
              Hdatebeforeindexerr: false,
              Hdatebeforeonseterr: false,
              Hdatebeforereserr: false,
              Hdateaftererr: false,
              Hdatebeforeeof: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              // Filter out manual rows only, keep auto-populated rows
              const rowsToKeep = innerArr.slice(0, deleteStartIndex);
              const rowsAfter = innerArr.slice(deleteStartIndex);

              const autoPopulatedRows = rowsAfter.filter(
                (r) => r.isAutoPopulated === true && r.rbl_D === 2,
              );

              innerArr = [...rowsToKeep, ...autoPopulatedRows];

              // innerArr.splice(
              //   deleteStartIndex,
              //   innerArr.length - deleteStartIndex,
              // );
            }
          }

          innerArr[id] = {
            ...row,
            rbl_B: value_int,
            date_B: null,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          RecomputeAeQ26WarningForRow(
            63,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          // QAQC-045 Rule A - table-level check across all rows
          {
            const cohortTreatmentIdForNeurotox =
              updatedArray[parentIndex].cohortTreatmentId ?? null;
            const neurotoxTreatmentSelection =
              Q63NeurotoxicityTreatmentSelectionList?.find(
                (x) => x.cohortTreatmentId === cohortTreatmentIdForNeurotox,
              );
            const isNeurotoxTreatmentSelected =
              neurotoxTreatmentSelection?.selected ?? false;
            const hasNeurotoxEvent = innerArr.some(
              (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
            );
            const q63q39Warning =
              isNeurotoxTreatmentSelected && !hasNeurotoxEvent;
            for (let i = 0; i < innerArr.length; i++) {
              innerArr[i] = {
                ...innerArr[i],
                Q63q39WarningMsg: q63q39Warning,
              };
            }
          }

          // QAQC-045 Rule B/C/D - recompute since row occurrence changed
          RecomputeQ63SubtypeWarningForCohort(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );
          RecomputeQ63SubtypeWarningForCohortFromQ26(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );
          RecomputeQ63SubtypeWarningForCohortFromQ38(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid == RRMMCohortTreatment.CARVYKTI ||
              cohortid == RRMMCohortTreatment.ABECMA
                ? (adverseEventWarningCarvykti || adverseEventWarningAbecma) &&
                  value_int == 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B == 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B == 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B == 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B == 1 &&
                  Q62.length &&
                  Q62[id]?.rbl_B == 1 &&
                  (isNewEcrfA ? Q64.length && Q64[id]?.q64_rbl_B === 1 : true)
                : false;
          }

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q63_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: value_int,
            date_C: null,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;
      case "q63_rbl_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: value_int,
            D_empty_err: false,

            ...(value_int !== 7 && {
              txt_D_oth: "",
              D_txt_empty_err: false,
            }),
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };

          // QAQC-045 Rule B/C/D - recompute since the reported subtype changed
          RecomputeQ63SubtypeWarningForCohort(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );
          RecomputeQ63SubtypeWarningForCohortFromQ26(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );
          RecomputeQ63SubtypeWarningForCohortFromQ38(
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );
        }
        break;
      // Q63 - Column D Text Input (Other, please specify)
      case "q63_txt_D_oth" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            txt_D_oth: value,
            rbl_D: 7,
            D_empty_err: false,
            D_txt_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };

          setAEDetails(updatedArray);
        }
        break;

      case "q63_rbl_E" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_E: value_int,
            E_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;

      case "q63_chk_F_1_" + id + "_" + parentIndex:
      case "q63_chk_F_2_" + id + "_" + parentIndex:
      case "q63chkFInpatient_" + id + "_" + parentIndex:
      case "q63chkFEmergencyRoom_" + id + "_" + parentIndex:
      case "q63chkFIntensiveCareUnit_" + id + "_" + parentIndex:
      case "q63chkFUrgentCareVisit_" + id + "_" + parentIndex:
      case "q63chkFOther_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];
          // Extract the Check box state name
          const parts = name.split("_");
          //const ctrlId = "chk_F_" + parts[3];
          //const ctrlId = parts[0].replace("q63", "");

          let ctrlId;
          // Check if it's the old naming pattern (Outpatient)
          if (name.startsWith("q63_chk_F_")) {
            const parts = name.split("_");
            ctrlId = "chk_F_" + parts[3]; // Results in "chk_F_1"
          } else {
            // New naming pattern (chkFInpatient, chkFEmergencyRoom, etc.)
            ctrlId = name.split("_")[0].replace("q63", ""); // Removes "q63" prefix
          }

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            rb_F: false,
            F_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;
      case "q63_rb_F" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_F: true,
            chk_F_1: false,
            chk_F_2: false,
            F_empty_err: false,

            chkFInpatient: false,
            chkFEmergencyRoom: false,
            chkFIntensiveCareUnit: false,
            chkFUrgentCareVisit: false,
            chkFOther: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;
      case "q63_chk_G_1_" + id + "_" + parentIndex:
      case "q63_chk_G_2_" + id + "_" + parentIndex:
      case "q63_chk_G_3_" + id + "_" + parentIndex:
      case "q63_chk_G_4_" + id + "_" + parentIndex:
      case "q63_chk_G_5_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];
          // Extract the Check box state name
          const parts = name.split("_");
          const ctrlId = "chk_G_" + parts[3];

          innerArr[id] = {
            ...innerArr[id],

            [ctrlId]: !innerArr[id][ctrlId],
            rb_G: false,
            G_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;
      case "q63_rb_G" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],

            rb_G: true,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            G_empty_err: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;

      // Q63 - Column H (Improvement) - NEW
      case `q63rblH_${id}_${parentIndex}`:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];
          innerArr[id] = {
            ...innerArr[id],
            rblH: value_int,
            dateH: null,
            Hemptyerr: false,
            Hdateemptyerr: false,
            Hdateinvalid: false,
            Hdatebeforeindexerr: false,
            Hdatebeforeonseterr: false,
            Hdatebeforereserr: false,
            Hdateaftererr: false,
            Hdatebeforeeof: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;

      //Q64
      case "q64_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];
          let row = innerArr[id];

          //Delete rows if No Event occurred is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              q64_rbl_C: null,
              q64_date_C: null,
              q64_rbl_D: null,
              q64_date_D: null,
              q64_chk_E_1: false,
              q64_chk_E_2: false,
              q64_chk_E_3: false,
              q64_chk_E_4: false,
              q64_chk_E_5: false,
              q64_chk_E_othTxt: "",
              q64_E_unk: false,
              q64_severity: null,
              //C Column
              q64CEmptyError: false,
              q64CDateEmptyError: false,
              q64CDateInvalidError: false,
              q64CDateBeforeError: false,
              q64CDateAfterResolutionDateError: false,
              q64CDateAfterAEDateError: false,

              //D Column
              q64DEmptyError: false,
              q64DDateEmptyError: false,
              q64DDateBeforeError: false,
              q64DDateInvalidError: false,
              q64DDateAfterAEDateError: false,

              //E Column
              q64EEmptyError: false,
              q64EOtherTxtEmptyError: false,

              //F
              q64FEmptyError: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            q64_rbl_B: value_int,
            q64_date_B: null,
            q64BEmptyError: false,
            q64BDateEmptyError: false,
            q64BDateBeforeError: false,
            q64BDateInvalidError: false,
            q64BDateEqualPrevError: false,
            q64BDateBeforeIndexDateError: false,
            q64BDateAfterCDateError: false,
            q64BDateAfterResolutionDateError: false,
            q64BDateAfterAEDateError: false,
          };

          RecomputeAeQ26WarningForRow(
            64,
            updatedArray[parentIndex].cohortTreatmentId ?? null,
            innerArr,
            parentIndex,
          );

          let q53 = [...updatedArray[parentIndex].q53_crs_list];

          let cohortid = updatedArray[parentIndex].cohortTreatmentId ?? null;
          if (q53[id]) {
            q53[id].ae_ManagementAeWarningTec =
              cohortid === RRMMCohortTreatment.CARVYKTI ||
              cohortid === RRMMCohortTreatment.ABECMA
                ? (adverseEventWarningCarvykti || adverseEventWarningAbecma) &&
                  value_int === 1 &&
                  Q53.length &&
                  Q53[id]?.rbl_B === 1 &&
                  Q54.length &&
                  Q54[id]?.rbl_B === 1 &&
                  Q56.length &&
                  Q56[id]?.rbl_B === 1 &&
                  Q55.length &&
                  Q55[id]?.rbl_B === 1 &&
                  Q62.length &&
                  Q62[id]?.rbl_B === 1 &&
                  Q63.length &&
                  Q63[id]?.rbl_B === 1
                : false;
          }

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
            q53_crs_list: q53,
          };
        }
        break;
      case "q64_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_rbl_C: value_int,
            q64_date_C: null,
            q64CEmptyError: false,
            q64CDateEmptyError: false,
            q64CDateInvalidError: false,
            q64CDateBeforeError: false,
            q64CDateAfterResolutionDateError: false,
            q64CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;
      case "q64_rbl_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_rbl_D: value_int,
            q64_date_D: null,
            q64DEmptyError: false,
            q64DDateEmptyError: false,
            q64DDateBeforeError: false,
            q64DDateInvalidError: false,
            q64DDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      case "q64_chk_E_1_" + id + "_" + parentIndex:
      case "q64_chk_E_2_" + id + "_" + parentIndex:
      case "q64_chk_E_3_" + id + "_" + parentIndex:
      case "q64_chk_E_4_" + id + "_" + parentIndex:
      case "q64_chk_E_5_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];
          const parts = name.split("_");

          const checkboxName = `q64_chk_E_${parts[3]}`;
          const isOther = checkboxName === "q64_chk_E_5";

          innerArr[id] = {
            ...innerArr[id],
            [checkboxName]: !(innerArr[id]?.[checkboxName] ?? false),
            q64EEmptyError: false,
            q64_E_unk: false,

            ...(isOther && {
              q64EOtherTxtEmptyError: false,
              q64_chk_E_othTxt: "",
            }),
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;
      case "q64_E_unk" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_E_unk: true,
            q64_chk_E_1: false,
            q64_chk_E_2: false,
            q64_chk_E_3: false,
            q64_chk_E_4: false,
            q64_chk_E_5: false,
            q64EEmptyError: false,
            q64EOtherTxtEmptyError: false,
            q64_chk_E_othTxt: "",
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      case "q64_chk_E_othTxt" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_chk_E_othTxt: value ?? "",
            q64_chk_E_5: true,
            q64EEmptyError: false,
            q64EOtherTxtEmptyError: false,
            q64_E_unk: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      case "q64_severity" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],

            q64_severity: value_int,
            q64FEmptyError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      //Q65
      case "q65_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q65_lymphopenia_list];
          let row = innerArr[id];

          //Delete rows if No Event occurred is selected and reset column C, D of current row
          if (value_int === 1) {
            row = {
              ...row,
              q65_rbl_B: null,
              q65_date_B: null,
              q65_rbl_C: null,
              q65_date_C: null,
              q65_severity: null,

              //B Column
              q65BEmptyError: false,
              q65BDateEmptyError: false,
              q65BDateInvalidError: false,
              q65BDateEqualPrevError: false,
              q65BDateBeforeIndexDateError: false,
              q65BDateAfterAEDateError: false,

              //C Column
              q65CEmptyError: false,
              q65CDateEmptyError: false,
              q65CDateInvalidError: false,
              q65CDateBeforeError: false,
              q65CDateAfterAEDateError: false,

              //D Column
              q65DEmptyError: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            q65_rbl_B: value_int,
            q65_date_B: null,
            q65BEmptyError: false,
            q65BDateEmptyError: false,
            q65BDateInvalidError: false,
            q65BDateEqualPrevError: false,
            q65BDateBeforeIndexDateError: false,
            q65BDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q65_lymphopenia_list: innerArr,
          };

          if (isNewEcrfB) {
            const computedWarnings = validateQ65Warnings(
              updatedArray,
              q65QLymphopheniaSelectionList,
            );

            setQ65Warnings(computedWarnings);
          }
        }

        break;
      // Date of resolution [C] retired
      /*
      case "q65_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q65_lymphopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            q65_rbl_C: value_int,
            q65_date_C: null,
            q65CEmptyError: false,
            q65CDateEmptyError: false,
            q65CDateInvalidError: false,
            q65CDateBeforeError: false,
            q65CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q65_lymphopenia_list: innerArr,
          };
        }
        break;
      */

      case "q65_severity" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q65_lymphopenia_list];

          innerArr[id] = {
            ...innerArr[id],

            q65_severity: value_int,
            q65DEmptyError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q65_lymphopenia_list: innerArr,
          };
        }
        break;

      //Q66
      case "q66_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q66_dizziness_list];
          let row = innerArr[id];

          //Delete rows if No Event occurred is selected and reset column C of current row
          if (value_int === 1) {
            row = {
              ...row,
              q66_rbl_B: null,
              q66_date_B: null,
              q66_rbl_C: null,
              q66_date_C: null,

              //B Column
              q66BEmptyError: false,
              q66BDateEmptyError: false,
              q66BDateInvalidError: false,
              q66BDateEqualPrevError: false,
              q66BDateBeforeIndexDateError: false,
              q66BDateAfterAEDateError: false,

              //C Column
              q66CEmptyError: false,
              q66CDateEmptyError: false,
              q66CDateInvalidError: false,
              q66CDateBeforeError: false,
              q66CDateAfterAEDateError: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            q66_rbl_B: value_int,
            q66_date_B: null,
            q66BEmptyError: false,
            q66BDateEmptyError: false,
            q66BDateInvalidError: false,
            q66BDateEqualPrevError: false,
            q66BDateBeforeIndexDateError: false,
            q66BDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q66_dizziness_list: innerArr,
          };

          if (isNewEcrfC) {
            const computedWarnings = validateQ66Warnings(
              updatedArray,
              q66QDizzinessSelectionList,
            );

            setQ66Warnings(computedWarnings);
          }
        }

        break;
      case "q66_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q66_dizziness_list];

          innerArr[id] = {
            ...innerArr[id],
            q66_rbl_C: value_int,
            q66_date_C: null,
            q66CEmptyError: false,
            q66CDateEmptyError: false,
            q66CDateInvalidError: false,
            q66CDateBeforeError: false,
            q66CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q66_dizziness_list: innerArr,
          };
        }
        break;

      //Q67
      case "q67_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];
          let row = innerArr[id];

          //Delete rows if No Event occurred is selected and reset columns C-F of current row
          if (value_int === 1) {
            row = {
              ...row,
              q67_rbl_B: null,
              q67_date_B: null,
              q67_rbl_C: null,
              q67_date_C: null,
              q67_severity: null,

              q67_chk_E_1: false,
              q67_chk_E_2: false,
              q67_chk_E_3: false,
              q67_chk_E_4: false,
              q67_chk_E_5: false,
              q67_chk_E_6: false,
              q67_chk_E_7: false,
              q67_chk_E_8: false,
              q67_chk_E_othTxt: "",
              q67_E_unk: false,

              q67_chk_F_1: false,
              q67_chk_F_2: false,
              q67_chk_F_3: false,
              q67_chk_F_4: false,
              q67_chk_F_5: false,
              q67_chk_F_6: false,
              q67_chk_F_7: false,
              q67_chk_F_othTxt: "",
              q67_F_unk: false,

              //B Column
              q67BEmptyError: false,
              q67BDateEmptyError: false,
              q67BDateInvalidError: false,
              q67BDateEqualPrevError: false,
              q67BDateBeforeIndexDateError: false,
              q67BDateAfterAEDateError: false,

              //C Column
              q67CEmptyError: false,
              q67CDateEmptyError: false,
              q67CDateInvalidError: false,
              q67CDateBeforeError: false,
              q67CDateAfterAEDateError: false,

              //D Column
              q67DEmptyError: false,

              //E Column
              q67EEmptyError: false,
              q67EOtherTxtEmptyError: false,

              //F Column
              q67FEmptyError: false,
              q67FOtherTxtEmptyError: false,
              q67F1MismatchError: false,
              q67F2MismatchError: false,
              q67F3MismatchError: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            q67_rbl_B: value_int,
            q67_date_B: null,
            q67BEmptyError: false,
            q67BDateEmptyError: false,
            q67BDateInvalidError: false,
            q67BDateEqualPrevError: false,
            q67BDateBeforeIndexDateError: false,
            q67BDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };

          if (isNewEcrfC) {
            const computedWarnings = validateQ67Warnings(
              updatedArray,
              q67QAtaxiaSelectionList,
            );

            setQ67Warnings(computedWarnings);
          }
        }

        break;
      case "q67_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_rbl_C: value_int,
            q67_date_C: null,
            q67CEmptyError: false,
            q67CDateEmptyError: false,
            q67CDateInvalidError: false,
            q67CDateBeforeError: false,
            q67CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      case "q67_chk_E_1_" + id + "_" + parentIndex:
      case "q67_chk_E_2_" + id + "_" + parentIndex:
      case "q67_chk_E_3_" + id + "_" + parentIndex:
      case "q67_chk_E_4_" + id + "_" + parentIndex:
      case "q67_chk_E_5_" + id + "_" + parentIndex:
      case "q67_chk_E_6_" + id + "_" + parentIndex:
      case "q67_chk_E_7_" + id + "_" + parentIndex:
      case "q67_chk_E_8_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];
          const parts = name.split("_");

          const checkboxName = `q67_chk_E_${parts[3]}`;
          const isOther = checkboxName === "q67_chk_E_8";

          innerArr[id] = {
            ...innerArr[id],
            [checkboxName]: !(innerArr[id]?.[checkboxName] ?? false),
            q67EEmptyError: false,
            q67_E_unk: false,

            ...(isOther && {
              q67EOtherTxtEmptyError: false,
              q67_chk_E_othTxt: "",
            }),
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;
      case "q67_E_unk" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_E_unk: true,
            q67_chk_E_1: false,
            q67_chk_E_2: false,
            q67_chk_E_3: false,
            q67_chk_E_4: false,
            q67_chk_E_5: false,
            q67_chk_E_6: false,
            q67_chk_E_7: false,
            q67_chk_E_8: false,
            q67EEmptyError: false,
            q67EOtherTxtEmptyError: false,
            q67_chk_E_othTxt: "",
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      case "q67_chk_E_othTxt" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_chk_E_othTxt: value ?? "",
            q67_chk_E_8: true,
            q67EEmptyError: false,
            q67EOtherTxtEmptyError: false,
            q67_E_unk: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      case "q67_chk_F_1_" + id + "_" + parentIndex:
      case "q67_chk_F_2_" + id + "_" + parentIndex:
      case "q67_chk_F_3_" + id + "_" + parentIndex:
      case "q67_chk_F_4_" + id + "_" + parentIndex:
      case "q67_chk_F_5_" + id + "_" + parentIndex:
      case "q67_chk_F_6_" + id + "_" + parentIndex:
      case "q67_chk_F_7_" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];
          const parts = name.split("_");

          const checkboxName = `q67_chk_F_${parts[3]}`;
          const isOther = checkboxName === "q67_chk_F_7";
          const newCheckedValue = !(innerArr[id]?.[checkboxName] ?? false);

          const atxSelection = q67QAtaxiaSelectionList?.find(
            (x) =>
              x.cohortTreatmentId ===
              updatedArray[parentIndex]?.cohortTreatmentId,
          );

          innerArr[id] = {
            ...innerArr[id],
            [checkboxName]: newCheckedValue,
            q67FEmptyError: false,
            q67_F_unk: false,

            ...(checkboxName === "q67_chk_F_1" && {
              q67F1MismatchError:
                newCheckedValue && !atxSelection?.q26DiscontinuedSelected,
            }),
            ...(checkboxName === "q67_chk_F_2" && {
              q67F2MismatchError:
                newCheckedValue && !atxSelection?.q382ADelaySelected,
            }),
            ...(checkboxName === "q67_chk_F_3" && {
              q67F3MismatchError:
                newCheckedValue && !atxSelection?.q381AReductionSelected,
            }),

            ...(isOther && {
              q67FOtherTxtEmptyError: false,
              q67_chk_F_othTxt: "",
            }),
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;
      case "q67_F_unk" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_F_unk: true,
            q67_chk_F_1: false,
            q67_chk_F_2: false,
            q67_chk_F_3: false,
            q67_chk_F_4: false,
            q67_chk_F_5: false,
            q67_chk_F_6: false,
            q67_chk_F_7: false,
            q67FEmptyError: false,
            q67FOtherTxtEmptyError: false,
            q67_chk_F_othTxt: "",
            q67F1MismatchError: false,
            q67F2MismatchError: false,
            q67F3MismatchError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      case "q67_chk_F_othTxt" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_chk_F_othTxt: value ?? "",
            q67_chk_F_7: true,
            q67FEmptyError: false,
            q67FOtherTxtEmptyError: false,
            q67_F_unk: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      case "q67_severity" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_severity: value_int,
            q67DEmptyError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      //Q57.1.B
      case "q571B_rbl_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q571B_immunoglobin_list];
          let row = innerArr[id];

          if (value_int === 1) {
            row = {
              ...row,
              q571B_rbl_B: null,
              q571B_date_B: null,
              q571B_rbl_C: null,
              q571B_unit_C: null,
              q571B_episode_D: null,

              //B Column
              q571BEmptyError: false,
              q571BDateEmptyError: false,
              q571BDateInvalidError: false,
              q571BDateEqualPrevError: false,
              q571BDateBeforeIndexDateError: false,
              q571BDateAfterDateError: false,

              //C Column
              q571CEmptyError: false,
              q571CUnitEmptyError: false,
              q571CUnitInvalidError: false,

              //D Column
              q571DEmptyError: false,
            };

            const deleteStartIndex = Number(id) + 1;
            if (deleteStartIndex >= 0 && deleteStartIndex < innerArr.length) {
              innerArr.splice(
                deleteStartIndex,
                innerArr.length - deleteStartIndex,
              );
            }
          }

          innerArr[id] = {
            ...row,
            q571B_rbl_B: value_int,
            q571B_date_B: null,
            q571BEmptyError: false,
            q571BDateEmptyError: false,
            q571BDateInvalidError: false,
            q571BDateEqualPrevError: false,
            q571BDateBeforeIndexDateError: false,
            q571BDateAfterDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q571B_immunoglobin_list: innerArr,
          };

          if (isNewEcrfB) {
            const computedWarnings = validateQ571BWarnings(updatedArray);

            setQ571BWarnings(computedWarnings);
          }
        }
        break;

      case "q571B_rbl_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q571B_immunoglobin_list];

          innerArr[id] = {
            ...innerArr[id],
            q571B_rbl_C: value_int,
            q571B_unit_C: null,
            q571CEmptyError: false,
            q571CUnitEmptyError: false,
            q571CUnitInvalidError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q571B_immunoglobin_list: innerArr,
          };
        }
        break;

      case "q571B_episode_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q571B_immunoglobin_list];

          innerArr[id] = {
            ...innerArr[id],
            q571B_episode_D: value,
            q571DEmptyError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q571B_immunoglobin_list: innerArr,
          };
        }
        break;

      default:
        break;
    }

    setAEDetails(updatedArray);
  };

  const HandleRepeaterDateChange = (name, date, id) => {
    const lastUnderscoreIndex = name.lastIndexOf("_");
    const parentIndex = name.substring(lastUnderscoreIndex + 1);

    let updatedArray = [...aeDetails];
    switch (name) {
      case "q53_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q53_crs_list];

          // Update the specific item in the inner array
          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          // Assign the updated inner array back to the outer array
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q53_crs_list: innerArr,
          };
        }
        break;

      // q53 date of resolution
      case `q53dateresolution_${id}_${parentIndex}`:
        let innerArr2 = [...updatedArray[parentIndex].q53_crs_list];
        innerArr2[id] = {
          ...innerArr2[id],
          rbl_resolution: 2, // Set to "Event resolved on"
          date_resolution: date,
          resolutionemptyerr: false,
          resolutiondateemptyerr: false,
          resolutiondateinvalid: false,
          resolutiondaterangeerr: false,
        };
        updatedArray[parentIndex] = {
          ...updatedArray[parentIndex],
          q53_crs_list: innerArr2,
        };
        break;

      case "q54_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q54_icans_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q54_icans_list: innerArr,
          };
          updatedArray = AutoPopulateQ63FromQ54(parentIndex, updatedArray);
        }
        break;

      //q54 CASE FOR RESOLUTION DATE
      case `q54dateresolution_${id}_${parentIndex}`:
        {
          let innerArr2 = [...updatedArray[parentIndex].q54_icans_list];
          innerArr2[id] = {
            ...innerArr2[id],
            rblresolution: 2, // Set to "Event resolved on"
            dateresolution: date,
            resolutionemptyerr: false,
            resolutiondateemptyerr: false,
            resolutiondateinvalid: false,
            resolutiondaterangeerr: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q54_icans_list: innerArr2,
          };
          updatedArray = AutoPopulateQ63FromQ54(parentIndex, updatedArray);
        }
        break;

      case "q55_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q55_neutropenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q55_neutropenia_list: innerArr,
          };
        }
        break;
      // Date of resolution [C] retired
      /*
      case "q55_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q55_neutropenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q55_neutropenia_list: innerArr,
          };
        }
        break;
      */
      case "q56_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q56_cytopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q56_cytopenia_list: innerArr,
          };
        }
        break;
      // Date of resolution [C] retired
      /*
      case "q56_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q56_cytopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q56_cytopenia_list: innerArr,
          };
        }
        break;
      */
      case "q57_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_before_diagnosis_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
            rbl_resolution: null,
            date_resolution: null,
            resolutionemptyerr: false,
            resolutiondateemptyerr: false,
            resolutiondateinvalid: false,
            resolutiondaterangeerr: false,
            chk_C_1: false,
            chk_C_2: false,
            chk_C_3: false,
            rb_C: false,
            C_empty_err: false,
            rbl_E: null,
            E_empty_err: false,
            rbl_F: null,
            F_empty_err: false,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            rb_G_unk: null,
            txt_G_oth: "",
            G_empty_err: false,
            G_txt_empty_err: false,
            rbl_H: null,
            date_H: null,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
            rbl_I: null,
            unit_I: "",
            I_empty_err: false,
            I_unit_empty_err: false,
            I_unit_invalid_err: false,
          };

          if (isNewEcrfB) {
            updatedArray[parentIndex] = rebuildInfectionEpisodesAndMappings(
              updatedArray[parentIndex],
              innerArr,
              updatedArray[parentIndex].q53_crs_list,
            );

            const computedWarnings = validateQ571BWarnings(updatedArray);
            setQ571BWarnings(computedWarnings);
          } else {
            updatedArray[parentIndex] = {
              ...updatedArray[parentIndex],
              q57_infection_list: innerArr,
            };
          }
        }
        break;

      // q57 date of resolution (Element C only)
      case `q57dateresolution_${id}_${parentIndex}`:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          innerArr[id] = {
            ...innerArr[id],
            rbl_resolution: 2,
            date_resolution: date,
            resolutionemptyerr: false,
            resolutiondateemptyerr: false,
            resolutiondateinvalid: false,
            resolutiondaterangeerr: false,
            chk_C_1: false,
            chk_C_2: false,
            chk_C_3: false,
            rb_C: false,
            C_empty_err: false,
            rbl_E: null,
            E_empty_err: false,
            rbl_F: null,
            F_empty_err: false,
            chk_G_1: false,
            chk_G_2: false,
            chk_G_3: false,
            chk_G_4: false,
            chk_G_5: false,
            rb_G_unk: null,
            txt_G_oth: "",
            G_empty_err: false,
            G_txt_empty_err: false,
            rbl_H: null,
            date_H: null,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
            rbl_I: null,
            unit_I: "",
            I_empty_err: false,
            I_unit_empty_err: false,
            I_unit_invalid_err: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;

      // q57 date of most recent IgG assessment prior to infection (Element C only)
      case "q57_date_H" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q57_infection_list];
          innerArr[id] = {
            ...innerArr[id],
            rbl_H: 2,
            date_H: date,
            H_empty_err: false,
            H_date_empty_err: false,
            H_date_invalid: false,
            H_date_before_diagnosis_err: false,
            H_date_after_onset_err: false,
          };
          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q57_infection_list: innerArr,
          };
        }
        break;
      case "q59_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q59_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q59_date_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q59_dysgeusia_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: 2,
            date_D: date,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
            D_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q59_dysgeusia_list: innerArr,
          };
        }
        break;
      case "q60_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
          };
        }
        break;
      case "q60_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
          };
        }
        break;
      case "q60_date_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q60_nail_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: 2,
            date_D: date,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
            D_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q60_nail_list: innerArr,
          };
        }
        break;
      case "q61_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
          };
        }
        break;
      case "q61_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
          };
        }
        break;
      case "q61_date_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q61_skin_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_D: 2,
            date_D: date,
            D_empty_err: false,
            D_date_empty_err: false,
            D_date_before_index_err: false,
            D_date_before_onset_err: false,
            D_date_before_impr_err: false,
            D_date_after_err: false,
            D_date_invalid: false,
            D_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q61_skin_list: innerArr,
          };
        }
        break;
      case "q62_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q62_spm_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q62_spm_list: innerArr,
          };
        }
        break;

      case "q62_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q62_spm_list];

          innerArr[id] = {
            ...innerArr[id],
            q62_rbl_C: 2,
            q62_date_C: date,
            q62CEmptyError: false,
            q62CDateEmptyError: false,
            q62CDateInvalidError: false,
            q62CDateBeforeIndexError: false,
            q62CDateBeforeOnsetError: false,
            q62CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q62_spm_list: innerArr,
          };
        }
        break;
      case "q63_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_B: 2,
            date_B: date,
            B_empty_err: false,
            B_date_empty_err: false,
            B_date_before_err: false,
            B_date_after_err: false,
            B_date_equal_prev_ep_err: false,
            B_date_invalid: false,
            B_date_after_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;
      case "q63_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];

          innerArr[id] = {
            ...innerArr[id],
            rbl_C: 2,
            date_C: date,
            C_empty_err: false,
            C_date_empty_err: false,
            C_date_before_index_err: false,
            C_date_before_onset_err: false,
            C_date_after_err: false,
            C_date_invalid: false,
            C_date_before_eof: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q63_neurotoxicity_list: innerArr,
          };
        }
        break;

      // Q63 date of improvement (Column H) - NEW
      case `q63dateH_${id}_${parentIndex}`:
        let innerArr = [...updatedArray[parentIndex].q63_neurotoxicity_list];
        innerArr[id] = {
          ...innerArr[id],
          rblH: 2, // Set to "Improvement occurred on date"
          dateH: date,
          Hemptyerr: false,
          Hdateemptyerr: false,
          Hdateinvalid: false,
          Hdatebeforeindexerr: false,
          Hdatebeforeonseterr: false,
          Hdatebeforereserr: false,
          Hdateaftererr: false,
          Hdatebeforeeof: false,
        };
        updatedArray[parentIndex] = {
          ...updatedArray[parentIndex],
          q63_neurotoxicity_list: innerArr,
        };
        break;

      //Q64
      case "q64_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_rbl_B: 2,
            q64_date_B: date,
            q64BEmptyError: false,
            q64BDateEmptyError: false,
            q64BDateBeforeError: false,
            q64BDateInvalidError: false,
            q64BDateEqualPrevError: false,
            q64BDateBeforeIndexDateError: false,
            q64BDateAfterCDateError: false,
            q64BDateAfterResolutionDateError: false,
            q64BDateAfterAEDateError: false,
            q64CDateBeforeError: false,
            q64CDateAfterResolutionDateError: false,
            q64CDateAfterAEDateError: false,
            q64DDateBeforeError: false,
            q64DDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;
      case "q64_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_rbl_C: 2,
            q64_date_C: date,
            q64CEmptyError: false,
            q64CDateEmptyError: false,
            q64CDateBeforeError: false,
            q64CDateInvalidError: false,
            q64CDateAfterResolutionDateError: false,
            q64CDateAfterAEDateError: false,
            q64BDateBeforeError: false,
            q64BDateBeforeIndexDateError: false,
            q64BDateAfterCDateError: false,
            q64BDateAfterResolutionDateError: false,
            q64BDateAfterAEDateError: false,
            q64DDateBeforeError: false,
            q64DDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      case "q64_date_D" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q64_enterocolitis_list];

          innerArr[id] = {
            ...innerArr[id],
            q64_rbl_D: 2,
            q64_date_D: date,
            q64DEmptyError: false,
            q64DDateEmptyError: false,
            q64DDateBeforeError: false,
            q64DDateInvalidError: false,
            q64DDateAfterAEDateError: false,
            q64BDateBeforeError: false,
            q64BDateBeforeIndexDateError: false,
            q64BDateAfterCDateError: false,
            q64BDateAfterResolutionDateError: false,
            q64BDateAfterAEDateError: false,
            q64CDateBeforeError: false,
            q64CDateAfterResolutionDateError: false,
            q64CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q64_enterocolitis_list: innerArr,
          };
        }
        break;

      //Q65
      case "q65_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q65_lymphopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            q65_rbl_B: 2,
            q65_date_B: date,
            q65BEmptyError: false,
            q65BDateEmptyError: false,
            q65BDateInvalidError: false,
            q65BDateEqualPrevError: false,
            q65BDateBeforeIndexDateError: false,
            q65BDateAfterAEDateError: false,
            q65CDateBeforeError: false,
            q65CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q65_lymphopenia_list: innerArr,
          };

          if (isNewEcrfB) {
            const computedWarnings = validateQ65Warnings(
              updatedArray,
              q65QLymphopheniaSelectionList,
            );

            setQ65Warnings(computedWarnings);
          }
        }
        break;
      // Date of resolution [C] retired
      /*
      case "q65_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q65_lymphopenia_list];

          innerArr[id] = {
            ...innerArr[id],
            q65_rbl_C: 2,
            q65_date_C: date,
            q64CEmptyError: false,
            q65CEmptyError: false,
            q65CDateEmptyError: false,
            q65CDateInvalidError: false,
            q65CDateBeforeError: false,
            q65CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q65_lymphopenia_list: innerArr,
          };
        }
        break;
      */

      //Q66
      case "q66_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q66_dizziness_list];

          innerArr[id] = {
            ...innerArr[id],
            q66_rbl_B: 2,
            q66_date_B: date,
            q66BEmptyError: false,
            q66BDateEmptyError: false,
            q66BDateInvalidError: false,
            q66BDateEqualPrevError: false,
            q66BDateBeforeIndexDateError: false,
            q66BDateAfterAEDateError: false,
            q66CDateBeforeError: false,
            q66CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q66_dizziness_list: innerArr,
          };

          if (isNewEcrfC) {
            const computedWarnings = validateQ66Warnings(
              updatedArray,
              q66QDizzinessSelectionList,
            );

            setQ66Warnings(computedWarnings);
          }
        }
        break;
      case "q66_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q66_dizziness_list];

          innerArr[id] = {
            ...innerArr[id],
            q66_rbl_C: 2,
            q66_date_C: date,
            q66CEmptyError: false,
            q66CDateEmptyError: false,
            q66CDateInvalidError: false,
            q66CDateBeforeError: false,
            q66CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q66_dizziness_list: innerArr,
          };
        }
        break;

      //Q67
      case "q67_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_rbl_B: 2,
            q67_date_B: date,
            q67BEmptyError: false,
            q67BDateEmptyError: false,
            q67BDateInvalidError: false,
            q67BDateEqualPrevError: false,
            q67BDateBeforeIndexDateError: false,
            q67BDateAfterAEDateError: false,
            q67CDateBeforeError: false,
            q67CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };

          if (isNewEcrfC) {
            const computedWarnings = validateQ67Warnings(
              updatedArray,
              q67QAtaxiaSelectionList,
            );

            setQ67Warnings(computedWarnings);
          }
        }
        break;
      case "q67_date_C" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q67_ataxia_list];

          innerArr[id] = {
            ...innerArr[id],
            q67_rbl_C: 2,
            q67_date_C: date,
            q67CEmptyError: false,
            q67CDateEmptyError: false,
            q67CDateInvalidError: false,
            q67CDateBeforeError: false,
            q67CDateAfterAEDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q67_ataxia_list: innerArr,
          };
        }
        break;

      //Q57.1.B
      case "q571B_date_B" + id + "_" + parentIndex:
        {
          let innerArr = [...updatedArray[parentIndex].q571B_immunoglobin_list];

          innerArr[id] = {
            ...innerArr[id],
            q571B_rbl_B: 2,
            q571B_date_B: date,
            q571BEmptyError: false,
            q571BDateEmptyError: false,
            q571BDateInvalidError: false,
            q571BDateEqualPrevError: false,
            q571BDateBeforeIndexDateError: false,
            q571BDateAfterDateError: false,
          };

          updatedArray[parentIndex] = {
            ...updatedArray[parentIndex],
            q571B_immunoglobin_list: innerArr,
          };
          if (isNewEcrfB) {
            const computedWarnings = validateQ571BWarnings(updatedArray);
            setQ571BWarnings(computedWarnings);
          }
        }
        break;

      default:
        break;
    }
    setAEDetails(updatedArray);
  };

  const HandleRepeaterUnitFieldChange = (e, radioValue) => {
    try {
      const { name, value } = e.target;

      const parts = name.replace("q571B_unit_C", "").split("_");
      const childIdx = Number(parts[0]);
      const parentIdx = Number(parts[1]);

      setAEDetails((prev) => {
        const newArr = [...prev];
        const parent = { ...newArr[parentIdx] };

        const list = [...(parent.q571B_immunoglobin_list || [])];
        const childRow = { ...list[childIdx] };

        childRow.q571B_unit_C = value;

        if (value !== "") {
          childRow.q571B_rbl_C = radioValue;
        }

        // Reset errors
        childRow.q571CEmptyError = false;
        childRow.q571CUnitEmptyError = false;
        childRow.q571CUnitInvalidError = false;

        list[childIdx] = childRow;
        parent.q571B_immunoglobin_list = list;
        newArr[parentIdx] = parent;

        return newArr;
      });
    } catch (e) {}
  };

  //Q57 IgG level (column I, Element C only)
  const HandleQ57IggUnitFieldChange = (e, radioValue) => {
    try {
      const { name, value } = e.target;

      const parts = name.replace("q57_unit_I", "").split("_");
      const childIdx = Number(parts[0]);
      const parentIdx = Number(parts[1]);

      setAEDetails((prev) => {
        const newArr = [...prev];
        const parent = { ...newArr[parentIdx] };

        const list = [...(parent.q57_infection_list || [])];
        const childRow = { ...list[childIdx] };

        childRow.unit_I = value;

        if (value !== "") {
          childRow.rbl_I = radioValue;
        }

        childRow.I_empty_err = false;
        childRow.I_unit_empty_err = false;
        childRow.I_unit_invalid_err = false;

        list[childIdx] = childRow;
        parent.q57_infection_list = list;
        newArr[parentIdx] = parent;

        return newArr;
      });
    } catch (e) {}
  };

  //Adding Deleting rows
  const DeleteQ53Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q53Array = [...aeDetails[cohortIndex].q53_crs_list];
    q53Array.splice(childIdx, 1);

    // QAQC-043 / QAQC-044 - these table-level CRS warnings must only be
    // shown once the abstractor clicks Validate/Next, not live on row
    // deletion, so they are intentionally cleared here (see Validate()).
    finalArray[cohortIndex].q53_crs_list = q53Array.map((r) => ({
      ...r,
      Q53q36WarningMsg: false,
      Q53q39WarningMsg: false,
    }));

    setAEDetails(finalArray);
  };
  const AddQ53Row = (cohortIndex) => {
    //outer array index
    let newArray = [...aeDetails];
    // newArray[cohortIndex].q53_crs_list = [...newArray[cohortIndex].q53_crs_list, q53_crs_row];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q53_crs_list: [...newArray[cohortIndex].q53_crs_list, q53_crs_row],
    };
    setAEDetails(newArray);
  };

  const DeleteQ54Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q54Array = [...aeDetails[cohortIndex].q54_icans_list];

    const deletedRow = q54Array[childIdx];
    const deletedIcansId = deletedRow?.icansId;

    // Remove from Q54
    q54Array.splice(childIdx, 1);

    // QAQC-043 - recompute the table-level warning after deletion, since the
    // remaining rows may no longer (or may now) satisfy ">=1 event reported".
    const cohortTreatmentIdForIcans =
      finalArray[cohortIndex].cohortTreatmentId ?? null;
    const icansSelection = Q36IcansSelectionList?.find(
      (x) => x.cohortTreatmentId === cohortTreatmentIdForIcans,
    );
    const isQ36IcansSelected = icansSelection?.selected ?? false;
    const hasIcansEvent = q54Array.some(
      (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
    );
    const q54q36Warning = isQ36IcansSelected && !hasIcansEvent;

    finalArray[cohortIndex].q54_icans_list = q54Array.map((r) => ({
      ...r,
      Q54q36WarningMsg: q54q36Warning,
    }));

    if (deletedIcansId) {
      let q63Array = [...finalArray[cohortIndex].q63_neurotoxicity_list];

      // Find and remove Q63 row with matching sourceQ54IcansId
      q63Array = q63Array.filter((q63Row) => {
        // Keep manual rows and non-ICANS rows
        if (!q63Row.isAutoPopulated || q63Row.rbl_D !== 2) {
          return true;
        }
        // Remove auto-populated ICANS row if it matches the deleted Q54 row
        return q63Row.sourceQ54IcansId !== deletedIcansId;
      });

      finalArray[cohortIndex].q63_neurotoxicity_list = q63Array;
    }

    setAEDetails(finalArray);
  };

  const AddQ54Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q54_icans_list: [...newArray[cohortIndex].q54_icans_list, q54_icans_row],
    };
    setAEDetails(newArray);
  };
  const DeleteQ55Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q55Array = [...aeDetails[cohortIndex].q55_neutropenia_list];
    q55Array.splice(childIdx, 1);
    finalArray[cohortIndex].q55_neutropenia_list = q55Array;

    setAEDetails(finalArray);
  };
  const AddQ55Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q55_neutropenia_list: [
        ...newArray[cohortIndex].q55_neutropenia_list,
        q55_neutropenia_row,
      ],
    };
    setAEDetails(newArray);
  };
  const DeleteQ56Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q56Array = [...aeDetails[cohortIndex].q56_cytopenia_list];
    q56Array.splice(childIdx, 1);
    finalArray[cohortIndex].q56_cytopenia_list = q56Array;

    setAEDetails(finalArray);
  };
  const AddQ56Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q56_cytopenia_list: [
        ...newArray[cohortIndex].q56_cytopenia_list,
        q56_cytopenia_row,
      ],
    };
    setAEDetails(newArray);
  };

  const DeleteQ57Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];

    const cohort = finalArray[cohortIndex];

    const q57Array = [...cohort.q57_infection_list];
    const removedRow = q57Array[childIdx];
    const infectionKey = removedRow?.infectionKey;

    const isLinkedToQ571B = cohort.q571B_immunoglobin_list.some(
      (item) => item.q571B_episode_D === infectionKey,
    );

    const isLinkedToEpisodeList = cohort.infectionEpisodeList.some(
      (item) => item.infectionKey === infectionKey,
    );

    const shouldRevalidate =
      !!infectionKey && (isLinkedToQ571B || isLinkedToEpisodeList);

    // remove infection row
    q57Array.splice(childIdx, 1);

    // Update Q57.1.B (Immunoglobin)
    const updatedQ571BList = infectionKey
      ? cohort.q571B_immunoglobin_list.map((item) =>
          item.q571B_episode_D === infectionKey
            ? { ...item, q571B_episode_D: null, q571DEmptyError: false }
            : item,
        )
      : cohort.q571B_immunoglobin_list;

    // Sync InfectionEpisodeList
    const updatedEpisodeList = infectionKey
      ? cohort.infectionEpisodeList.filter(
          (item) => item.infectionKey !== infectionKey,
        )
      : cohort.infectionEpisodeList;

    finalArray[cohortIndex] = {
      ...cohort,
      q57_infection_list: q57Array,
      q571B_immunoglobin_list: updatedQ571BList,
      infectionEpisodeList: updatedEpisodeList,
    };

    setAEDetails(finalArray);

    if (shouldRevalidate) {
      setQ571BWarnings(validateQ571BWarnings(finalArray));
    }
  };

  const AddQ57Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    const newRow = {
      ...q57_infection_row,
      infectionKey: GenerateKey(),
    };

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q57_infection_list: [...newArray[cohortIndex].q57_infection_list, newRow],
    };
    setAEDetails(newArray);
  };
  // Q58.C field handlers - kept self-contained (not folded into the shared
  // HandleRepeaterFieldChange switch). Rows are auto-generated (BuildQ58Meta)
  // and cannot be added/removed by the abstractor.
  //
  // Matching the convention used by every other question on this page: onChange
  // handlers only update the value and clear that field's own error flags: they
  // do not (re)compute error state. Error flags are only ever set by the master
  // Validate() function, which runs when the abstractor clicks Validate/Save.
  const Q58_C_ERROR_RESET = {
    C_empty_err: false,
    C_date_empty_err: false,
    C_date_invalid: false,
    C_date_before_index_err: false,
    C_date_after_err: false,
    C_date_outside_window_err: false,
    C_date_not_increasing_err: false,
  };
  const Q58_D_ERROR_RESET = {
    D_empty_err: false,
    D_kg_range_err: false,
    D_lbs_range_err: false,
  };

  const HandleQ58DateChange = (cohortIndex, childIdx, date) => {
    const finalArray = [...aeDetails];
    const q58Array = [...finalArray[cohortIndex].q58_weight_list];
    q58Array[childIdx] = {
      ...q58Array[childIdx],
      // Picking a date auto-selects "date entered" (rbl_C = 2), so the
      // abstractor doesn't have to click the radio separately first.
      rbl_C: 2,
      date_C: date,
      touched: true,
      ...Q58_C_ERROR_RESET,
    };
    finalArray[cohortIndex] = {
      ...finalArray[cohortIndex],
      q58_weight_list: q58Array,
    };
    setAEDetails(finalArray);
  };

  const HandleQ58AssessmentChange = (cohortIndex, childIdx, value) => {
    const finalArray = [...aeDetails];
    const q58Array = [...finalArray[cohortIndex].q58_weight_list];
    q58Array[childIdx] = {
      ...q58Array[childIdx],
      rbl_C: value,
      date_C: value === 2 ? q58Array[childIdx].date_C : null,
      touched: true,
      ...Q58_C_ERROR_RESET,
      // Column D becomes "Not applicable" only when "Weight not assessed" is
      // selected - clear its selection/values (not just its errors) so a stale
      // rbl_D/weight doesn't silently reappear if C is switched back later.
      ...(value === 1
        ? {
            ...Q58_D_ERROR_RESET,
            rbl_D: null,
            weightKg_D: "",
            weightLbs_D: "",
          }
        : null),
    };
    finalArray[cohortIndex] = {
      ...finalArray[cohortIndex],
      q58_weight_list: q58Array,
    };
    setAEDetails(finalArray);
  };

  const HandleQ58WeightUnitChange = (cohortIndex, childIdx, value) => {
    const finalArray = [...aeDetails];
    const q58Array = [...finalArray[cohortIndex].q58_weight_list];

    q58Array[childIdx] = {
      ...q58Array[childIdx],
      rbl_D: value,
      weightKg_D: value === 1 ? q58Array[childIdx].weightKg_D : "",
      weightLbs_D: value === 2 ? q58Array[childIdx].weightLbs_D : "",
      D_empty_err: false,
      D_kg_range_err: false,
      D_lbs_range_err: false,
    };

    finalArray[cohortIndex] = {
      ...finalArray[cohortIndex],
      q58_weight_list: q58Array,
    };
    setAEDetails(finalArray);
  };

  const HandleQ58WeightValueChange = (cohortIndex, childIdx, field, value) => {
    const finalArray = [...aeDetails];
    const q58Array = [...finalArray[cohortIndex].q58_weight_list];

    const autoSelectedUnit =
      field === "weightKg_D" ? 1 : field === "weightLbs_D" ? 2 : null;

    q58Array[childIdx] = {
      ...q58Array[childIdx],
      weightKg_D: field === "weightKg_D" ? value : "",
      weightLbs_D: field === "weightLbs_D" ? value : "",
      rbl_D: autoSelectedUnit ?? q58Array[childIdx].rbl_D,
      D_empty_err: false,
      D_kg_range_err: false,
      D_lbs_range_err: false,
    };

    finalArray[cohortIndex] = {
      ...finalArray[cohortIndex],
      q58_weight_list: q58Array,
    };
    setAEDetails(finalArray);
  };

  const DeleteQ59Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q59Array = [...aeDetails[cohortIndex].q59_dysgeusia_list];
    q59Array.splice(childIdx, 1);
    finalArray[cohortIndex].q59_dysgeusia_list = q59Array;

    setAEDetails(finalArray);
  };
  const AddQ59Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q59_dysgeusia_list: [
        ...newArray[cohortIndex].q59_dysgeusia_list,
        q59_dysgeusia_row,
      ],
    };
    setAEDetails(newArray);
  };
  const DeleteQ60Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q60Array = [...aeDetails[cohortIndex].q60_nail_list];
    q60Array.splice(childIdx, 1);
    finalArray[cohortIndex].q60_nail_list = q60Array;

    setAEDetails(finalArray);
  };
  const AddQ60Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q60_nail_list: [...newArray[cohortIndex].q60_nail_list, q60_nail_row],
    };
    setAEDetails(newArray);
  };
  const DeleteQ61Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q61Array = [...aeDetails[cohortIndex].q61_skin_list];
    q61Array.splice(childIdx, 1);
    finalArray[cohortIndex].q61_skin_list = q61Array;

    setAEDetails(finalArray);
  };
  const AddQ61Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q61_skin_list: [...newArray[cohortIndex].q61_skin_list, q61_skin_row],
    };
    setAEDetails(newArray);
  };
  const DeleteQ62Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q62Array = [...aeDetails[cohortIndex].q62_spm_list];
    q62Array.splice(childIdx, 1);
    finalArray[cohortIndex].q62_spm_list = q62Array;

    setAEDetails(finalArray);
  };

  const AddQ62Row = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q62_spm_list: [
                ...(item.q62_spm_list ?? []),
                {
                  ...q62_spm_row,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ63Row = (cohortIndex, childIdx) => {
    const finalArray = [...aeDetails];
    const q63Array = [...aeDetails[cohortIndex].q63_neurotoxicity_list];
    q63Array.splice(childIdx, 1);

    // QAQC-045 Rule A - recompute the table-level warning after deletion.
    const cohortTreatmentIdForNeurotox =
      finalArray[cohortIndex].cohortTreatmentId ?? null;
    const neurotoxTreatmentSelection =
      Q63NeurotoxicityTreatmentSelectionList?.find(
        (x) => x.cohortTreatmentId === cohortTreatmentIdForNeurotox,
      );
    const isNeurotoxTreatmentSelected =
      neurotoxTreatmentSelection?.selected ?? false;
    const hasNeurotoxEvent = q63Array.some(
      (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
    );
    const q63q39Warning = isNeurotoxTreatmentSelected && !hasNeurotoxEvent;

    finalArray[cohortIndex].q63_neurotoxicity_list = q63Array.map((r) => ({
      ...r,
      Q63q39WarningMsg: q63q39Warning,
    }));

    setAEDetails(finalArray);

    // QAQC-045 Rule B/C/D - recompute since row occurrence changed.
    RecomputeQ63SubtypeWarningForCohort(
      cohortTreatmentIdForNeurotox,
      q63Array,
      cohortIndex,
    );
    RecomputeQ63SubtypeWarningForCohortFromQ26(
      cohortTreatmentIdForNeurotox,
      q63Array,
      cohortIndex,
    );
    RecomputeQ63SubtypeWarningForCohortFromQ38(
      cohortTreatmentIdForNeurotox,
      q63Array,
      cohortIndex,
    );
  };
  const AddQ63Row = (cohortIndex) => {
    let newArray = [...aeDetails];

    newArray[cohortIndex] = {
      ...newArray[cohortIndex],
      q63_neurotoxicity_list: [
        ...newArray[cohortIndex].q63_neurotoxicity_list,
        q63_neurotoxicity_row,
      ],
    };
    setAEDetails(newArray);
  };

  const AddQ64Row = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q64_enterocolitis_list: [
                ...(item.q64_enterocolitis_list ?? []),
                {
                  ...q64_enterocolitis_row,
                  patientId: pid,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ64Row = (cohortIndex, childIdx) => {
    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q64_enterocolitis_list: item.q64_enterocolitis_list.filter(
                (_, i) => i !== childIdx,
              ),
            },
      ),
    );
  };

  const AddQ65Row = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q65_lymphopenia_list: [
                ...(item.q65_lymphopenia_list ?? []),
                {
                  ...q65_lymphopenia_row,
                  patientId: pid,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ571BRow = (cohortIndex, childIdx) => {
    setAEDetails((prev) => {
      const updatedArr = prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q571B_immunoglobin_list: item.q571B_immunoglobin_list.filter(
                (_, i) => i !== childIdx,
              ),
            },
      );

      const qQ571BWarnings = validateQ571BWarnings(updatedArr);
      setQ571BWarnings(qQ571BWarnings);

      return updatedArr;
    });
  };

  const AddQ571BRow = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q571B_immunoglobin_list: [
                ...(item.q571B_immunoglobin_list ?? []),
                {
                  ...q571B_immunoglobin_row,
                  patientId: pid,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ65Row = (cohortIndex, childIdx) => {
    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q65_lymphopenia_list: item.q65_lymphopenia_list.filter(
                (_, i) => i !== childIdx,
              ),
            },
      ),
    );
  };

  const AddQ66Row = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q66_dizziness_list: [
                ...(item.q66_dizziness_list ?? []),
                {
                  ...q66_dizziness_row,
                  patientId: pid,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ66Row = (cohortIndex, childIdx) => {
    setAEDetails((prev) => {
      const updatedArr = prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q66_dizziness_list: item.q66_dizziness_list.filter(
                (_, i) => i !== childIdx,
              ),
            },
      );

      if (isNewEcrfC) {
        setQ66Warnings(
          validateQ66Warnings(updatedArr, q66QDizzinessSelectionList),
        );
      }

      return updatedArr;
    });
  };

  const AddQ67Row = (cohortIndex, cohortTreatmentId) => {
    if (cohortIndex === null || cohortIndex === undefined) return;

    setAEDetails((prev) =>
      prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q67_ataxia_list: [
                ...(item.q67_ataxia_list ?? []),
                {
                  ...q67_ataxia_row,
                  patientId: pid,
                  cohortTreatmentId,
                },
              ],
            },
      ),
    );
  };

  const DeleteQ67Row = (cohortIndex, childIdx) => {
    setAEDetails((prev) => {
      const updatedArr = prev.map((item, idx) =>
        idx !== cohortIndex
          ? item
          : {
              ...item,
              q67_ataxia_list: item.q67_ataxia_list.filter(
                (_, i) => i !== childIdx,
              ),
            },
      );

      if (isNewEcrfC) {
        setQ67Warnings(
          validateQ67Warnings(updatedArr, q67QAtaxiaSelectionList),
        );
      }

      return updatedArr;
    });
  };

  useEffect(() => {
    async function FetchData() {
      if (!isNaN(pid) && Number(pid) !== 0) {
        ToggleLoader(true);
        await LoadData();
        ToggleLoader(false);
      }
    }
    FetchData();
  }, []);

  async function LoadData() {
    try {
      if (pid > 0) {
        const response = await AdverseEventsService.GetAdverseEventsData(pid);
        if (response?.status !== HTTPResponse.OK) {
          if (response?.status === HTTPResponse.Unauthorized)
            HandleSessionTimeout();
          else if (response?.status === HTTPResponse.BadRequest)
            throw response.error;
        } else {
          const storedData = GetLocalStorageData("CohortTreatment");

          if (storedData) {
            const cohortTreatmentModelListt = JSON.parse(storedData);
            const rrmmTmtList = cohortTreatmentModelListt.filter(
              (c) => c.isEligible === true && !c.ndmm,
            );
            if (rrmmTmtList && rrmmTmtList.length > 0) {
              let list = [];
              const aeDateList = response?.data?.aE_DateList;
              const isNewEcrfB = response?.data?.isNewEcrfB ?? false;
              const isNewEcrfC = response?.data?.isNewEcrfC ?? false;

              const q36CrsSelectionList =
                response?.data?.q36CohortCrsHospitalizationSelections || [];
              setQ36CrsSelectionList(q36CrsSelectionList);
              const q36IcansSelectionList =
                response?.data?.q36CohortIcansHospitalizationSelections || [];
              setQ36IcansSelectionList(q36IcansSelectionList);
              const q53CrsTreatmentSelectionList =
                response?.data?.q53CohortCrsTreatmentSelections || [];
              setQ53CrsTreatmentSelectionList(q53CrsTreatmentSelectionList);
              const q63NeurotoxicityTreatmentSelectionList =
                response?.data?.q63CohortNeurotoxicityTreatmentSelections || [];
              setQ63NeurotoxicityTreatmentSelectionList(
                q63NeurotoxicityTreatmentSelectionList,
              );
              const q63NeurotoxicitySubtypeSelectionList =
                response?.data?.q63CohortNeurotoxicitySubtypeSelections || [];
              setQ63NeurotoxicitySubtypeSelectionList(
                q63NeurotoxicitySubtypeSelectionList,
              );
              const q63NeurotoxicitySubtypeSelectionListFromQ26 =
                response?.data
                  ?.q63CohortNeurotoxicitySubtypeSelectionsFromQ26 || [];
              setQ63NeurotoxicitySubtypeSelectionListFromQ26(
                q63NeurotoxicitySubtypeSelectionListFromQ26,
              );
              const q63NeurotoxicitySubtypeSelectionListFromQ38 =
                response?.data
                  ?.q63CohortNeurotoxicitySubtypeSelectionsFromQ38 || [];
              setQ63NeurotoxicitySubtypeSelectionListFromQ38(
                q63NeurotoxicitySubtypeSelectionListFromQ38,
              );
              setEndOfFollowUpDate(response?.data?.endofFollowUpDate);
              setMmDiagnosisDate(response?.data?.mmDiagnosisDate);
              setIsNewEcrfA(response?.data?.isNewEcrfA ?? false);
              setIsNewEcrfB(isNewEcrfB);
              setIsNewEcrfC(isNewEcrfC);
              setAdverseEventWarningTec(response?.data?.q33AdverseSelectedTec);
              setAdverseEventWarningTal(response?.data?.q33AdverseSelectedTal);
              setAdverseEventWarningElr(response?.data?.q33AdverseSelectedElr);
              setAdverseEventWarningLin(response?.data?.q33AdverseSelectedLin);
              setAdverseEventWarningCarvykti(
                response?.data?.q33AdverseSelectedCarvykti,
              );
              setAdverseEventWarningAbecma(
                response?.data?.q33AdverseSelectedAbecma,
              );

              // One AE group per cohort treatment + lot, so a cohort received
              // in several lots gets its own Q53-Q67 group for each lot.
              const rrmmEntries = rrmmTmtList.flatMap((ct) => {
                const savedLots = (response?.data?.adverseEventsData ?? [])
                  .filter((a) => a.cohortTreatmentId === ct.cohortTreatmentID)
                  .map((a) => a.lotnumber);
                const lots = [
                  ...new Set(
                    [
                      ...(ct.receivedLinesOfTherapy ?? []).map(
                        (l) => l.lineOfTherapy,
                      ),
                      ...savedLots,
                    ].filter((l) => l > 0),
                  ),
                ].sort((x, y) => x - y);
                if (lots.length === 0)
                  lots.push(ct.lineOfTherapy > 0 ? ct.lineOfTherapy : null);
                return lots.map((lot, lotIdx) => ({
                  ct,
                  lot,
                  isFirstLot: lotIdx === 0,
                  lotCount: lots.length,
                }));
              });

              list = rrmmEntries.map(({ ct, lot, isFirstLot, lotCount }) => {
                const cohortTreatmentId = ct.cohortTreatmentID;
                const cohortTreatmentName = ct.cohortTreatmentName;
                const superScriptText = ct.cohortTreatmentNameSuperscript;
                const cohortIndexDate = ct.earliestCohortIndexDate;
                let aeEndDate = "[AE end date]";
                let q58BackendPeriodEndDate = null;

                if (aeDateList?.length > 0) {
                  const aeDateEntry = aeDateList?.find(
                    (a) => a.cohortTreatmentId === ct.cohortTreatmentID,
                  );
                  aeEndDate = aeDateEntry?.aeEndDate ?? "[AE end date]";
                  q58BackendPeriodEndDate =
                    aeDateEntry?.q58PeriodEndDate ?? null;
                }

                const isTECVAYLI =
                  ct.cohortTreatmentID === RRMMCohortTreatment.TECVAYLI;
                const isTALVEY =
                  ct.cohortTreatmentID === RRMMCohortTreatment.TALVEY;
                const isELREXFIO =
                  ct.cohortTreatmentID === RRMMCohortTreatment.ELREXFIO;
                const isLinvoseltamab =
                  ct.cohortTreatmentID === RRMMCohortTreatment.Linvoseltamab;
                const isCARVYKTI =
                  ct.cohortTreatmentID === RRMMCohortTreatment.CARVYKTI;
                const isABECMA =
                  ct.cohortTreatmentID === RRMMCohortTreatment.ABECMA;

                //Check if data is present for a cohort tmt
                const cohortDbRows =
                  response?.data?.adverseEventsData?.filter(
                    (a) => a.cohortTreatmentId === ct.cohortTreatmentID,
                  ) ?? [];
                // rows saved before lots were tracked have no lot number -> first lot
                const dbRow =
                  cohortDbRows.find((a) => a.lotnumber === lot) ??
                  (isFirstLot
                    ? cohortDbRows.find((a) => a.lotnumber == null)
                    : undefined) ??
                  (lotCount === 1 ? cohortDbRows[0] : undefined);
                let infectionEpisodeList = [];

                let q53_crs_list = [q53_crs_row]; //[]; //show for all cohorts
                let q54_icans_list = [q54_icans_row]; //show for all cohorts
                let q55_neutropenia_list =
                  isTECVAYLI ||
                  isTALVEY ||
                  isELREXFIO ||
                  isLinvoseltamab ||
                  isCARVYKTI ||
                  isABECMA
                    ? [q55_neutropenia_row]
                    : null; //“Tec”, “Elran”, “Linvo”, “Cilta-cel” or “Ide-cel”
                let q56_cytopenia_list =
                  isTECVAYLI ||
                  isTALVEY ||
                  isELREXFIO ||
                  isLinvoseltamab ||
                  isCARVYKTI ||
                  isABECMA
                    ? [q56_cytopenia_row]
                    : null; // “Tec”, “Elran”, “Linvo”, “Cilta-cel” or “Ide-cel”

                let q57_infection_list =
                  isTECVAYLI || isTALVEY || isELREXFIO || isLinvoseltamab
                    ? [
                        {
                          ...q57_infection_row,
                          infectionKey: GenerateKey(),
                        },
                      ]
                    : null; //“Tec”, “Tal”, “Elran”, or “Linvo”

                const q58Meta = isNewEcrfC
                  ? BuildQ58Meta(
                      cohortIndexDate,
                      q58BackendPeriodEndDate,
                      isTECVAYLI,
                      isTALVEY,
                    )
                  : null; //“Tec” or “Tal”, only when Element C is enabled
                let q58_weight_list = q58Meta?.rows ?? null;
                if (
                  q58_weight_list?.length > 0 &&
                  dbRow?.aE_WeightTrajectoryList?.length > 0
                ) {
                  q58_weight_list = q58_weight_list.map((row) => {
                    const saved = dbRow.aE_WeightTrajectoryList.find(
                      (b) => b.assessmentTargetdate === row.targetDate_B,
                    );
                    if (!saved) return row;

                    return {
                      ...row,
                      aE_WeightTrajectoryId: saved.aE_WeightTrajectoryId,
                      touched: true,
                      rbl_C: saved.weightAssessment,
                      date_C: saved.weightAssessmentDate ?? null,
                      rbl_D: saved.weightUnit ?? null,
                      weightKg_D:
                        saved.weightUnit === 1 && saved.weightUnitValue != null
                          ? String(saved.weightUnitValue)
                          : "",
                      weightLbs_D:
                        saved.weightUnit === 2 && saved.weightUnitValue != null
                          ? String(saved.weightUnitValue)
                          : "",
                    };
                  });
                  q58_weight_list = ValidateQ58Rows(q58_weight_list);
                }
                let q58_indexDate = q58Meta?.indexDateStr ?? null;
                let q58_periodEndDate = q58Meta?.periodEndStr ?? null;
                let q58_windowDays = q58Meta?.windowDays ?? null;
                let q58_intervalLabel = q58Meta?.intervalLabel ?? null;
                let q59_dysgeusia_list = isTALVEY ? [q59_dysgeusia_row] : null; //“Tal”
                let q60_nail_list = isTALVEY ? [q60_nail_row] : null; //“Tal”
                let q61_skin_list = isTALVEY ? [q61_skin_row] : null; //“Tal”
                let q62_spm_list =
                  isCARVYKTI || isABECMA ? [q62_spm_row] : null; //“Cilta-cel” or “Ide-cel”
                // UPC-020 - Q63 is asked for "Cilta-cel" or "Ide-cel", and
                // additionally for "Tal" under eCRF New Elements C.
                let q63_neurotoxicity_list =
                  isCARVYKTI || isABECMA || (isNewEcrfC && isTALVEY)
                    ? [q63_neurotoxicity_row]
                    : null;
                let q64_enterocolitis_list =
                  isCARVYKTI || isABECMA
                    ? [
                        {
                          ...q64_enterocolitis_row,
                          patientId: pid,
                          cohortTreatmentId: ct.cohortTreatmentID,
                        },
                      ]
                    : null; //“Cilta-cel” or “Ide-cel”
                let q65_lymphopenia_list = [
                  {
                    ...q65_lymphopenia_row,
                    patientId: pid,
                    cohortTreatmentId: ct.cohortTreatmentID,
                  },
                ]; //show for all cohorts

                let q66_dizziness_list =
                  isNewEcrfC && isTALVEY
                    ? [
                        {
                          ...q66_dizziness_row,
                          patientId: pid,
                          cohortTreatmentId: ct.cohortTreatmentID,
                        },
                      ]
                    : null; //“Tal” only, Element C

                let q67_ataxia_list =
                  isNewEcrfC && isTALVEY
                    ? [
                        {
                          ...q67_ataxia_row,
                          patientId: pid,
                          cohortTreatmentId: ct.cohortTreatmentID,
                        },
                      ]
                    : null; //“Tal” only, Element C

                let q571B_immunoglobin_list = [
                  {
                    ...q571B_immunoglobin_row,
                    patientId: pid,
                    cohortTreatmentId: ct.cohortTreatmentID,
                  },
                ]; //“Tec”, “Tal”, “Elran”, or “Linvo”

                if (dbRow) {
                  infectionEpisodeList = dbRow.infectionEpisodeList ?? [];

                  if (dbRow.aE_CRSList && dbRow.aE_CRSList.length > 0) {
                    // QAQC-043 / QAQC-044 - these warnings must not be shown
                    // on page load; they are only computed/displayed once the
                    // abstractor clicks Validate/Next (see Validate() below).
                    q53_crs_list = dbRow.aE_CRSList.map((b) => ({
                      ...q53_crs_row,
                      crsId: b.aE_CRSId,
                      rbl_B: b.crsNotExperienced
                        ? 1
                        : b.csrDateUnk
                          ? 99
                          : !isStringEmpty(b.csrDate)
                            ? 2
                            : null,
                      date_B: b.csrDate,
                      rbl_C: b.severity,

                      rbl_resolution: b.crsresolution,
                      date_resolution: b.crsresolutiondate,
                    }));
                  }
                  if (dbRow.aE_IcansList && dbRow.aE_IcansList.length > 0) {
                    // QAQC-043 - table-level check, same reasoning as CRS above.
                    const icansSelection = q36IcansSelectionList?.find(
                      (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                    );
                    const isQ36IcansSelected =
                      icansSelection?.selected ?? false;
                    const hasIcansEvent = dbRow.aE_IcansList.some(
                      (b) => b.icansNotExperienced !== true,
                    );
                    const q54q36Warning = isQ36IcansSelected && !hasIcansEvent;

                    q54_icans_list = dbRow.aE_IcansList.map((b) => ({
                      ...q54_icans_row,
                      icansId: b.aE_ICANSId,
                      rbl_B: b.icansNotExperienced
                        ? 1
                        : b.icansOccuredDateUnk
                          ? 99
                          : !isStringEmpty(b.icansOccuredDate)
                            ? 2
                            : null,
                      date_B: b.icansOccuredDate,

                      rblC: b.severity,
                      rblresolution: b.icansresolution,
                      dateresolution: b.icansresolutiondate,

                      Q54q36WarningMsg: q54q36Warning,
                    }));
                  } else {
                    // QAQC-043 - a cohort with no saved Q54 rows is exactly the
                    // case this warning exists for, so it must be computed here
                    // too, not only when rows already exist.
                    const icansSelection = q36IcansSelectionList?.find(
                      (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                    );
                    if (
                      icansSelection?.selected === true &&
                      q54_icans_list?.length > 0
                    ) {
                      q54_icans_list = q54_icans_list.map((r) => ({
                        ...r,
                        Q54q36WarningMsg: true,
                      }));
                    }
                  }
                  if (
                    dbRow.aE_NeutropeniumList &&
                    dbRow.aE_NeutropeniumList.length > 0
                  ) {
                    q55_neutropenia_list = dbRow.aE_NeutropeniumList.map(
                      (b) => ({
                        ...q55_neutropenia_row,
                        neutropeniaId: b.aE_NeutropeniaId,
                        rbl_B: b.neutropeniaNotExperienced
                          ? 1
                          : b.neutropeniaOnsetDateUnk
                            ? 99
                            : !isStringEmpty(b.neutropeniaOnsetDate)
                              ? 2
                              : null,
                        date_B: b.neutropeniaOnsetDate,
                        // Date of resolution [C] retired
                        // rbl_C: b.neutropeniaNotResolved
                        //   ? 1
                        //   : b.neutropeniaResolutionDateUnk
                        //     ? 3
                        //     : b.neutropeniaResolvedUnk
                        //       ? 99
                        //       : !isStringEmpty(b.neutropeniaResolutionDate)
                        //         ? 2
                        //         : null,
                        // date_C: b.neutropeniaResolutionDate,

                        rblD: b.severity,
                      }),
                    );
                  }
                  if (
                    dbRow.aE_CytopeniumList &&
                    dbRow.aE_CytopeniumList.length > 0
                  ) {
                    q56_cytopenia_list = dbRow.aE_CytopeniumList.map((b) => ({
                      ...q56_cytopenia_row,
                      cytopeniaId: b.aE_CytopeniaId,
                      rbl_B: b.cytopeniaNotExperienced
                        ? 1
                        : b.cytopeniaOnsetDateUnk
                          ? 99
                          : !isStringEmpty(b.cytopeniaOnsetDate)
                            ? 2
                            : null,
                      date_B: b.cytopeniaOnsetDate,
                      // Date of resolution [C] retired
                      // rbl_C: b.cytopeniaNotResolved
                      //   ? 1
                      //   : b.cytopeniaResolutionDateUnk
                      //     ? 3
                      //     : b.cytopeniaResolvedUnk
                      //       ? 99
                      //       : !isStringEmpty(b.cytopeniaResolutionDate)
                      //         ? 2
                      //         : null,
                      // date_C: b.cytopeniaResolutionDate,
                      rblD: b.severity,
                    }));
                  }
                  if (
                    dbRow.aE_InfectionList &&
                    dbRow.aE_InfectionList.length > 0
                  ) {
                    q57_infection_list = dbRow.aE_InfectionList.map((b) => ({
                      ...q57_infection_row,
                      infectionId: b.aE_InfectionId,
                      infectionKey: b.infectionKey,
                      rbl_B: b.infectionNotExperienced
                        ? 1
                        : b.infectionOccuredDateUnk
                          ? 99
                          : !isStringEmpty(b.infectionOccuredDate)
                            ? 2
                            : null,
                      date_B: b.infectionOccuredDate,

                      rbl_resolution:
                        b.resolutionDateEventC === 99
                          ? 4
                          : b.resolutionDateEventC,
                      date_resolution: b.resolvedDateC,

                      rbl_H: b.iggAssessedC,
                      date_H: b.iggAssessedDateC,

                      rbl_I: b.iggLevelC,
                      unit_I:
                        b.iggLevelUnitC != null ? String(b.iggLevelUnitC) : "",

                      chk_C_1: b.infectionTypeBacterial,
                      chk_C_2: b.infectionTypeViral,
                      chk_C_3: b.infectionTypeFungal,
                      rb_C: b.infectionTypeUnk,
                      // Infection organism [D] retired
                      // chk_D_1: b.infectionOrganismAdenovirus,
                      // chk_D_2: b.infectionOrganismCandida,
                      // chk_D_3: b.infectionOrganismCovid19,
                      // chk_D_4: b.infectionOrganismCytomegalovirus,
                      // chk_D_5: b.infectionOrganismEscherichia,
                      // chk_D_6: b.infectionOrganismHelicobacter,
                      // chk_D_7: b.infectionOrganismHepatitis,
                      // chk_D_8: b.infectionOrganismHerpes,
                      // chk_D_9: b.infectionOrganismInfluenza,
                      // chk_D_10: b.infectionOrganismPjp,
                      // chk_D_11: b.infectionOrganismProgressive,
                      // chk_D_12: b.infectionOrganismRsv,
                      // chk_D_13: b.infectionOrganismStreptococcus,
                      // chk_D_14: b.infectionOrganismVaricella,
                      // chk_D_15: b.infectionOrganismOth,
                      // txt_D_oth: b.infectionOrganismOthTxt || "",
                      // rb_D: b.infectionOrganismUnk,
                      rbl_E: b.severity,
                      rbl_F: b.ledErVisit,
                      chk_G_1: b.managementSettingOutpatient,
                      chk_G_2: b.managementSettingInpatient,
                      chk_G_3: b.managementSettingEmergencyroom,
                      chk_G_4: b.managementSettingIntensivecareunit,
                      chk_G_5: b.managementSettingOth,
                      rb_G_unk: b.managementSettingUnk,
                      txt_G_oth: b.managementSettingOthTxt || "",
                    }));
                  }
                  if (
                    dbRow.aE_DysgeusiumList &&
                    dbRow.aE_DysgeusiumList.length > 0
                  ) {
                    q59_dysgeusia_list = dbRow.aE_DysgeusiumList.map((b) => ({
                      ...q59_dysgeusia_row,
                      dysgeusiaId: b.aE_DysgeusiaId,
                      rbl_B: b.dysgeusiaNotexperienced
                        ? 1
                        : b.dysgeusiaOnsetDateUnk
                          ? 99
                          : !isStringEmpty(b.dysgeusiaOnsetOcurredDate)
                            ? 2
                            : null,
                      date_B: b.dysgeusiaOnsetOcurredDate,

                      chk_CC_1: b.interventionDexamethasoneMouthwash,
                      chk_CC_1_P: b.dexamethasoneMouthwashProphylactic,
                      chk_CC_1_T: b.dexamethasoneMouthwashTherapeutic,
                      chk_CC_2: b.interventionNystatinMouthwash,
                      chk_CC_2_P: b.nystatinMouthwashProphylactic,
                      chk_CC_2_T: b.nystatinMouthwashTherapeutic,
                      chk_CC_3: b.interventionSalineMouthwash,
                      chk_CC_3_P: b.salineMouthwashProphylactic,
                      chk_CC_3_T: b.salineMouthwashTherapeutic,
                      chk_CC_4: b.interventionBioteneMouthwash,
                      chk_CC_4_P: b.bioteneMouthwashProphylactic,
                      chk_CC_4_T: b.bioteneMouthwashTherapeutic,
                      chk_CC_5: b.interventionZincMouthwash,
                      chk_CC_5_P: b.zincMouthwashProphylactic,
                      chk_CC_5_T: b.zincMouthwashTherapeutic,
                      chk_CC_6: b.interventionVitaminbMouthwash,
                      chk_CC_6_P: b.vitaminbMouthwashProphylactic,
                      chk_CC_6_T: b.vitaminbMouthwashTherapeutic,
                      chk_CC_7: b.interventionOtherMouthwash,
                      txt_CC_oth: b.interventionOthertextMouthwash,
                      chk_CC_7_P: b.otherMouthwashProphylactic,
                      chk_CC_7_T: b.otherMouthwashTherapeutic,
                      rb_CC: b.interventionUnknown,

                      rbl_C: b.noImprovement
                        ? 1
                        : b.improvementDateUnk
                          ? 3
                          : b.improvementUnk
                            ? 99
                            : !isStringEmpty(b.improvementDate)
                              ? 2
                              : null,
                      date_C: b.improvementDate,
                      rbl_D: b.dysgeusiaNotResolved
                        ? 1
                        : b.resolutionOccuredDateUnk
                          ? 3
                          : b.dysgeusiaResolvedUnk
                            ? 99
                            : !isStringEmpty(b.resolutionOccuredDate)
                              ? 2
                              : null,
                      date_D: b.resolutionOccuredDate,
                    }));
                  }
                  if (
                    dbRow.aE_NaildisorderList &&
                    dbRow.aE_NaildisorderList.length > 0
                  ) {
                    q60_nail_list = dbRow.aE_NaildisorderList.map((b) => ({
                      ...q60_nail_row,
                      nailDisorderId: b.aE_NailDisorderId,
                      rbl_B: b.naildisorderNotexperienced
                        ? 1
                        : b.naildisorderOnsetDateUnk
                          ? 99
                          : !isStringEmpty(b.naildisorderOnsetOcurredDate)
                            ? 2
                            : null,
                      date_B: b.naildisorderOnsetOcurredDate,
                      rbl_C: b.noImprovement
                        ? 1
                        : b.improvementDateUnk
                          ? 3
                          : b.improvementUnk
                            ? 99
                            : !isStringEmpty(b.improvementDate)
                              ? 2
                              : null,
                      date_C: b.improvementDate,

                      rbl_D: b.naildisorderNotResolved
                        ? 1
                        : b.naildisorderOccuredDateUnk
                          ? 3
                          : b.naildisorderResolvedUnk
                            ? 99
                            : !isStringEmpty(b.naildisorderOccuredDate)
                              ? 2
                              : null,
                      date_D: b.naildisorderOccuredDate,
                    }));
                  }
                  if (
                    dbRow.aE_SkinDisorderList &&
                    dbRow.aE_SkinDisorderList.length > 0
                  ) {
                    q61_skin_list = dbRow.aE_SkinDisorderList.map((b) => ({
                      ...q61_skin_row,
                      skinDisorderId: b.aE_SkinDisorderId,
                      rbl_B: b.skindisorderNotexperienced
                        ? 1
                        : b.skindisorderOnsetDateUnk
                          ? 99
                          : !isStringEmpty(b.skindisorderOnsetOcurredDate)
                            ? 2
                            : null,
                      date_B: b.skindisorderOnsetOcurredDate,
                      rbl_C: b.noImprovement
                        ? 1
                        : b.improvementDateUnk
                          ? 3
                          : b.improvementUnk
                            ? 99
                            : !isStringEmpty(b.improvementDate)
                              ? 2
                              : null,
                      date_C: b.improvementDate,

                      rbl_D: b.skindisorderNotResolved
                        ? 1
                        : b.skindisorderOccuredDateUnk
                          ? 3
                          : b.skindisorderResolvedUnk
                            ? 99
                            : !isStringEmpty(b.skindisorderOccuredDate)
                              ? 2
                              : null,
                      date_D: b.skindisorderOccuredDate,
                    }));
                  }

                  if (dbRow.aE_SecondaryPrimaryMalignancyList?.length > 0) {
                    q62_spm_list = dbRow.aE_SecondaryPrimaryMalignancyList.map(
                      (b) => ({
                        ...q62_spm_row,
                        cohortTreatmentId: b.cohortTreatmentId,
                        spmId: b.aE_SecondaryPrimaryMalignancyId,
                        rbl_B: b.secondMalignancyNotReceived
                          ? 1
                          : b.secondMalignancyOccuredDateUnk
                            ? 99
                            : !isStringEmpty(b.secondMalignancyOccuredDate)
                              ? 2
                              : null,
                        date_B: b.secondMalignancyOccuredDate,
                        q62_rbl_C: b.secondMalignancyResolution ?? null,
                        q62_date_C: b.secondMalignancyResolutionDate ?? "",
                        q62_severity: b.severity ?? null,
                      }),
                    );
                  }
                  if (
                    dbRow.aE_NeurotoxicityList &&
                    dbRow.aE_NeurotoxicityList.length > 0
                  ) {
                    // QAQC-045 Rule A - table-level check across all rows,
                    // same reasoning as QAQC-043/044.
                    const neurotoxTreatmentSelection =
                      q63NeurotoxicityTreatmentSelectionList?.find(
                        (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                      );
                    const isNeurotoxTreatmentSelected =
                      neurotoxTreatmentSelection?.selected ?? false;
                    const hasNeurotoxEvent = dbRow.aE_NeurotoxicityList.some(
                      (b) => b.neurotoxicityNotExperienced !== true,
                    );
                    const q63q39Warning =
                      isNeurotoxTreatmentSelected && !hasNeurotoxEvent;

                    q63_neurotoxicity_list = dbRow.aE_NeurotoxicityList.map(
                      (b) => ({
                        ...q63_neurotoxicity_row,
                        neurotoxicityId: b.aE_NeurotoxicityId,
                        Q63q39WarningMsg: false, // shown only after Validate is clicked
                        rbl_B: b.neurotoxicityNotExperienced
                          ? 1
                          : b.neurotoxicityOnsetOccuredDateUnk
                            ? 99
                            : !isStringEmpty(b.neurotoxicityOnsetOccuredDate)
                              ? 2
                              : null,
                        date_B: b.neurotoxicityOnsetOccuredDate,
                        rbl_C: b.neurotoxicityNotResolved
                          ? 1
                          : b.resolutionOccuredDateUnk
                            ? 3
                            : b.neurotoxicityResolvedUnk
                              ? 99
                              : !isStringEmpty(b.resolutionOccuredDate)
                                ? 2
                                : null,
                        date_C: b.resolutionOccuredDate,
                        rbl_D: b.neurotoxicitySubtype,
                        txt_D_oth: b.neurotoxicitySubtypeOth || "",
                        rbl_E: b.severity,
                        chk_F_1: b.managementSettingOutpatient,
                        chk_F_2: b.managementSettingHospital,
                        rb_F: b.managementSettingUnk,
                        chk_G_1: b.interventionsCorticosteroids,
                        chk_G_2: b.interventionsAnakinra,
                        chk_G_3: b.interventionsTocilizumab,
                        chk_G_4: b.interventionsSiltuximab,
                        chk_G_5: b.interventionsOth,
                        rb_G: b.interventionsUnk,

                        chkFInpatient: b.managementSettingInpatient,
                        chkFEmergencyRoom: b.managementSettingEmergencyroom,
                        chkFIntensiveCareUnit:
                          b.managementSettingIntensivecareunit,
                        chkFUrgentCareVisit: b.managementSettingUrgentcarevisit,
                        chkFOther: b.managementSettingOth,

                        rblH: b.improvement,
                        dateH: b.improvementdate,

                        isAutoPopulated: b.isDependent || false,
                        sourceQ54IcansId: b.sourceQ54IcansId,
                      }),
                    );
                  } else {
                    // QAQC-045 Rule A - a cohort with no saved Q63 rows is
                    // exactly the case the warning exists for, so it must be
                    // computed here too, not only when rows already exist.
                    const neurotoxTreatmentSelection =
                      q63NeurotoxicityTreatmentSelectionList?.find(
                        (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                      );
                    if (
                      neurotoxTreatmentSelection?.selected === true &&
                      q63_neurotoxicity_list?.length > 0
                    ) {
                      q63_neurotoxicity_list = q63_neurotoxicity_list.map(
                        (r) => ({
                          ...r,
                          Q63q39WarningMsg: false, // shown only after Validate is clicked
                        }),
                      );
                    }
                  }

                  if (dbRow.aE_EnterocolitisList?.length > 0) {
                    q64_enterocolitis_list = dbRow.aE_EnterocolitisList.map(
                      (b) => ({
                        ...q64_enterocolitis_row,
                        adverseevententerocolitisid:
                          b.adverseevententerocolitisid,
                        patientId: b.patientId,
                        cohortTreatmentId: b.cohortTreatmentId,
                        q64_rbl_B: b.enterocolitisOnsetEventOccurred ?? null,
                        q64_date_B: b.enterocolitisEventOccurredDate ?? "",
                        q64_rbl_C: b.enterocolitisImprovement ?? null,
                        q64_date_C: b.enterocolitisImprovementDate ?? "",
                        q64_rbl_D: b.enterocolitisEventResolution,
                        q64_date_D: b.resolutionEventResolutionDate ?? "",
                        q64_chk_E_1:
                          b.enterocolitisInterventionsSupportiveCare ?? false,
                        q64_chk_E_2:
                          b.enterocolitisInterventionsAntibiotics ?? false,
                        q64_chk_E_3:
                          b.enterocolitisInterventionsSurgery ?? false,
                        q64_chk_E_4: b.enterocolitisInterventionsGcsf ?? false,
                        q64_chk_E_5: b.enterocolitisInterventionsOth ?? false,
                        q64_chk_E_othTxt:
                          b.enterocolitisInterventionsOthTxt ?? "",
                        q64_E_unk: b.enterocolitisInterventionsUnk ?? false,
                        q64_severity: b.severity,
                      }),
                    );
                  }

                  if (dbRow.aE_LymphopeniaList?.length > 0 && isNewEcrfB) {
                    q65_lymphopenia_list = dbRow.aE_LymphopeniaList.map(
                      (b) => ({
                        ...q65_lymphopenia_row,
                        adverseeventlymphopeniaid: b.adverseeventlymphopeniaid,
                        patientId: b.patientId,
                        cohortTreatmentId: b.cohortTreatmentId,
                        q65_rbl_B: b.lymphopeniaOnsetEventOccurred ?? null,
                        q65_date_B: b.lymphopeniaEventOccurredDate ?? "",
                        // Date of resolution [C] retired
                        // q65_rbl_C: b.lymphopeniaEventResolution,
                        // q65_date_C: b.lymphopeniaResolutionDate ?? "",
                        q65_severity: b.severity,
                      }),
                    );
                  }

                  if (
                    dbRow.aE_DizzinessList?.length > 0 &&
                    isNewEcrfC &&
                    isTALVEY
                  ) {
                    q66_dizziness_list = dbRow.aE_DizzinessList.map((b) => ({
                      ...q66_dizziness_row,
                      adverseeventdizzinessid: b.adverseeventdizzinessid,
                      patientId: b.patientId,
                      cohortTreatmentId: b.cohortTreatmentId,
                      q66_rbl_B: b.dizzinessOnsetEventOccurred ?? null,
                      q66_date_B: b.dizzinessEventOccurredDate ?? "",
                      q66_rbl_C: b.dizzinessEventResolution,
                      q66_date_C: b.dizzinessResolutionDate ?? "",
                    }));
                  }

                  if (
                    dbRow.aE_AtaxiaList?.length > 0 &&
                    isNewEcrfC &&
                    isTALVEY
                  ) {
                    q67_ataxia_list = dbRow.aE_AtaxiaList.map((b) => ({
                      ...q67_ataxia_row,
                      adverseeventataxiaid: b.adverseeventataxiaid,
                      patientId: b.patientId,
                      cohortTreatmentId: b.cohortTreatmentId,
                      q67_rbl_B: b.ataxiaOnsetEventOccurred ?? null,
                      q67_date_B: b.ataxiaEventOccurredDate ?? "",
                      q67_rbl_C: b.ataxiaEventResolution,
                      q67_date_C: b.ataxiaResolutionDate ?? "",
                      q67_severity: b.severity,

                      q67_chk_E_1: b.ataxiaPresentationDizziness ?? false,
                      q67_chk_E_2: b.ataxiaPresentationGait ?? false,
                      q67_chk_E_3: b.ataxiaPresentationFalls ?? false,
                      q67_chk_E_4: b.ataxiaPresentationCerebellar ?? false,
                      q67_chk_E_5: b.ataxiaPresentationDysarthria ?? false,
                      q67_chk_E_6: b.ataxiaPresentationEye ?? false,
                      q67_chk_E_7: b.ataxiaPresentationParesthesia ?? false,
                      q67_chk_E_8: b.ataxiaPresentationOth ?? false,
                      q67_chk_E_othTxt: b.ataxiaPresentationOthTxt ?? "",
                      q67_E_unk: b.ataxiaPresentationUnk ?? false,

                      q67_chk_F_1: b.ataxiaInterventionDiscontinuation ?? false,
                      q67_chk_F_2: b.ataxiaInterventionDelay ?? false,
                      q67_chk_F_3: b.ataxiaInterventionDose ?? false,
                      q67_chk_F_4: b.ataxiaInterventionSupportive ?? false,
                      q67_chk_F_5: b.ataxiaInterventionFall ?? false,
                      q67_chk_F_6: b.ataxiaInterventionNeurological ?? false,
                      q67_chk_F_7: b.ataxiaInterventionOth ?? false,
                      q67_chk_F_othTxt: b.ataxiaInterventionOthTxt ?? "",
                      q67_F_unk: b.ataxiaInterventionUnk ?? false,
                    }));
                  }

                  if (dbRow.aE_ImmunoglobinList?.length > 0 && isNewEcrfB) {
                    q571B_immunoglobin_list = dbRow.aE_ImmunoglobinList.map(
                      (b) => ({
                        ...q571B_immunoglobin_row,
                        adverseeventimmunoglobinid:
                          b.adverseeventimmunoglobinid,
                        patientId: b.patientId,
                        cohortTreatmentId: b.cohortTreatmentId,
                        q571B_rbl_B: b.immunoglobinNotAssessed ?? null,
                        q571B_date_B: b.immunoglobinAssessmentDate ?? "",
                        q571B_rbl_C: b.immunoglobinDosage,
                        q571B_unit_C: b.immunoglobinDosageUnit,
                        q571B_episode_D: b.infectionKey,
                      }),
                    );
                  }

                  return {
                    ...AERow,
                    cohortTreatmentId,
                    cohortTreatmentName,
                    superScriptText,
                    cohortIndexDate,
                    lineOfTherapy: lot ?? dbRow?.lotnumber ?? null,
                    aeEndDate,
                    q53_crs_list,
                    q54_icans_list,
                    q55_neutropenia_list,
                    q56_cytopenia_list,
                    q57_infection_list,
                    q58_weight_list,
                    q58_indexDate,
                    q58_periodEndDate,
                    q58_windowDays,
                    q58_intervalLabel,
                    q59_dysgeusia_list,
                    q60_nail_list,
                    q61_skin_list,
                    q62_spm_list,
                    q63_neurotoxicity_list,
                    q64_enterocolitis_list,
                    q65_lymphopenia_list,
                    q66_dizziness_list,
                    q67_ataxia_list,
                    q571B_immunoglobin_list,
                    infectionEpisodeList,
                  };
                } else {
                  // QAQC-044 / QAQC-045 Rule A - a cohort with no saved AE
                  // data at all is exactly the case these warnings exist for,
                  // so they must be computed on this path too.
                  // Note: QAQC-043/044 (Q53) is intentionally NOT computed
                  // here - those warnings must not be shown on page load,
                  // only once the abstractor clicks Validate/Next (see
                  // Validate() below).
                  {
                    const icansSelection = q36IcansSelectionList?.find(
                      (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                    );
                    if (
                      icansSelection?.selected === true &&
                      q54_icans_list?.length > 0
                    ) {
                      q54_icans_list = q54_icans_list.map((r) => ({
                        ...r,
                        Q54q36WarningMsg: true,
                      }));
                    }

                    const neurotoxTreatmentSelection =
                      q63NeurotoxicityTreatmentSelectionList?.find(
                        (x) => x.cohortTreatmentId === ct.cohortTreatmentID,
                      );
                    if (
                      neurotoxTreatmentSelection?.selected === true &&
                      q63_neurotoxicity_list?.length > 0
                    ) {
                      q63_neurotoxicity_list = q63_neurotoxicity_list.map(
                        (r) => ({
                          ...r,
                          Q63q39WarningMsg: false, // shown only after Validate is clicked
                        }),
                      );
                    }
                  }

                  return {
                    ...AERow,
                    cohortTreatmentId,
                    cohortTreatmentName,
                    superScriptText,
                    cohortIndexDate,
                    lineOfTherapy: lot ?? dbRow?.lotnumber ?? null,
                    aeEndDate,

                    q53_crs_list,
                    q54_icans_list,
                    q55_neutropenia_list,
                    q56_cytopenia_list,
                    q57_infection_list,
                    q58_weight_list,
                    q58_indexDate,
                    q58_periodEndDate,
                    q58_windowDays,
                    q58_intervalLabel,
                    q59_dysgeusia_list,
                    q60_nail_list,
                    q61_skin_list,
                    q62_spm_list,
                    q63_neurotoxicity_list,
                    q64_enterocolitis_list,
                    q65_lymphopenia_list,
                    q66_dizziness_list,
                    q67_ataxia_list,
                    q571B_immunoglobin_list,
                    infectionEpisodeList,
                  };
                }
              });

              const q65SelectionList =
                response?.data?.q65CohortLymphopeniaSelections || [];

              setQ65LymphopheniaSelectionList(q65SelectionList);

              const q66SelectionList =
                response?.data?.q66CohortDizzinessSelections || [];

              setQ66DizzinessSelectionList(q66SelectionList);

              const q67SelectionList =
                response?.data?.q67CohortAtaxiaSelections || [];

              setQ67AtaxiaSelectionList(q67SelectionList);

              if (isNewEcrfC) {
                setQ66Warnings(validateQ66Warnings(list, q66SelectionList));
                setQ67Warnings(validateQ67Warnings(list, q67SelectionList));
              }

              // QAQC-045 Rule B/C/D - must be computed after `list` (the AE
              // data) is populated above, otherwise these always evaluate
              // against an empty list and the warnings only ever appear
              // after the user clicks Validate (which recomputes them with
              // the current state).
              // Not shown on load - only after Validate is clicked.
              setQ63SubtypeWarnings({});
              setQ63SubtypeWarningsFromQ26({});
              setQ63SubtypeWarningsFromQ38({});

              if (isNewEcrfB) {
                const q65Warnings = validateQ65Warnings(list, q65SelectionList);
                setQ65Warnings(q65Warnings);

                const qQ571BWarnings = validateQ571BWarnings(list);
                setQ571BWarnings(qQ571BWarnings);
              }

              // QAQC-040 - AE-vs-discontinuation-reason consistency
              const aeQ26SelectionListsData = {};
              Object.keys(AeQ26ReasonConfig).forEach((qNum) => {
                aeQ26SelectionListsData[qNum] =
                  response?.data?.[AeQ26ReasonConfig[qNum].responseKey] || [];
              });
              setAeQ26SelectionLists(aeQ26SelectionListsData);
              const aeQ26WarningsData = validateAllAeQ26Warnings(
                list,
                aeQ26SelectionListsData,
              );
              // QAQC-040/041/042 for Q53 (CRS) must not be shown on page
              // load - only once the abstractor clicks Validate/Next (see
              // Validate()). Other AE types keep their existing behavior.
              // Note: must set explicit `false` flags per cohort (not an
              // empty object) - ErrorField defaults `show` to true when the
              // prop is undefined, so leaving this empty made the warnings
              // render immediately on load instead of staying hidden.
              const aeQ26WarningsQ53Data = {};
              list.forEach((ae) => {
                aeQ26WarningsQ53Data[AeKey(ae)] = {
                  q26Discontinuation: false,
                  q38DoseChange: false,
                  q43ManagementUse: false,
                };
              });
              aeQ26WarningsData[53] = aeQ26WarningsQ53Data;
              // Q63 (neurologic event) is also Validate-only
              aeQ26WarningsData[63] = { ...aeQ26WarningsQ53Data };
              setAeQ26Warnings(aeQ26WarningsData);

              // Recalculate warning flags for every cohort AFTER building the data
              const finalList = list.map((ae) =>
                computeAEWarnings(response?.data, ae),
              );

              setAEDetails(finalList);
            }
          }
        }
      }
    } catch (error) {
      ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
    }
  }

  const validateQ65Warnings = (aelist, q65SelectionList) => {
    const warnings = {};
    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;

        const selection = q65SelectionList?.find(
          (x) => x.cohortTreatmentId === cohortTreatmentId,
        );

        const isQ26Selected = selection?.q26Selected ?? false;
        const isQ38Selected = selection?.q38Selected ?? false;
        const isQ43Selected = selection?.q43Selected ?? false;

        const lymphopeniaList = ae.q65_lymphopenia_list || [];

        const hasEvents = lymphopeniaList.some(
          (e) => e.q65_rbl_B === 2 || e.q65_rbl_B === 99,
        );

        warnings[AeKey(ae)] = {
          q26Discontinuation: isQ26Selected && !hasEvents,
          q38DoseChange: isQ38Selected && !hasEvents,
          q43SteroidUse: isQ43Selected && !hasEvents,
        };
      });
    }

    return warnings;
  };

  const validateQ66Warnings = (aelist, q66SelectionList) => {
    const warnings = {};
    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;

        const selection = q66SelectionList?.find(
          (x) => x.cohortTreatmentId === cohortTreatmentId,
        );

        const isQ26Selected = selection?.q26Selected ?? false;
        const isQ38Selected = selection?.q38Selected ?? false;

        const dizzinessList = ae.q66_dizziness_list || [];

        // Only warn once the user has explicitly picked "No events occurred"
        // for the (only) row that allows it — not merely because no row has
        // been answered yet.
        const noEventsSelected = dizzinessList.some((e) => e.q66_rbl_B === 1);

        warnings[AeKey(ae)] = {
          q26Discontinuation: isQ26Selected && noEventsSelected,
          q38DoseChange: isQ38Selected && noEventsSelected,
        };
      });
    }

    return warnings;
  };

  const validateQ67Warnings = (aelist, q67SelectionList) => {
    const warnings = {};
    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;

        const selection = q67SelectionList?.find(
          (x) => x.cohortTreatmentId === cohortTreatmentId,
        );

        const isQ26Selected = selection?.q26Selected ?? false;
        const isQ38Selected = selection?.q38Selected ?? false;

        const ataxiaList = ae.q67_ataxia_list || [];

        // Only warn once the user has explicitly picked "No events occurred"
        // for the (only) row that allows it — not merely because no row has
        // been answered yet.
        const noEventsSelected = ataxiaList.some((e) => e.q67_rbl_B === 1);

        warnings[AeKey(ae)] = {
          q26Discontinuation: isQ26Selected && noEventsSelected,
          q38DoseChange: isQ38Selected && noEventsSelected,
        };
      });
    }

    return warnings;
  };

  // QAQC-040 (Q26/Q26.1 discontinuation reason) / QAQC-041 (Q38.1.A/Q38.2.A
  // dose-change reason) / QAQC-042 (Q43.2.A CAR-T "AE management" reason) -
  // generic validator shared by Q53/Q54/Q55/Q56/Q57/Q59/Q60/Q61/Q62/Q63/Q64.
  // Warns when the patient discontinued due to, had a dose change due to, or
  // had IV corticosteroids given to manage this AE type, but no row in the
  // matching AE table has been marked as having occurred.
  const validateAeQ26Warning = (aelist, selectionList, config) => {
    const warnings = {};
    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;

        const selection = selectionList?.find(
          (x) => x.cohortTreatmentId === cohortTreatmentId,
        );
        const isQ26Selected =
          !config.skipQ26 && (selection?.q26Selected ?? false);
        const isQ38Selected =
          !config.skipQ38 && (selection?.q38Selected ?? false);
        const isQ43Selected = selection?.q43Selected ?? false;

        const list = ae[config.listField] || [];
        const hasEvents = list.some(
          (e) =>
            e[config.radioField] !== null &&
            e[config.radioField] !== undefined &&
            e[config.radioField] !== 1,
        );

        warnings[AeKey(ae)] = {
          q26Discontinuation: isQ26Selected && !hasEvents,
          q38DoseChange: isQ38Selected && !hasEvents,
          q43ManagementUse: isQ43Selected && !hasEvents,
        };
      });
    }

    return warnings;
  };

  const validateAllAeQ26Warnings = (aelist, selectionLists) => {
    const result = {};
    Object.keys(AeQ26ReasonConfig).forEach((qNum) => {
      const config = AeQ26ReasonConfig[qNum];
      result[qNum] = validateAeQ26Warning(
        aelist,
        selectionLists?.[qNum],
        config,
      );
    });
    return result;
  };

  // QAQC-045 Rule B - for each cohort, returns the list of Q43.2.A
  // neurologic-event subtype codes that have NO matching row in Q63 with
  // that same subtype (rbl_D). Unlike the other AE-consistency rules, this
  // is evaluated per-subtype, not just "any event reported".
  const validateQ63SubtypeWarnings = (aelist, subtypeSelectionList) => {
    const warnings = {};
    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;
        const selection = subtypeSelectionList?.find(
          (x) => x.cohortTreatmentId === cohortTreatmentId,
        );
        const selectedSubtypeCodes = selection?.selectedSubtypeCodes || [];

        const list = ae.q63_neurotoxicity_list || [];
        const reportedSubtypeCodes = new Set(
          list
            .filter(
              (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
            )
            .map((r) => r.rbl_D),
        );

        warnings[AeKey(ae)] = selectedSubtypeCodes.filter(
          (code) => !reportedSubtypeCodes.has(code),
        );
      });
    }
    return warnings;
  };

  // QAQC-040/041/042 - live recompute for a single AE type/cohort, called
  // from that AE type's own onChange handler so the warning updates as the
  // user edits, instead of only refreshing on page load and Validate/Save
  // (matching how Q65/Q66/Q67 already recompute live).
  const RecomputeAeQ26WarningForRow = (
    qNum,
    cohortTreatmentId,
    rowList,
    groupIdx,
  ) => {
    const config = AeQ26ReasonConfig[qNum];
    if (!config) return;
    const warnKey =
      groupIdx != null ? AeKey(aeDetails[groupIdx]) : cohortTreatmentId;

    // QAQC-040/041/042 for Q53 (CRS) must not be shown live as the
    // abstractor edits the radios - only once Validate/Next is clicked (see
    // Validate()). Other AE types keep the existing live-recompute behavior.
    if (Number(qNum) === 53) {
      setAeQ26Warnings((prev) => ({
        ...prev,
        [qNum]: {
          ...prev[qNum],
          [warnKey]: {
            q26Discontinuation: false,
            q38DoseChange: false,
            q43ManagementUse: false,
          },
        },
      }));
      return;
    }

    const selectionList = aeQ26SelectionLists?.[qNum];
    const selection = selectionList?.find(
      (x) => x.cohortTreatmentId === cohortTreatmentId,
    );
    const isQ26Selected = !config.skipQ26 && (selection?.q26Selected ?? false);
    const isQ38Selected = !config.skipQ38 && (selection?.q38Selected ?? false);
    const isQ43Selected = selection?.q43Selected ?? false;

    const hasEvents = (rowList || []).some(
      (e) =>
        e[config.radioField] !== null &&
        e[config.radioField] !== undefined &&
        e[config.radioField] !== 1,
    );

    setAeQ26Warnings((prev) => ({
      ...prev,
      [qNum]: {
        ...prev[qNum],
        [warnKey]: {
          q26Discontinuation: isQ26Selected && !hasEvents,
          q38DoseChange: isQ38Selected && !hasEvents,
          q43ManagementUse: isQ43Selected && !hasEvents,
        },
      },
    }));
  };

  // QAQC-045 Rule B - live recompute for a single cohort's Q63 table, called
  // from Q63's rbl_B/rbl_D onChange handlers so the per-subtype warning list
  // updates as the user edits.
  const RecomputeQ63SubtypeWarningForCohort = (
    cohortTreatmentId,
    rowList,
    groupIdx,
  ) => {
    const selection = Q63NeurotoxicitySubtypeSelectionList?.find(
      (x) => x.cohortTreatmentId === cohortTreatmentId,
    );
    const selectedSubtypeCodes = selection?.selectedSubtypeCodes || [];
    const warnKey =
      groupIdx != null ? AeKey(aeDetails[groupIdx]) : cohortTreatmentId;

    const reportedSubtypeCodes = new Set(
      (rowList || [])
        .filter(
          (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
        )
        .map((r) => r.rbl_D),
    );

    const missingSubtypeCodes = selectedSubtypeCodes.filter(
      (code) => !reportedSubtypeCodes.has(code),
    );

    setQ63SubtypeWarnings((prev) => ({
      ...prev,
      [warnKey]: missingSubtypeCodes,
    }));
  };

  // QAQC-045 Rule C - live recompute for a single cohort's Q63 table, called
  // from Q63's rbl_B/rbl_D onChange handlers so the per-subtype warning list
  // (matched against Q26/Q26.1's discontinuation-reason subtype, rather than
  // Q43.2.A's) updates as the user edits.
  const RecomputeQ63SubtypeWarningForCohortFromQ26 = (
    cohortTreatmentId,
    rowList,
    groupIdx,
  ) => {
    const selection = Q63NeurotoxicitySubtypeSelectionListFromQ26?.find(
      (x) => x.cohortTreatmentId === cohortTreatmentId,
    );
    const selectedSubtypeCodes = selection?.selectedSubtypeCodes || [];
    const warnKey =
      groupIdx != null ? AeKey(aeDetails[groupIdx]) : cohortTreatmentId;

    const reportedSubtypeCodes = new Set(
      (rowList || [])
        .filter(
          (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
        )
        .map((r) => r.rbl_D),
    );

    const missingSubtypeCodes = selectedSubtypeCodes.filter(
      (code) => !reportedSubtypeCodes.has(code),
    );

    setQ63SubtypeWarningsFromQ26((prev) => ({
      ...prev,
      [warnKey]: missingSubtypeCodes,
    }));
  };

  // QAQC-045 Rule D - live recompute for a single cohort's Q63 table, called
  // from Q63's rbl_B/rbl_D onChange handlers so the per-subtype warning list
  // (matched against Q38.1.A/Q38.2.A's dose-change-reason subtype, rather
  // than Q43.2.A's or Q26/Q26.1's) updates as the user edits.
  const RecomputeQ63SubtypeWarningForCohortFromQ38 = (
    cohortTreatmentId,
    rowList,
    groupIdx,
  ) => {
    const selection = Q63NeurotoxicitySubtypeSelectionListFromQ38?.find(
      (x) => x.cohortTreatmentId === cohortTreatmentId,
    );
    const selectedSubtypeCodes = selection?.selectedSubtypeCodes || [];
    const warnKey =
      groupIdx != null ? AeKey(aeDetails[groupIdx]) : cohortTreatmentId;

    const reportedSubtypeCodes = new Set(
      (rowList || [])
        .filter(
          (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
        )
        .map((r) => r.rbl_D),
    );

    const missingSubtypeCodes = selectedSubtypeCodes.filter(
      (code) => !reportedSubtypeCodes.has(code),
    );

    setQ63SubtypeWarningsFromQ38((prev) => ({
      ...prev,
      [warnKey]: missingSubtypeCodes,
    }));
  };

  const validateQ571BWarnings = (aelist) => {
    const warnings = {};

    if (aelist?.length > 0) {
      aelist.forEach((ae) => {
        const cohortTreatmentId = ae.cohortTreatmentId;
        const q571BList = ae.q571B_immunoglobin_list ?? [];
        const q57List = ae.q57_infection_list ?? [];

        let rowWarning = false;

        q571BList.forEach((item) => {
          const assessmentDate = item.q571B_date_B
            ? new Date(item.q571B_date_B)
            : null;

          const infectionEpisode = q57List?.find(
            (ep) => ep.infectionKey === item.q571B_episode_D,
          );

          const onsetDate = infectionEpisode?.date_B
            ? new Date(infectionEpisode.date_B)
            : null;

          if (assessmentDate && onsetDate && assessmentDate < onsetDate) {
            rowWarning = true;
          }
        });

        warnings[AeKey(ae)] = {
          q571BDateWarning: rowWarning,
        };
      });
    }

    return warnings;
  };

  function computeAEWarnings(responseData, ae) {
    const cohortId = ae.cohortTreatmentId;

    const isTEC = cohortId === RRMMCohortTreatment.TECVAYLI;
    const isTAL = cohortId === RRMMCohortTreatment.TALVEY;
    const isELR = cohortId === RRMMCohortTreatment.ELREXFIO;
    const isLIN = cohortId === RRMMCohortTreatment.Linvoseltamab;
    const isCAR = cohortId === RRMMCohortTreatment.CARVYKTI;
    const isABE = cohortId === RRMMCohortTreatment.ABECMA;

    const Q53 = ae.q53_crs_list;
    const Q54 = ae.q54_icans_list;
    const Q55 = ae.q55_neutropenia_list;
    const Q56 = ae.q56_cytopenia_list;
    const Q57 = ae.q57_infection_list;
    const Q59 = ae.q59_dysgeusia_list;
    const Q60 = ae.q60_nail_list;
    const Q61 = ae.q61_skin_list;
    const Q62 = ae.q62_spm_list;
    const Q63 = ae.q63_neurotoxicity_list;
    const Q64 = ae.q64_enterocolitis_list;

    const newQ53 = Q53.map((row, i) => {
      const val53 = row?.rbl_B ?? null; // 1/2/99/null

      let warn = false;

      // ------------ TECVAYLI ------------
      if (isTEC) {
        warn =
          responseData.q33AdverseSelectedTec &&
          Q53[i]?.rbl_B == 1 &&
          Q54[i]?.rbl_B == 1 &&
          Q55[i]?.rbl_B == 1 &&
          Q56[i]?.rbl_B == 1 &&
          Q57[i]?.rbl_B == 1;
      }

      // ------------ TALVEY ------------
      if (isTAL) {
        warn =
          responseData.q33AdverseSelectedTal &&
          Q53[i]?.rbl_B == 1 &&
          Q54[i]?.rbl_B == 1 &&
          Q57[i]?.rbl_B == 1 &&
          Q59[i]?.rbl_B == 1 &&
          Q60[i]?.rbl_B == 1 &&
          Q61[i]?.rbl_B == 1;
      }

      // ------------ ELREXFIO ---------
      if (isELR) {
        warn =
          responseData.q33AdverseSelectedElr &&
          Q53[i]?.rbl_B == 1 &&
          Q54[i]?.rbl_B == 1 &&
          Q55[i]?.rbl_B == 1 &&
          Q56[i]?.rbl_B == 1 &&
          Q57[i]?.rbl_B == 1;
      }

      // ------------ LINVAL ---------
      if (isLIN) {
        warn =
          responseData.q33AdverseSelectedLin &&
          Q53[i]?.rbl_B == 1 &&
          Q54[i]?.rbl_B == 1 &&
          Q55[i]?.rbl_B == 1 &&
          Q56[i]?.rbl_B == 1 &&
          Q57[i]?.rbl_B == 1;
      }

      // ------------ CARVYKTI / ABECMA ----------
      if (isCAR || isABE) {
        warn =
          (responseData.q33AdverseSelectedCarvykti ||
            responseData.q33AdverseSelectedAbecma) &&
          Q53[i]?.rbl_B == 1 &&
          Q54[i]?.rbl_B == 1 &&
          Q55[i]?.rbl_B == 1 &&
          Q56[i]?.rbl_B == 1 &&
          Q62[i]?.rbl_B == 1 &&
          Q63[i]?.rbl_B == 1 &&
          (isNewEcrfA ? Q64[i]?.q64_rbl_B === 1 : true);
      }

      return {
        ...row,
        ae_ManagementAeWarningTec: warn,
      };
    });

    return {
      ...ae,
      q53_crs_list: newQ53,
    };
  }

  const SavePage = async (validate) => {
    try {
      ToggleLoader(true);
      let isValid = validate ? Validate() : true;

      if (isValid) {
        setShowTopErrMsg(false);

        let payload = {
          AdverseEventsData: [],
          PatientId: pid,
          UserId: GetLoggedInUserID(),
          IsValidated: validate,
          IsAdmin: IsAdmin(),
        };
        if (aeDetails && aeDetails.length > 0) {
          payload.AdverseEventsData = aeDetails.map((a) => {
            let AE_CRSList = [];
            let AE_IcansList = [];
            let AE_NeutropeniumList = [];
            let AE_CytopeniumList = [];
            let AE_InfectionList = [];
            let AE_DysgeusiumList = [];
            let AE_NaildisorderList = [];
            let AE_SkinDisorderList = [];
            let AE_SecondaryPrimaryMalignancyList = [];
            let AE_NeurotoxicityList = [];
            let AE_EnterocolitisList = [];
            let AE_LymphopeniaList = [];
            let AE_DizzinessList = [];
            let AE_AtaxiaList = [];
            let AE_ImmunoglobinList = [];
            let AE_WeightTrajectoryList = [];

            //ADD CONDITIONALS ALSO incase Q is not shown for certain cohorts not reuired
            if (a.q53_crs_list && a.q53_crs_list.length > 0) {
              AE_CRSList = a.q53_crs_list.map((b) => ({
                AE_CRSId: b.crsId || 0,
                CrsNotExperienced: b.rbl_B === 1,
                CsrDate: b.date_B != "Invalid Date" ? b.date_B : "",
                CsrDateUnk: b.rbl_B === 99,
                Severity: b.rbl_C,
                Crsresolution: b.rbl_resolution,
                Crsresolutiondate: b.date_resolution,
              }));
            }
            if (a.q54_icans_list && a.q54_icans_list.length > 0) {
              AE_IcansList = a.q54_icans_list.map((b) => ({
                AE_ICANSId: b.icansId || 0,
                IcansNotExperienced: b.rbl_B === 1,
                IcansOccuredDate: b.date_B != "Invalid Date" ? b.date_B : "",
                IcansOccuredDateUnk: b.rbl_B === 99,

                Severity: b.rblC,
                Icansresolution: b.rblresolution,
                Icansresolutiondate:
                  b.dateresolution !== "Invalid Date" ? b.dateresolution : "",
              }));
            }

            if (a.q55_neutropenia_list && a.q55_neutropenia_list.length > 0) {
              AE_NeutropeniumList = a.q55_neutropenia_list.map((b) => ({
                AE_NeutropeniaId: b.neutropeniaId || 0,
                NeutropeniaNotExperienced: b.rbl_B === 1,
                NeutropeniaOnsetDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                NeutropeniaOnsetDateUnk: b.rbl_B === 99,
                // Date of resolution [C] retired
                // NeutropeniaNotResolved: b.rbl_C === 1,
                // NeutropeniaResolutionDate:
                //   b.date_C != "Invalid Date" ? b.date_C : "",
                // NeutropeniaResolutionDateUnk: b.rbl_C === 3,
                // NeutropeniaResolvedUnk: b.rbl_C === 99,
                Severity: b.rblD,
              }));
            }
            if (a.q56_cytopenia_list && a.q56_cytopenia_list.length > 0) {
              AE_CytopeniumList = a.q56_cytopenia_list.map((b) => ({
                AE_CytopeniaId: b.cytopeniaId || 0,
                CytopeniaNotExperienced: b.rbl_B === 1,
                CytopeniaOnsetDate: b.date_B != "Invalid Date" ? b.date_B : "",
                CytopeniaOnsetDateUnk: b.rbl_B === 99,
                // Date of resolution [C] retired
                // CytopeniaNotResolved: b.rbl_C === 1,
                // CytopeniaResolutionDate:
                //   b.date_C != "Invalid Date" ? b.date_C : "",
                // CytopeniaResolutionDateUnk: b.rbl_C === 3,
                // CytopeniaResolvedUnk: b.rbl_C === 99,
                Severity: b.rblD,
              }));
            }
            if (a.q57_infection_list && a.q57_infection_list.length > 0) {
              AE_InfectionList = a.q57_infection_list.map((b) => ({
                AE_InfectionId: b.infectionId || 0,
                InfectionKey: b.infectionKey,

                InfectionNotExperienced: b.rbl_B === 1,
                InfectionOccuredDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                InfectionOccuredDateUnk: b.rbl_B === 99,

                InfectionTypeBacterial: b.chk_C_1,
                InfectionTypeViral: b.chk_C_2,
                InfectionTypeFungal: b.chk_C_3,
                InfectionTypeUnk: b.rb_C,

                // Infection organism [D] retired
                // InfectionOrganismAdenovirus: b.chk_D_1,
                // InfectionOrganismCandida: b.chk_D_2,
                // InfectionOrganismCovid19: b.chk_D_3,
                // InfectionOrganismCytomegalovirus: b.chk_D_4,
                // InfectionOrganismEscherichia: b.chk_D_5,
                // InfectionOrganismHelicobacter: b.chk_D_6,
                // InfectionOrganismHepatitis: b.chk_D_7,
                // InfectionOrganismHerpes: b.chk_D_8,
                // InfectionOrganismInfluenza: b.chk_D_9,
                // InfectionOrganismPjp: b.chk_D_10,
                // InfectionOrganismProgressive: b.chk_D_11,
                // InfectionOrganismRsv: b.chk_D_12,
                // InfectionOrganismStreptococcus: b.chk_D_13,
                // InfectionOrganismVaricella: b.chk_D_14,
                // InfectionOrganismOth: b.chk_D_15,
                // InfectionOrganismOthTxt: b.txt_D_oth,
                // InfectionOrganismUnk: b.rb_D,

                Severity: b.rbl_E,

                LedErVisit: b.rbl_F,

                ManagementSettingInpatient: b.chk_G_1,
                ManagementSettingOutpatient: b.chk_G_2,
                ManagementSettingEmergencyroom: b.chk_G_3,
                ManagementSettingIntensivecareunit: b.chk_G_4,
                ManagementSettingOth: b.chk_G_5,
                ManagementSettingOthTxt: b.txt_G_oth,
                ManagementSettingUnk: b.rb_G_unk,

                ResolutionDateEventC:
                  b.rbl_resolution === 4 ? 99 : b.rbl_resolution,
                ResolvedDateC:
                  b.date_resolution != "Invalid Date" ? b.date_resolution : "",

                IggAssessedC: b.rbl_H,
                IggAssessedDateC: b.date_H != "Invalid Date" ? b.date_H : "",

                IggLevelC: b.rbl_I,
                IggLevelUnitC:
                  b.unit_I !== "" && b.unit_I != null
                    ? parseInt(b.unit_I, 10)
                    : null,
              }));
            }

            if (a.q571B_immunoglobin_list?.length > 0 && isNewEcrfB) {
              AE_ImmunoglobinList = a.q571B_immunoglobin_list.map((b) => ({
                Adverseeventimmunoglobinid: b.adverseeventimmunoglobinid || 0,
                Patientid: b.patientId,
                CohortTreatmentId: b.cohortTreatmentId,

                //B
                ImmunoglobinNotAssessed: b.q571B_rbl_B,
                ImmunoglobinAssessmentDate:
                  b.q571B_date_B !== "Invalid Date" ? b.q571B_date_B : "",

                //C
                ImmunoglobinDosage: b.q571B_rbl_C,
                ImmunoglobinDosageUnit: b.q571B_unit_C,

                //D
                InfectionKey: b.q571B_episode_D,
              }));
            }
            if (a.q58_weight_list?.length > 0) {
              AE_WeightTrajectoryList = a.q58_weight_list
                .filter((b) => b.rbl_C !== null)
                .map((b) => ({
                  AE_WeightTrajectoryId: b.aE_WeightTrajectoryId || 0,
                  AssessmentTargetdate: b.targetDate_B,
                  WeightAssessment: b.rbl_C,
                  WeightAssessmentDate: b.rbl_C === 2 ? b.date_C : "",
                  // Column D (weight) is answerable whenever C != "not assessed"
                  // (rbl_C === 1) - i.e. for both "date entered" (2) and "date
                  // unknown" (99), matching the Not applicable/radios toggle in
                  // the JSX at ~line 13857.
                  WeightUnit: b.rbl_C !== 1 ? b.rbl_D : null,
                  WeightUnitValue:
                    b.rbl_C !== 1 &&
                    b.rbl_D === 1 &&
                    !isStringEmpty(b.weightKg_D)
                      ? Number(b.weightKg_D)
                      : b.rbl_C !== 1 &&
                          b.rbl_D === 2 &&
                          !isStringEmpty(b.weightLbs_D)
                        ? Number(b.weightLbs_D)
                        : null,
                }));
            }
            if (a.q59_dysgeusia_list && a.q59_dysgeusia_list.length > 0) {
              AE_DysgeusiumList = a.q59_dysgeusia_list.map((b) => ({
                AE_DysgeusiaId: b.dysgeusiaId || 0,
                DysgeusiaNotexperienced: b.rbl_B === 1,
                DysgeusiaOnsetOcurredDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                DysgeusiaOnsetDateUnk: b.rbl_B === 99,
                InterventionDexamethasoneMouthwash: b.chk_CC_1,
                DexamethasoneMouthwashProphylactic: b.chk_CC_1_P,
                DexamethasoneMouthwashTherapeutic: b.chk_CC_1_T,
                InterventionNystatinMouthwash: b.chk_CC_2,
                NystatinMouthwashProphylactic: b.chk_CC_2_P,
                NystatinMouthwashTherapeutic: b.chk_CC_2_T,
                InterventionSalineMouthwash: b.chk_CC_3,
                SalineMouthwashProphylactic: b.chk_CC_3_P,
                SalineMouthwashTherapeutic: b.chk_CC_3_T,
                InterventionBioteneMouthwash: b.chk_CC_4,
                BioteneMouthwashProphylactic: b.chk_CC_4_P,
                BioteneMouthwashTherapeutic: b.chk_CC_4_T,
                InterventionZincMouthwash: b.chk_CC_5,
                ZincMouthwashProphylactic: b.chk_CC_5_P,
                ZincMouthwashTherapeutic: b.chk_CC_5_T,
                InterventionVitaminbMouthwash: b.chk_CC_6,
                VitaminbMouthwashProphylactic: b.chk_CC_6_P,
                VitaminbMouthwashTherapeutic: b.chk_CC_6_T,
                InterventionOtherMouthwash: b.chk_CC_7,
                InterventionOthertextMouthwash: b.txt_CC_oth,
                OtherMouthwashProphylactic: b.chk_CC_7_P,
                OtherMouthwashTherapeutic: b.chk_CC_7_T,
                InterventionUnknown: b.rb_CC,
                NoImprovement: b.rbl_C === 1,
                ImprovementDate: b.date_C != "Invalid Date" ? b.date_C : "",
                ImprovementDateUnk: b.rbl_C === 3,
                ImprovementUnk: b.rbl_C === 99,
                DysgeusiaNotResolved: b.rbl_D === 1,
                ResolutionOccuredDate:
                  b.date_D != "Invalid Date" ? b.date_D : "",
                ResolutionOccuredDateUnk: b.rbl_D === 3,
                DysgeusiaResolvedUnk: b.rbl_D === 99,
              }));
            }
            if (a.q60_nail_list && a.q60_nail_list.length > 0) {
              AE_NaildisorderList = a.q60_nail_list.map((b) => ({
                AE_NailDisorderId: b.nailDisorderId || 0,
                NaildisorderNotexperienced: b.rbl_B === 1,
                NaildisorderOnsetOcurredDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                NaildisorderOnsetDateUnk: b.rbl_B === 99,
                NoImprovement: b.rbl_C === 1,
                ImprovementDate: b.date_C != "Invalid Date" ? b.date_C : "",
                ImprovementDateUnk: b.rbl_C === 3,
                ImprovementUnk: b.rbl_C === 99,
                NaildisorderNotResolved: b.rbl_D === 1,
                NaildisorderOccuredDate:
                  b.date_D != "Invalid Date" ? b.date_D : "",
                NaildisorderOccuredDateUnk: b.rbl_D === 3,
                NaildisorderResolvedUnk: b.rbl_D === 99,
              }));
            }
            if (a.q61_skin_list && a.q61_skin_list.length > 0) {
              AE_SkinDisorderList = a.q61_skin_list.map((b) => ({
                AE_SkinDisorderId: b.skinDisorderId || 0,
                SkindisorderNotexperienced: b.rbl_B === 1,
                SkindisorderOnsetOcurredDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                SkindisorderOnsetDateUnk: b.rbl_B === 99,
                NoImprovement: b.rbl_C === 1,
                ImprovementDate: b.date_C != "Invalid Date" ? b.date_C : "",
                ImprovementDateUnk: b.rbl_C === 3,
                ImprovementUnk: b.rbl_C === 99,
                SkindisorderNotResolved: b.rbl_D === 1,
                SkindisorderOccuredDate:
                  b.date_D != "Invalid Date" ? b.date_D : "",
                SkindisorderOccuredDateUnk: b.rbl_D === 3,
                SkindisorderResolvedUnk: b.rbl_D === 99,
              }));
            }

            if (a.q62_spm_list && a.q62_spm_list.length > 0) {
              AE_SecondaryPrimaryMalignancyList = a.q62_spm_list.map((b) => ({
                AE_SecondaryPrimaryMalignancyId: b.spmId || 0,
                SecondMalignancyNotReceived: b.rbl_B === 1,
                SecondMalignancyOccuredDate:
                  b.date_B !== "Invalid Date" ? b.date_B : "",
                SecondMalignancyOccuredDateUnk: b.rbl_B === 99,
                SecondMalignancyResolution: b.q62_rbl_C,
                SecondMalignancyResolutionDate:
                  b.q62_date_C !== "Invalid Date" ? b.q62_date_C : "",
                Severity: b.q62_severity,
              }));
            }

            if (
              a.q63_neurotoxicity_list &&
              a.q63_neurotoxicity_list.length > 0
            ) {
              AE_NeurotoxicityList = a.q63_neurotoxicity_list.map((b) => ({
                AE_NeurotoxicityId: b.neurotoxicityId || 0,
                NeurotoxicityNotExperienced: b.rbl_B === 1,
                NeurotoxicityOnsetOccuredDate:
                  b.date_B != "Invalid Date" ? b.date_B : "",
                NeurotoxicityOnsetOccuredDateUnk: b.rbl_B === 99,
                NeurotoxicityNotResolved: b.rbl_C === 1,
                ResolutionOccuredDate:
                  b.date_C != "Invalid Date" ? b.date_C : "",
                ResolutionOccuredDateUnk: b.rbl_C === 3,
                NeurotoxicityResolvedUnk: b.rbl_C === 99,
                NeurotoxicitySubtype: b.rbl_D,
                NeurotoxicitySubtypeOth: b.txt_D_oth,
                Severity: b.rbl_E,
                ManagementSettingOutpatient: b.chk_F_1,
                ManagementSettingHospital: b.chk_F_2,
                ManagementSettingUnk: b.rb_F,
                InterventionsCorticosteroids: b.chk_G_1,
                InterventionsAnakinra: b.chk_G_2,
                InterventionsTocilizumab: b.chk_G_3,
                InterventionsSiltuximab: b.chk_G_4,
                InterventionsOth: b.chk_G_5,
                InterventionsUnk: b.rb_G,

                ManagementSettingInpatient: b.chkFInpatient,
                ManagementSettingEmergencyroom: b.chkFEmergencyRoom,
                ManagementSettingIntensivecareunit: b.chkFIntensiveCareUnit,
                ManagementSettingUrgentcarevisit: b.chkFUrgentCareVisit,
                ManagementSettingOth: b.chkFOther,

                Improvement: b.rblH,
                Improvementdate: b.dateH !== "Invalid Date" ? b.dateH : "",

                IsDependent: b.isAutoPopulated || false,
                SourceQ54IcansId: b.sourceQ54IcansId,
              }));
            }

            if (a.q64_enterocolitis_list?.length > 0 && isNewEcrfA) {
              AE_EnterocolitisList = a.q64_enterocolitis_list.map((b) => ({
                Adverseevententerocolitisid: b.adverseevententerocolitisid || 0,
                PatientId: b.patientId,
                CohortTreatmentId: b.cohortTreatmentId,

                //B
                EnterocolitisOnsetEventOccurred: b.q64_rbl_B,
                EnterocolitisEventOccurredDate:
                  b.q64_date_B !== "Invalid Date" ? b.q64_date_B : "",

                //C
                EnterocolitisImprovement: b.q64_rbl_C,
                EnterocolitisImprovementDate:
                  b.q64_date_C !== "Invalid Date" ? b.q64_date_C : "",

                //D
                EnterocolitisEventResolution: b.q64_rbl_D,
                ResolutionEventResolutionDate:
                  b.q64_date_D !== "Invalid Date" ? b.q64_date_D : "",

                //E
                EnterocolitisInterventionsSupportiveCare: b.q64_chk_E_1,
                EnterocolitisInterventionsAntibiotics: b.q64_chk_E_2,
                EnterocolitisInterventionsSurgery: b.q64_chk_E_3,
                EnterocolitisInterventionsGcsf: b.q64_chk_E_4,
                EnterocolitisInterventionsOth: b.q64_chk_E_5,
                EnterocolitisInterventionsOthTxt: b.q64_chk_E_othTxt,
                EnterocolitisInterventionsUnk: b.q64_E_unk ?? false,

                //F
                Severity: b.q64_severity,
              }));
            }

            if (a.q65_lymphopenia_list?.length > 0 && isNewEcrfB) {
              AE_LymphopeniaList = a.q65_lymphopenia_list.map((b) => ({
                Adverseeventlymphopeniaid: b.adverseeventlymphopeniaid || 0,
                PatientId: b.patientId,
                CohortTreatmentId: b.cohortTreatmentId,

                //B
                LymphopeniaOnsetEventOccurred: b.q65_rbl_B,
                LymphopeniaEventOccurredDate:
                  b.q65_date_B !== "Invalid Date" ? b.q65_date_B : "",

                // Date of resolution [C] retired
                //C
                // LymphopeniaEventResolution: b.q65_rbl_C,
                // LymphopeniaResolutionDate:
                //   b.q65_date_C !== "Invalid Date" ? b.q65_date_C : "",

                //D
                Severity: b.q65_severity,
              }));
            }

            if (a.q66_dizziness_list?.length > 0 && isNewEcrfC) {
              AE_DizzinessList = a.q66_dizziness_list.map((b) => ({
                Adverseeventdizzinessid: b.adverseeventdizzinessid || 0,
                PatientId: b.patientId,
                CohortTreatmentId: b.cohortTreatmentId,

                //B
                DizzinessOnsetEventOccurred: b.q66_rbl_B,
                DizzinessEventOccurredDate:
                  b.q66_date_B !== "Invalid Date" ? b.q66_date_B : "",

                //C
                DizzinessEventResolution: b.q66_rbl_C,
                DizzinessResolutionDate:
                  b.q66_date_C !== "Invalid Date" ? b.q66_date_C : "",
              }));
            }

            if (a.q67_ataxia_list?.length > 0 && isNewEcrfC) {
              AE_AtaxiaList = a.q67_ataxia_list.map((b) => ({
                Adverseeventataxiaid: b.adverseeventataxiaid || 0,
                PatientId: b.patientId,
                CohortTreatmentId: b.cohortTreatmentId,

                //B
                AtaxiaOnsetEventOccurred: b.q67_rbl_B,
                AtaxiaEventOccurredDate:
                  b.q67_date_B !== "Invalid Date" ? b.q67_date_B : "",

                //C
                AtaxiaEventResolution: b.q67_rbl_C,
                AtaxiaResolutionDate:
                  b.q67_date_C !== "Invalid Date" ? b.q67_date_C : "",

                //D
                Severity: b.q67_severity,

                //E
                AtaxiaPresentationDizziness: b.q67_chk_E_1,
                AtaxiaPresentationGait: b.q67_chk_E_2,
                AtaxiaPresentationFalls: b.q67_chk_E_3,
                AtaxiaPresentationCerebellar: b.q67_chk_E_4,
                AtaxiaPresentationDysarthria: b.q67_chk_E_5,
                AtaxiaPresentationEye: b.q67_chk_E_6,
                AtaxiaPresentationParesthesia: b.q67_chk_E_7,
                AtaxiaPresentationOth: b.q67_chk_E_8,
                AtaxiaPresentationOthTxt: b.q67_chk_E_othTxt,
                AtaxiaPresentationUnk: b.q67_E_unk ?? false,

                //F
                AtaxiaInterventionDiscontinuation: b.q67_chk_F_1,
                AtaxiaInterventionDelay: b.q67_chk_F_2,
                AtaxiaInterventionDose: b.q67_chk_F_3,
                AtaxiaInterventionSupportive: b.q67_chk_F_4,
                AtaxiaInterventionFall: b.q67_chk_F_5,
                AtaxiaInterventionNeurological: b.q67_chk_F_6,
                AtaxiaInterventionOth: b.q67_chk_F_7,
                AtaxiaInterventionOthTxt: b.q67_chk_F_othTxt,
                AtaxiaInterventionUnk: b.q67_F_unk ?? false,
              }));
            }

            return {
              CohortTreatmentId: a.cohortTreatmentId,
              Lotnumber: a.lineOfTherapy > 0 ? a.lineOfTherapy : null,
              AE_CRSList,
              AE_IcansList,
              AE_NeutropeniumList,
              AE_CytopeniumList,
              AE_InfectionList,
              AE_DysgeusiumList,
              AE_NaildisorderList,
              AE_SkinDisorderList,
              AE_SecondaryPrimaryMalignancyList,
              AE_NeurotoxicityList,
              AE_EnterocolitisList,
              AE_LymphopeniaList,
              AE_DizzinessList,
              AE_AtaxiaList,
              AE_ImmunoglobinList,
              AE_WeightTrajectoryList,
            };
          });
        }
        if (pid > 0) {
          const response =
            await AdverseEventsService.SaveAdverseEventsData(payload);
          ToggleLoader(false);
          if (response.status !== HTTPResponse.OK) {
            if (response?.status === HTTPResponse.Unauthorized) {
              HandleSessionTimeout();
            } else throw response.error;
          } else {
            ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
            await LoadData();
          }
        }
        return true;
      } else {
        ToggleLoader(false);
        setShowTopErrMsg(true);
        return false;
      }
    } catch (error) {
      ToggleLoader(false);
      ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
    }
  };
  const Validate = () => {
    let isValid = true;

    //Check if onset date is before index date
    function IsOnsetBeforeIndex(onsetDt, indexDt) {
      if (!isStringEmpty(onsetDt) && !isStringEmpty(indexDt)) {
        return IsDateAfter(indexDt, onsetDt);
      }
      return false;
    }
    //Check if onset date is after AE end date
    function IsDateAfterFunction(onsetDt, aeEndDt) {
      if (!isStringEmpty(onsetDt) && !isStringEmpty(aeEndDt)) {
        return IsDateAfter(onsetDt, aeEndDt);
      }
      return false;
    }

    if (aeDetails && aeDetails.length > 0) {
      const updatedArr = aeDetails.map((p) => {
        let innerArrQ53 = null;
        let innerArrQ54 = null;
        let innerArrQ55 = null;
        let innerArrQ56 = null;
        let innerArrQ57 = null;
        let innerArrQ59 = null;
        let innerArrQ60 = null;
        let innerArrQ61 = null;
        let innerArrQ62 = null;
        let innerArrQ63 = null;
        let innerArrQ64 = null;
        let innerArrQ65 = null;
        let innerArrQ66 = null;
        let innerArrQ67 = null;
        let innerArrQ571B = null;
        let innerArrQ58 = null;

        if (p.q53_crs_list && p.q53_crs_list.length > 0) {
          innerArrQ53 = p.q53_crs_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_before_eof = false;

            // RESOLUTION VALIDATION
            let resolutionemptyerr = false;
            let resolutiondateemptyerr = false;
            let resolutiondateinvalid = false;
            let resolutiondaterangeerr = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_before_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }

            if (cIndex > 0 && c.date_B) {
              // Filter out the current index and get only the previous rows that have a date
              const previousDates = p.q53_crs_list
                .slice(0, cIndex) // Exclude the current index by slicing up to it
                .filter(
                  (_, idx) => idx !== cIndex && p.q53_crs_list[idx].date_B,
                ) // Ensure that previous indices have a date
                .map((crs) => crs.date_B); // Map to get the dates

              // // Get the most recent previous date if any
              // if (previousDates.length > 0) {
              //   const mostRecentPrevDate = previousDates[previousDates.length - 1];

              //   // Check if the current date is less than or equal to the most recent previous date
              //   if (IsDateEqual(mostRecentPrevDate, c.date_B)) {
              //     B_date_equal_prev_ep_err = true;
              //   }
              // }
              // Check if the current date is equal to any of the previous dates
              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            const C_empty_err =
              c.rbl_B != null && c.rbl_B !== 1 && c.rbl_C == null;
            if (isNewEcrfA && c.rbl_B !== null && c.rbl_B !== 1) {
              // Check if radio button is selected
              resolutionemptyerr = c.rbl_resolution === null;

              if (c.rbl_resolution != null && c.rbl_resolution === 2) {
                // Check if date is selected when "Event resolved on" is chosen
                if (
                  isStringEmpty(c.date_resolution) ||
                  c.date_resolution === null
                ) {
                  resolutiondateemptyerr = true;
                } else if (!IsDateValid(c.date_resolution)) {
                  resolutiondateinvalid = true;
                } else {
                  // Validate: index date ≤ onset date ≤ resolution date ≤ AE end date
                  const indexDate = p.cohortIndexDate;
                  const onsetDate = c.date_B;
                  const resolutionDate = c.date_resolution;
                  const aeEndDate = p.aeEndDate;

                  const isBeforeOnset = IsOnsetBeforeIndex(
                    resolutionDate,
                    onsetDate,
                  );
                  const isBeforeIndex = IsOnsetBeforeIndex(
                    resolutionDate,
                    indexDate,
                  );

                  const isAfterAeEnd = IsDateAfterFunction(
                    resolutionDate,
                    aeEndDate,
                  );

                  const isAfterEof = IsDateAfter(
                    resolutionDate,
                    endOfFollowUpDate,
                  );

                  // Single error flag for date range validation
                  resolutiondaterangeerr =
                    isBeforeIndex ||
                    isBeforeOnset ||
                    isAfterAeEnd ||
                    isAfterEof;
                }
              }
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              C_empty_err ||
              B_date_before_eof ||
              resolutionemptyerr ||
              resolutiondateemptyerr ||
              resolutiondateinvalid ||
              resolutiondaterangeerr
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              C_empty_err,
              B_date_before_eof,
              resolutionemptyerr,
              resolutiondateemptyerr,
              resolutiondateinvalid,
              resolutiondaterangeerr,
            };
          });

          // QAQC-043 / QAQC-044 - table-level check across all rows,
          // recomputed on every Validate() pass so it can't go stale.
          const crsSelection = Q36CrsSelectionList?.find(
            (x) => x.cohortTreatmentId === p.cohortTreatmentId,
          );
          const isQ36CrsSelected = crsSelection?.selected ?? false;
          const crsTreatmentSelection = Q53CrsTreatmentSelectionList?.find(
            (x) => x.cohortTreatmentId === p.cohortTreatmentId,
          );
          const isCrsTreatmentSelected =
            crsTreatmentSelection?.selected ?? false;
          const hasCrsEvent = innerArrQ53.some(
            (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
          );
          const q53q36Warning = isQ36CrsSelected && !hasCrsEvent;
          const q53q39Warning = isCrsTreatmentSelected && !hasCrsEvent;
          innerArrQ53 = innerArrQ53.map((r) => ({
            ...r,
            Q53q36WarningMsg: q53q36Warning,
            Q53q39WarningMsg: q53q39Warning,
          }));

          // QAQC-043 / QAQC-044 - the eCRF specifies "do not permit validation
          // of this question", so an unresolved warning blocks the page as well
          // as being displayed.
          if (q53q36Warning || q53q39Warning) {
            isValid = false;
          }
        }
        if (p.q54_icans_list && p.q54_icans_list.length > 0) {
          innerArrQ54 = p.q54_icans_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_before_eof = false;
            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_before_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q54_icans_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) => idx !== cIndex && p.q54_icans_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            // SEVERITY VALIDATION
            const Cemptyerr =
              isNewEcrfA &&
              c.rbl_B !== null &&
              c.rbl_B !== 1 &&
              c.rblC === null;

            //  RESOLUTION VALIDATION (ONLY FOR isNewEcrfA)
            let resolutionemptyerr = false;
            let resolutiondateemptyerr = false;
            let resolutiondateinvalid = false;
            let resolutiondaterangeerr = false;

            if (isNewEcrfA && c.rbl_B !== null && c.rbl_B !== 1) {
              resolutionemptyerr = c.rblresolution === null;

              if (!resolutionemptyerr && c.rblresolution === 2) {
                if (
                  isStringEmpty(c.dateresolution) ||
                  c.dateresolution === null
                ) {
                  resolutiondateemptyerr = true;
                } else if (!IsDateValid(c.dateresolution)) {
                  resolutiondateinvalid = true;
                } else {
                  const indexDate = p.cohortIndexDate;
                  const onsetDate = c.date_B;
                  const resolutionDate = c.dateresolution;
                  const aeEndDate = p.aeEndDate;

                  const isBeforeOnset = IsOnsetBeforeIndex(
                    resolutionDate,
                    onsetDate,
                  );
                  const isBeforeIndex = IsOnsetBeforeIndex(
                    resolutionDate,
                    indexDate,
                  );
                  const isAfterAeEnd = IsDateAfterFunction(
                    resolutionDate,
                    aeEndDate,
                  );
                  const isAfterEof = IsDateAfter(
                    resolutionDate,
                    endOfFollowUpDate,
                  );

                  resolutiondaterangeerr =
                    isBeforeIndex ||
                    isBeforeOnset ||
                    isAfterAeEnd ||
                    isAfterEof;
                }
              }
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_before_eof ||
              resolutionemptyerr ||
              resolutiondateemptyerr ||
              resolutiondateinvalid ||
              resolutiondaterangeerr ||
              Cemptyerr
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_before_eof,
              resolutionemptyerr,
              resolutiondateemptyerr,
              resolutiondateinvalid,
              resolutiondaterangeerr,
              Cemptyerr,
            };
          });

          // QAQC-043 - table-level check across all rows, recomputed on
          // every Validate() pass so it can't go stale.
          const icansSelection = Q36IcansSelectionList?.find(
            (x) => x.cohortTreatmentId === p.cohortTreatmentId,
          );
          const isQ36IcansSelected = icansSelection?.selected ?? false;
          const hasIcansEvent = innerArrQ54.some(
            (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
          );
          const q54q36Warning = isQ36IcansSelected && !hasIcansEvent;
          innerArrQ54 = innerArrQ54.map((r) => ({
            ...r,
            Q54q36WarningMsg: q54q36Warning,
          }));

          // QAQC-043 - see the Q53 block above.
          if (q54q36Warning) {
            isValid = false;
          }
        }
        if (p.q55_neutropenia_list && p.q55_neutropenia_list.length > 0) {
          innerArrQ55 = p.q55_neutropenia_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q55_neutropenia_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) =>
                    idx !== cIndex && p.q55_neutropenia_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            // Date of resolution [C] retired
            /*
            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;

              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }
            }
            */

            let Demptyerr = false;
            if (isNewEcrfA && c.rbl_B !== null && c.rbl_B !== 1) {
              Demptyerr = c.rblD === null;
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              // Date of resolution [C] retired
              // C_empty_err ||
              // C_date_empty_err ||
              // C_date_before_index_err ||
              // C_date_before_onset_err ||
              // C_date_after_err ||
              // C_date_invalid ||
              // C_date_before_eof ||
              Demptyerr
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              B_date_after_eof,
              C_date_before_eof,
              Demptyerr,
            };
          });
        }
        if (p.q56_cytopenia_list && p.q56_cytopenia_list.length > 0) {
          innerArrQ56 = p.q56_cytopenia_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q56_cytopenia_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) =>
                    idx !== cIndex && p.q56_cytopenia_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            // Date of resolution [C] retired
            /*
            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;

              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }
            }
            */

            let Demptyerr = false;
            if (isNewEcrfA && c.rbl_B !== null && c.rbl_B !== 1) {
              Demptyerr = c.rblD === null;
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              // Date of resolution [C] retired
              // C_empty_err ||
              // C_date_empty_err ||
              // C_date_before_index_err ||
              // C_date_before_onset_err ||
              // C_date_after_err ||
              // C_date_invalid ||
              // C_date_before_eof ||
              Demptyerr
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              B_date_after_eof,
              C_date_before_eof,
              Demptyerr,
            };
          });
        }
        if (p.q57_infection_list && p.q57_infection_list.length > 0) {
          innerArrQ57 = p.q57_infection_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_before_diagnosis_err = false;
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;

            // RESOLUTION VALIDATION (Element C only)
            let resolutionemptyerr = false;
            let resolutiondateemptyerr = false;
            let resolutiondateinvalid = false;
            let resolutiondaterangeerr = false;

            // IgG assessment prior to infection VALIDATION (Element C only)
            let H_empty_err = false;
            let H_date_empty_err = false;
            let H_date_invalid = false;

            // IgG level VALIDATION (Element C only)
            let I_empty_err = false;
            let I_unit_empty_err = false;
            let I_unit_invalid_err = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_diagnosis_err = IsOnsetBeforeIndex(
                    c.date_B,
                    mmDiagnosisDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q57_infection_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) =>
                    idx !== cIndex && p.q57_infection_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }
            let C_empty_err = false;
            // Infection organism [D] retired
            let D_empty_err = false;
            let D_txt_empty_err = false;
            let E_empty_err = false;
            let F_empty_err = false;
            let G_empty_err = false;
            let G_txt_empty_err = false;

            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = !c.chk_C_1 && !c.chk_C_2 && !c.chk_C_3 && !c.rb_C;
              // Infection organism [D] retired
              /*
              D_empty_err =
                !c.chk_D_1 &&
                !c.chk_D_2 &&
                !c.chk_D_3 &&
                !c.chk_D_4 &&
                !c.chk_D_5 &&
                !c.chk_D_6 &&
                !c.chk_D_7 &&
                !c.chk_D_8 &&
                !c.chk_D_9 &&
                !c.chk_D_10 &&
                !c.chk_D_11 &&
                !c.chk_D_12 &&
                !c.chk_D_13 &&
                !c.chk_D_14 &&
                !c.chk_D_15 &&
                !c.txt_D_oth &&
                !c.rb_D;
              D_txt_empty_err = c.chk_D_15 && isStringEmpty(c.txt_D_oth);
              */
              E_empty_err = c.rbl_E == null;
              F_empty_err = c.rbl_F == null;

              if (isNewEcrfB || isNewEcrfC) {
                G_empty_err =
                  !c.chk_G_1 &&
                  !c.chk_G_2 &&
                  !c.chk_G_3 &&
                  !c.chk_G_4 &&
                  !c.chk_G_5 &&
                  !c.rb_G_unk;
                G_txt_empty_err = c.chk_G_5 && isStringEmpty(c.txt_G_oth);
              }

              if (isNewEcrfC) {
                // Date of resolution
                resolutionemptyerr = c.rbl_resolution == null;

                if (c.rbl_resolution != null && c.rbl_resolution === 2) {
                  if (isStringEmpty(c.date_resolution)) {
                    resolutiondateemptyerr = true;
                  } else if (!IsDateValid(c.date_resolution)) {
                    resolutiondateinvalid = true;
                  } else {
                    const isBeforeOnset = IsOnsetBeforeIndex(
                      c.date_resolution,
                      c.date_B,
                    );
                    const isBeforeIndex = IsOnsetBeforeIndex(
                      c.date_resolution,
                      p.cohortIndexDate,
                    );
                    const isAfterAeEnd = IsDateAfterFunction(
                      c.date_resolution,
                      p.aeEndDate,
                    );
                    const isAfterEof = IsDateAfter(
                      c.date_resolution,
                      endOfFollowUpDate,
                    );

                    resolutiondaterangeerr =
                      isBeforeIndex ||
                      isBeforeOnset ||
                      isAfterAeEnd ||
                      isAfterEof;
                  }
                }
              }
            }

            // Date of most recent IgG assessment prior to infection / IgG level
            // (Element C only; not tied to whether an infection episode occurred)
            let H_date_before_diagnosis_err = false;
            let H_date_after_onset_err = false;
            if (isNewEcrfC && c.rbl_B != null && c.rbl_B !== 1) {
              H_empty_err = c.rbl_H == null;
              if (!H_empty_err && c.rbl_H === 2) {
                if (isStringEmpty(c.date_H)) {
                  H_date_empty_err = true;
                } else if (!IsDateValid(c.date_H)) {
                  H_date_invalid = true;
                } else {
                  H_date_before_diagnosis_err = IsOnsetBeforeIndex(
                    c.date_H,
                    mmDiagnosisDate,
                  );
                  if (
                    !H_date_before_diagnosis_err &&
                    !isStringEmpty(c.date_B)
                  ) {
                    H_date_after_onset_err = IsDateAfterFunction(
                      c.date_H,
                      c.date_B,
                    );
                  }
                }
              }

              // I is rendered as an active (non-"Not applicable") field whenever
              // H isn't "not assessed" - including while H itself is still
              // unanswered - so it must be required under that same condition.
              if (c.rbl_H !== 1) {
                I_empty_err = c.rbl_I == null;
                if (!I_empty_err && c.rbl_I !== 99) {
                  if (isStringEmpty(c.unit_I)) {
                    I_unit_empty_err = true;
                  } else {
                    const unitVal = parseInt(c.unit_I, 10);
                    const minVal = 1;
                    const maxVal = c.rbl_I === 1 ? 5000 : 50;
                    if (
                      isNaN(unitVal) ||
                      unitVal < minVal ||
                      unitVal > maxVal
                    ) {
                      I_unit_invalid_err = true;
                    }
                  }
                }
              }
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_before_diagnosis_err ||
              B_date_after_eof ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              C_empty_err ||
              // Infection organism [D] retired
              // D_empty_err ||
              // D_txt_empty_err ||
              E_empty_err ||
              F_empty_err ||
              G_empty_err ||
              G_txt_empty_err ||
              resolutionemptyerr ||
              resolutiondateemptyerr ||
              resolutiondateinvalid ||
              resolutiondaterangeerr ||
              H_empty_err ||
              H_date_empty_err ||
              H_date_invalid ||
              H_date_before_diagnosis_err ||
              H_date_after_onset_err ||
              I_empty_err ||
              I_unit_empty_err ||
              I_unit_invalid_err
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_before_diagnosis_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              C_empty_err,
              D_empty_err,
              D_txt_empty_err,
              E_empty_err,
              F_empty_err,
              B_date_after_eof,
              G_empty_err,
              G_txt_empty_err,
              resolutionemptyerr,
              resolutiondateemptyerr,
              resolutiondateinvalid,
              resolutiondaterangeerr,
              H_empty_err,
              H_date_empty_err,
              H_date_invalid,
              H_date_before_diagnosis_err,
              H_date_after_onset_err,
              I_empty_err,
              I_unit_empty_err,
              I_unit_invalid_err,
            };
          });
        }
        if (p.q59_dysgeusia_list && p.q59_dysgeusia_list.length > 0) {
          innerArrQ59 = p.q59_dysgeusia_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;
            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q59_dysgeusia_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) =>
                    idx !== cIndex && p.q59_dysgeusia_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let CC_1_empty_err = false;
            let CC_2_empty_err = false;
            let CC_3_empty_err = false;
            let CC_4_empty_err = false;
            let CC_5_empty_err = false;
            let CC_6_empty_err = false;
            let CC_7_empty_err = false;
            let CC_empty_err = false;
            let CC_txt_empty_err = false;

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            let D_empty_err = false;
            let D_date_empty_err = false;
            let D_date_before_index_err = false;
            let D_date_before_onset_err = false;
            let D_date_before_impr_err = false;
            let D_date_after_err = false;
            let D_date_invalid = false;
            let D_date_before_eof = false;

            if (c.rbl_B != null && c.rbl_B !== 1) {
              CC_1_empty_err = c.chk_CC_1 && !c.chk_CC_1_P && !c.chk_CC_1_T;
              CC_2_empty_err = c.chk_CC_2 && !c.chk_CC_2_P && !c.chk_CC_2_T;
              CC_3_empty_err = c.chk_CC_3 && !c.chk_CC_3_P && !c.chk_CC_3_T;
              CC_4_empty_err = c.chk_CC_4 && !c.chk_CC_4_P && !c.chk_CC_4_T;
              CC_5_empty_err = c.chk_CC_5 && !c.chk_CC_5_P && !c.chk_CC_5_T;
              CC_6_empty_err = c.chk_CC_6 && !c.chk_CC_6_P && !c.chk_CC_6_T;
              CC_7_empty_err = c.chk_CC_7 && !c.chk_CC_7_P && !c.chk_CC_7_T;
              CC_empty_err =
                !c.chk_CC_1 &&
                !c.chk_CC_2 &&
                !c.chk_CC_3 &&
                !c.chk_CC_4 &&
                !c.chk_CC_5 &&
                !c.chk_CC_6 &&
                !c.chk_CC_7 &&
                !c.rb_CC;
              CC_txt_empty_err = c.chk_CC_7 && isStringEmpty(c.txt_CC_oth);

              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;
              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }

              D_empty_err = c.rbl_D == null;
              D_date_empty_err = false; //c.rbl_D === 2 && isStringEmpty(c.date_D);
              D_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_D, p.cohortIndexDate);
              D_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_B);
              D_date_before_impr_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_C);
              D_date_after_err = false; //IsDateAfterFunction(c.date_D, p.aeEndDate);
              D_date_before_eof = false;
              if (!D_empty_err) {
                if (c.rbl_D === 2) {
                  if (isStringEmpty(c.date_D)) {
                    D_date_empty_err = true;
                  } else if (!IsDateValid(c.date_D)) {
                    D_date_invalid = true;
                  } else {
                    D_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_D,
                      p.cohortIndexDate,
                    );
                    D_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_B,
                    );
                    D_date_before_impr_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_C,
                    );
                    D_date_after_err = IsDateAfterFunction(
                      c.date_D,
                      p.aeEndDate,
                    );
                    D_date_before_eof = IsDateAfter(
                      c.date_D,
                      endOfFollowUpDate,
                    );
                  }
                }
              }
            }
            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              CC_1_empty_err ||
              CC_2_empty_err ||
              CC_3_empty_err ||
              CC_4_empty_err ||
              CC_5_empty_err ||
              CC_6_empty_err ||
              CC_7_empty_err ||
              CC_empty_err ||
              CC_txt_empty_err ||
              C_empty_err ||
              C_date_empty_err ||
              C_date_before_index_err ||
              C_date_before_onset_err ||
              C_date_after_err ||
              C_date_invalid ||
              C_date_before_eof ||
              D_empty_err ||
              D_date_empty_err ||
              D_date_before_index_err ||
              D_date_before_onset_err ||
              D_date_before_impr_err ||
              D_date_after_err ||
              D_date_invalid ||
              D_date_before_eof
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_after_eof,
              CC_1_empty_err,
              CC_2_empty_err,
              CC_3_empty_err,
              CC_4_empty_err,
              CC_5_empty_err,
              CC_6_empty_err,
              CC_7_empty_err,
              CC_empty_err,
              CC_txt_empty_err,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              C_date_before_eof,
              D_empty_err,
              D_date_empty_err,
              D_date_before_index_err,
              D_date_before_onset_err,
              D_date_before_impr_err,
              D_date_after_err,
              D_date_invalid,
              D_date_before_eof,
            };
          });
        }
        if (p.q60_nail_list && p.q60_nail_list.length > 0) {
          innerArrQ60 = p.q60_nail_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q60_nail_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) => idx !== cIndex && p.q60_nail_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            let D_empty_err = false;
            let D_date_empty_err = false;
            let D_date_before_index_err = false;
            let D_date_before_onset_err = false;
            let D_date_before_impr_err = false;
            let D_date_after_err = false;
            let D_date_invalid = false;
            let D_date_before_eof = false;

            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;
              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }

              D_empty_err = c.rbl_D == null;
              D_date_empty_err = false; //c.rbl_D === 2 && isStringEmpty(c.date_D);
              D_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_D, p.cohortIndexDate);
              D_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_B);
              D_date_before_impr_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_C);
              D_date_after_err = false; //IsDateAfterFunction(c.date_D, p.aeEndDate);
              D_date_before_eof = false;
              if (!D_empty_err) {
                if (c.rbl_D === 2) {
                  if (isStringEmpty(c.date_D)) {
                    D_date_empty_err = true;
                  } else if (!IsDateValid(c.date_D)) {
                    D_date_invalid = true;
                  } else {
                    D_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_D,
                      p.cohortIndexDate,
                    );
                    D_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_B,
                    );
                    D_date_before_impr_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_C,
                    );
                    D_date_after_err = IsDateAfterFunction(
                      c.date_D,
                      p.aeEndDate,
                    );
                    D_date_before_eof = IsDateAfter(
                      c.date_D,
                      endOfFollowUpDate,
                    );
                  }
                }
              }
            }
            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              C_empty_err ||
              C_date_empty_err ||
              C_date_before_index_err ||
              C_date_before_onset_err ||
              C_date_after_err ||
              C_date_invalid ||
              C_date_before_eof ||
              D_empty_err ||
              D_date_empty_err ||
              D_date_before_index_err ||
              D_date_before_onset_err ||
              D_date_before_impr_err ||
              D_date_after_err ||
              D_date_invalid ||
              C_date_before_eof
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_after_eof,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              C_date_before_eof,
              D_empty_err,
              D_date_empty_err,
              D_date_before_index_err,
              D_date_before_onset_err,
              D_date_before_impr_err,
              D_date_after_err,
              D_date_invalid,
              D_date_before_eof,
            };
          });
        }
        if (p.q61_skin_list && p.q61_skin_list.length > 0) {
          innerArrQ61 = p.q61_skin_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;
            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q61_skin_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) => idx !== cIndex && p.q61_skin_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            let D_empty_err = false;
            let D_date_empty_err = false;
            let D_date_before_index_err = false;
            let D_date_before_onset_err = false;
            let D_date_before_impr_err = false;
            let D_date_after_err = false;
            let D_date_invalid = false;
            let D_date_before_eof = false;

            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;
              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }

              D_empty_err = c.rbl_D == null;
              D_date_empty_err = false; //c.rbl_D === 2 && isStringEmpty(c.date_D);
              D_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_D, p.cohortIndexDate);
              D_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_B);
              D_date_before_impr_err = false; //IsOnsetBeforeIndex(c.date_D, c.date_C);
              D_date_after_err = false; //IsDateAfterFunction(c.date_D, p.aeEndDate);
              D_date_before_eof = false;
              if (!D_empty_err) {
                if (c.rbl_D === 2) {
                  if (isStringEmpty(c.date_D)) {
                    D_date_empty_err = true;
                  } else if (!IsDateValid(c.date_D)) {
                    D_date_invalid = true;
                  } else {
                    D_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_D,
                      p.cohortIndexDate,
                    );
                    D_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_B,
                    );
                    D_date_before_impr_err = IsOnsetBeforeIndex(
                      c.date_D,
                      c.date_C,
                    );
                    D_date_after_err = IsDateAfterFunction(
                      c.date_D,
                      p.aeEndDate,
                    );
                    D_date_before_eof = IsDateAfter(
                      c.date_D,
                      endOfFollowUpDate,
                    );
                  }
                }
              }
            }
            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              C_empty_err ||
              C_date_empty_err ||
              C_date_before_index_err ||
              C_date_before_onset_err ||
              C_date_after_err ||
              C_date_invalid ||
              C_date_before_eof ||
              D_empty_err ||
              D_date_empty_err ||
              D_date_before_index_err ||
              D_date_before_onset_err ||
              D_date_before_impr_err ||
              D_date_after_err ||
              D_date_invalid ||
              D_date_before_eof
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_after_eof,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              C_date_before_eof,
              D_empty_err,
              D_date_empty_err,
              D_date_before_index_err,
              D_date_before_onset_err,
              D_date_before_impr_err,
              D_date_after_err,
              D_date_invalid,
              D_date_before_eof,
            };
          });
        }
        if (p.q62_spm_list && p.q62_spm_list.length > 0) {
          innerArrQ62 = p.q62_spm_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;

            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q62_spm_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) => idx !== cIndex && p.q62_spm_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let q62CEmptyError = false;
            let q62CDateEmptyError = false;
            let q62CDateInvalidError = false;
            let q62CDateBeforeIndexError = false;
            let q62CDateBeforeOnsetError = false;
            let q62CDateAfterAEDateError = false;

            let q62DEmptyError = false;

            if (c.rbl_B != null && c.rbl_B !== 1 && isNewEcrfA) {
              q62CEmptyError = c.q62_rbl_C == null;

              if (!q62CEmptyError) {
                if (c.q62_rbl_C === 2) {
                  if (isStringEmpty(c.q62_date_C)) {
                    q62CDateEmptyError = true;
                  } else if (!IsDateValid(c.q62_date_C)) {
                    q62CDateInvalidError = true;
                  } else {
                    q62CDateBeforeIndexError = IsOnsetBeforeIndex(
                      c.q62_date_C,
                      p.cohortIndexDate,
                    );
                    q62CDateBeforeOnsetError = IsOnsetBeforeIndex(
                      c.q62_date_C,
                      c.date_B,
                    );
                    q62CDateAfterAEDateError = IsDateAfterFunction(
                      c.q62_date_C,
                      p.aeEndDate,
                    );
                  }
                }
              }

              q62DEmptyError = c.q62_severity == null;
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              q62CEmptyError ||
              q62CDateEmptyError ||
              q62CDateInvalidError ||
              q62CDateBeforeIndexError ||
              q62CDateBeforeOnsetError ||
              q62CDateAfterAEDateError ||
              q62DEmptyError
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_after_eof,
              q62CEmptyError,
              q62CDateEmptyError,
              q62CDateInvalidError,
              q62CDateBeforeIndexError,
              q62CDateBeforeOnsetError,
              q62CDateAfterAEDateError,
              q62DEmptyError,
            };
          });
        }
        if (p.q63_neurotoxicity_list && p.q63_neurotoxicity_list.length > 0) {
          innerArrQ63 = p.q63_neurotoxicity_list.map((c, cIndex) => {
            const B_empty_err = c.rbl_B == null;
            let B_date_empty_err = false; //c.rbl_B === 2 && isStringEmpty(c.date_B);
            let B_date_before_err = false; //IsOnsetBeforeIndex(c.date_B, p.cohortIndexDate);
            let B_date_after_err = false; //IsDateAfterFunction(c.date_B, p.aeEndDate);
            let B_date_equal_prev_ep_err = false;
            let B_date_invalid = false;
            let B_date_after_eof = false;
            if (!B_empty_err) {
              if (c.rbl_B === 2) {
                if (isStringEmpty(c.date_B)) {
                  B_date_empty_err = true;
                } else if (!IsDateValid(c.date_B)) {
                  B_date_invalid = true;
                } else {
                  B_date_before_err = IsOnsetBeforeIndex(
                    c.date_B,
                    p.cohortIndexDate,
                  );
                  B_date_after_err = IsDateAfterFunction(c.date_B, p.aeEndDate);
                  B_date_after_eof = IsDateAfter(c.date_B, endOfFollowUpDate);
                }
              }
            }
            if (cIndex > 0 && c.date_B) {
              const previousDates = p.q63_neurotoxicity_list
                .slice(0, cIndex)
                .filter(
                  (_, idx) =>
                    idx !== cIndex && p.q63_neurotoxicity_list[idx].date_B,
                )
                .map((crs) => crs.date_B);

              if (previousDates.length > 0) {
                if (previousDates.includes(c.date_B)) {
                  B_date_equal_prev_ep_err = true;
                }
              }
            }

            let C_empty_err = false;
            let C_date_empty_err = false;
            let C_date_before_index_err = false;
            let C_date_before_onset_err = false;
            let C_date_after_err = false;
            let C_date_invalid = false;
            let C_date_before_eof = false;

            let D_empty_err = false;
            let D_txt_empty_err = false;
            let E_empty_err = false;
            let F_empty_err = false;
            let G_empty_err = false;

            if (c.rbl_B != null && c.rbl_B !== 1) {
              C_empty_err = c.rbl_C == null;
              C_date_empty_err = false; //c.rbl_C === 2 && isStringEmpty(c.date_C);
              C_date_before_index_err = false; //IsOnsetBeforeIndex(c.date_C, p.cohortIndexDate);
              C_date_before_onset_err = false; //IsOnsetBeforeIndex(c.date_C, c.date_B);
              C_date_after_err = false; //IsDateAfterFunction(c.date_C, p.aeEndDate);
              C_date_before_eof = false;
              if (!C_empty_err) {
                if (c.rbl_C === 2) {
                  if (isStringEmpty(c.date_C)) {
                    C_date_empty_err = true;
                  } else if (!IsDateValid(c.date_C)) {
                    C_date_invalid = true;
                  } else {
                    C_date_before_index_err = IsOnsetBeforeIndex(
                      c.date_C,
                      p.cohortIndexDate,
                    );
                    C_date_before_onset_err = IsOnsetBeforeIndex(
                      c.date_C,
                      c.date_B,
                    );
                    C_date_after_err = IsDateAfterFunction(
                      c.date_C,
                      p.aeEndDate,
                    );
                    C_date_before_eof = IsDateAfter(
                      c.date_C,
                      endOfFollowUpDate,
                    );
                  }
                }
              }

              D_empty_err = c.rbl_D == null;

              if (c.rbl_D === 7) {
                D_txt_empty_err = isStringEmpty(c.txt_D_oth);
              }

              E_empty_err = c.rbl_E == null;

              F_empty_err =
                !c.chk_F_1 &&
                !c.chk_F_2 && // legacy old-eCRF "Hospital" value, kept so previously-saved rows still validate
                !c.chkFInpatient &&
                !c.chkFEmergencyRoom &&
                !c.chkFIntensiveCareUnit &&
                !c.chkFUrgentCareVisit &&
                !c.chkFOther &&
                !c.rb_F;
              G_empty_err =
                !c.chk_G_1 &&
                !c.chk_G_2 &&
                !c.chk_G_3 &&
                !c.chk_G_4 &&
                !c.chk_G_5 &&
                !c.rb_G;
            }

            // After Gemptyerr validation, add Column H validation:
            let Hemptyerr = false;
            let Hdateemptyerr = false;
            let Hdateinvalid = false;
            let Hdatebeforeindexerr = false;
            let Hdatebeforeonseterr = false;
            let Hdatebeforereserr = false;
            let Hdateaftererr = false;
            let Hdatebeforeeof = false;

            if (c.rbl_B !== null && c.rbl_B !== 1 && isNewEcrfA) {
              // Column H: Earliest date of improvement validation
              Hemptyerr = c.rblH === null;
              if (!Hemptyerr && isNewEcrfA) {
                if (c.rblH === 2) {
                  if (isStringEmpty(c.dateH)) {
                    Hdateemptyerr = true;
                  } else if (!IsDateValid(c.dateH)) {
                    Hdateinvalid = true;
                  } else {
                    Hdatebeforeindexerr = IsOnsetBeforeIndex(
                      c.dateH,
                      p.cohortIndexDate,
                    );
                    Hdatebeforeonseterr = IsOnsetBeforeIndex(c.dateH, c.date_B);
                    Hdatebeforereserr = IsDateAfterFunction(c.dateH, c.date_C);
                    Hdateaftererr = IsDateAfterFunction(c.dateH, p.aeEndDate);
                    Hdatebeforeeof = IsDateAfter(c.dateH, endOfFollowUpDate);
                  }
                }
              }
            }

            if (
              B_empty_err ||
              B_date_empty_err ||
              B_date_before_err ||
              B_date_after_err ||
              B_date_equal_prev_ep_err ||
              B_date_invalid ||
              B_date_after_eof ||
              C_empty_err ||
              C_date_empty_err ||
              C_date_before_index_err ||
              C_date_before_onset_err ||
              C_date_after_err ||
              C_date_invalid ||
              C_date_before_eof ||
              D_empty_err ||
              D_txt_empty_err ||
              E_empty_err ||
              F_empty_err ||
              G_empty_err ||
              Hemptyerr ||
              Hdateemptyerr ||
              Hdateinvalid ||
              Hdatebeforeindexerr ||
              Hdatebeforeonseterr ||
              Hdatebeforereserr ||
              Hdateaftererr ||
              Hdatebeforeeof
            ) {
              isValid = false;
            }

            return {
              ...c,
              B_empty_err,
              B_date_empty_err,
              B_date_before_err,
              B_date_after_err,
              B_date_equal_prev_ep_err,
              B_date_invalid,
              B_date_after_eof,
              C_empty_err,
              C_date_empty_err,
              C_date_before_index_err,
              C_date_before_onset_err,
              C_date_after_err,
              C_date_invalid,
              C_date_before_eof,
              D_empty_err,
              D_txt_empty_err,
              E_empty_err,
              F_empty_err,
              G_empty_err,

              Hemptyerr,
              Hdateemptyerr,
              Hdateinvalid,
              Hdatebeforeindexerr,
              Hdatebeforeonseterr,
              Hdatebeforereserr,
              Hdateaftererr,
              Hdatebeforeeof,
            };
          });

          // QAQC-045 Rule A - table-level check across all rows, recomputed
          // on every Validate() pass so it can't go stale.
          const neurotoxTreatmentSelection =
            Q63NeurotoxicityTreatmentSelectionList?.find(
              (x) => x.cohortTreatmentId === p.cohortTreatmentId,
            );
          const isNeurotoxTreatmentSelected =
            neurotoxTreatmentSelection?.selected ?? false;
          const hasNeurotoxEvent = innerArrQ63.some(
            (r) => r.rbl_B !== null && r.rbl_B !== undefined && r.rbl_B !== 1,
          );
          const q63q39Warning =
            isNeurotoxTreatmentSelected && !hasNeurotoxEvent;
          innerArrQ63 = innerArrQ63.map((r) => ({
            ...r,
            Q63q39WarningMsg: q63q39Warning,
          }));

          // QAQC-045 Rule A - see the Q53 block above.
          if (q63q39Warning) {
            isValid = false;
          }
        }

        // Q64 Validations
        if (isNewEcrfA && p.q64_enterocolitis_list?.length > 0) {
          innerArrQ64 = p.q64_enterocolitis_list.map((c, cIndex) => {
            const errors = {
              q64BEmptyError: false,
              q64BDateEmptyError: false,
              q64BDateInvalidError: false,
              q64BDateEqualPrevError: false,
              q64BDateBeforeIndexDateError: false,
              q64BDateAfterCDateError: false,
              q64BDateAfterResolutionDateError: false,
              q64BDateAfterAEDateError: false,

              q64CEmptyError: false,
              q64CDateEmptyError: false,
              q64CDateInvalidError: false,
              q64CDateBeforeError: false,
              q64CDateAfterResolutionDateError: false,
              q64CDateAfterAEDateError: false,

              q64DEmptyError: false,
              q64DDateEmptyError: false,
              q64DDateInvalidError: false,
              q64DDateBeforeError: false,
              q64DDateAfterAEDateError: false,

              q64EEmptyError: false,
              q64EOtherTxtEmptyError: false,
              q64FEmptyError: false,
            };

            errors.q64BEmptyError = c.q64_rbl_B == null;

            if (!errors.q64BEmptyError && c.q64_rbl_B === 2) {
              if (isStringEmpty(c.q64_date_B)) {
                errors.q64BDateEmptyError = true;
              } else if (!IsDateValid(c.q64_date_B)) {
                errors.q64BDateInvalidError = true;
              }

              // Duplicate B date
              if (cIndex > 0 && c.q64_date_B) {
                const prevBDates = p.q64_enterocolitis_list
                  .slice(0, cIndex)
                  .map((x) => x.q64_date_B)
                  .filter(Boolean);

                if (prevBDates.includes(c.q64_date_B)) {
                  errors.q64BDateEqualPrevError = true;
                }
              }
            }

            if (c.q64_rbl_B != null && c.q64_rbl_B !== 1) {
              // C
              errors.q64CEmptyError = c.q64_rbl_C == null;
              if (!errors.q64CEmptyError && c.q64_rbl_C === 2) {
                if (isStringEmpty(c.q64_date_C)) {
                  errors.q64CDateEmptyError = true;
                } else if (!IsDateValid(c.q64_date_C)) {
                  errors.q64CDateInvalidError = true;
                }
              }

              // D
              errors.q64DEmptyError = c.q64_rbl_D == null;
              if (!errors.q64DEmptyError && c.q64_rbl_D === 2) {
                if (isStringEmpty(c.q64_date_D)) {
                  errors.q64DDateEmptyError = true;
                } else if (!IsDateValid(c.q64_date_D)) {
                  errors.q64DDateInvalidError = true;
                }
              }

              // E
              errors.q64EEmptyError =
                !c.q64_chk_E_1 &&
                !c.q64_chk_E_2 &&
                !c.q64_chk_E_3 &&
                !c.q64_chk_E_4 &&
                !c.q64_chk_E_5 &&
                !c.q64_E_unk;

              if (c.q64_chk_E_5 && isStringEmpty(c.q64_chk_E_othTxt)) {
                errors.q64EOtherTxtEmptyError = true;
              }

              // F
              errors.q64FEmptyError = c.q64_severity == null;
            }

            /* Index ≤ B ≤ C ≤ D ≤ AE End
             */
            // --- DATE CHAIN VALIDATION ---
            const dateChainErrors = validateQ64Dates(
              c,
              p.cohortIndexDate,
              p.aeEndDate,
            );
            Object.assign(errors, dateChainErrors);

            // mark overall validity
            if (Object.values(errors).some(Boolean)) {
              isValid = false;
            }

            return { ...c, ...errors };
          });
        }

        // Q65 Validations
        if (isNewEcrfB && p.q65_lymphopenia_list?.length > 0) {
          innerArrQ65 = p.q65_lymphopenia_list.map((c, cIndex) => {
            const errors = {
              q65BEmptyError: false,
              q65BDateEmptyError: false,
              q65BDateInvalidError: false,
              q65BDateEqualPrevError: false,
              q65BDateBeforeIndexDateError: false,
              q65BDateAfterAEDateError: false,

              q65CEmptyError: false,
              q65CDateEmptyError: false,
              q65CDateInvalidError: false,
              q65CDateBeforeError: false,
              q65CDateAfterAEDateError: false,

              q65DEmptyError: false,
            };

            errors.q65BEmptyError = c.q65_rbl_B == null;

            if (!errors.q65BEmptyError && c.q65_rbl_B === 2) {
              if (isStringEmpty(c.q65_date_B)) {
                errors.q65BDateEmptyError = true;
              } else if (!IsDateValid(c.q65_date_B)) {
                errors.q65BDateInvalidError = true;
              }

              // Duplicate B date
              if (cIndex > 0 && c.q65_date_B) {
                const prevBDates = p.q65_lymphopenia_list
                  .slice(0, cIndex)
                  .map((x) => x.q65_date_B)
                  .filter(Boolean);

                if (prevBDates.includes(c.q65_date_B)) {
                  errors.q65BDateEqualPrevError = true;
                }
              }
            }

            if (c.q65_rbl_B != null && c.q65_rbl_B !== 1) {
              // Date of resolution [C] retired
              /*
              // C
              errors.q65CEmptyError = c.q65_rbl_C == null;
              if (!errors.q65CEmptyError && c.q65_rbl_C === 2) {
                if (isStringEmpty(c.q65_date_C)) {
                  errors.q65CDateEmptyError = true;
                } else if (!IsDateValid(c.q65_date_C)) {
                  errors.q65CDateInvalidError = true;
                }
              }
              */

              // D
              errors.q65DEmptyError = c.q65_severity == null;
            }

            /* Index ≤ B ≤ C ≤ AE End
             */
            // --- DATE CHAIN VALIDATION ---
            const dateChainErrors = validateQ65Dates(
              c,
              p.cohortIndexDate,
              p.aeEndDate,
            );
            Object.assign(errors, dateChainErrors);

            // mark overall validity
            if (Object.values(errors).some(Boolean)) {
              isValid = false;
            }

            return { ...c, ...errors };
          });
        }

        // Q66 Validations
        if (isNewEcrfC && p.q66_dizziness_list?.length > 0) {
          innerArrQ66 = p.q66_dizziness_list.map((c, cIndex) => {
            const errors = {
              q66BEmptyError: false,
              q66BDateEmptyError: false,
              q66BDateInvalidError: false,
              q66BDateEqualPrevError: false,
              q66BDateBeforeIndexDateError: false,
              q66BDateAfterAEDateError: false,

              q66CEmptyError: false,
              q66CDateEmptyError: false,
              q66CDateInvalidError: false,
              q66CDateBeforeError: false,
              q66CDateAfterAEDateError: false,
            };

            errors.q66BEmptyError = c.q66_rbl_B == null;

            if (!errors.q66BEmptyError && c.q66_rbl_B === 2) {
              if (isStringEmpty(c.q66_date_B)) {
                errors.q66BDateEmptyError = true;
              } else if (!IsDateValid(c.q66_date_B)) {
                errors.q66BDateInvalidError = true;
              }

              // Duplicate B date
              if (cIndex > 0 && c.q66_date_B) {
                const prevBDates = p.q66_dizziness_list
                  .slice(0, cIndex)
                  .map((x) => x.q66_date_B)
                  .filter(Boolean);

                if (prevBDates.includes(c.q66_date_B)) {
                  errors.q66BDateEqualPrevError = true;
                }
              }
            }

            if (c.q66_rbl_B != null && c.q66_rbl_B !== 1) {
              // C
              errors.q66CEmptyError = c.q66_rbl_C == null;
              if (!errors.q66CEmptyError && c.q66_rbl_C === 2) {
                if (isStringEmpty(c.q66_date_C)) {
                  errors.q66CDateEmptyError = true;
                } else if (!IsDateValid(c.q66_date_C)) {
                  errors.q66CDateInvalidError = true;
                }
              }
            }

            /* Index ≤ B ≤ C ≤ AE End */
            // --- DATE CHAIN VALIDATION ---
            const dateChainErrors = validateQ66Dates(
              c,
              p.cohortIndexDate,
              p.aeEndDate,
            );
            Object.assign(errors, dateChainErrors);

            // Cross-question warning: if Q26/Q26.1 or Q38.1.A/Q38.2.A already
            // flagged "Dizziness" as a reason for this cohort-defining
            // treatment, selecting "No events occurred" here fails
            // validation per the Q66 spec.
            if (cIndex === 0 && c.q66_rbl_B === 1) {
              const selection = q66QDizzinessSelectionList?.find(
                (x) => x.cohortTreatmentId === p.cohortTreatmentId,
              );
              if (selection?.q26Selected || selection?.q38Selected) {
                isValid = false;
              }
            }

            // mark overall validity
            if (Object.values(errors).some(Boolean)) {
              isValid = false;
            }

            return { ...c, ...errors };
          });
        }

        // Q67 Validations
        if (isNewEcrfC && p.q67_ataxia_list?.length > 0) {
          innerArrQ67 = p.q67_ataxia_list.map((c, cIndex) => {
            const errors = {
              q67BEmptyError: false,
              q67BDateEmptyError: false,
              q67BDateInvalidError: false,
              q67BDateEqualPrevError: false,
              q67BDateBeforeIndexDateError: false,
              q67BDateAfterAEDateError: false,

              q67CEmptyError: false,
              q67CDateEmptyError: false,
              q67CDateInvalidError: false,
              q67CDateBeforeError: false,
              q67CDateAfterAEDateError: false,

              q67DEmptyError: false,

              q67EEmptyError: false,
              q67EOtherTxtEmptyError: false,

              q67FEmptyError: false,
              q67FOtherTxtEmptyError: false,
              q67F1MismatchError: false,
              q67F2MismatchError: false,
              q67F3MismatchError: false,
            };

            errors.q67BEmptyError = c.q67_rbl_B == null;

            if (!errors.q67BEmptyError && c.q67_rbl_B === 2) {
              if (isStringEmpty(c.q67_date_B)) {
                errors.q67BDateEmptyError = true;
              } else if (!IsDateValid(c.q67_date_B)) {
                errors.q67BDateInvalidError = true;
              }

              // Duplicate B date
              if (cIndex > 0 && c.q67_date_B) {
                const prevBDates = p.q67_ataxia_list
                  .slice(0, cIndex)
                  .map((x) => x.q67_date_B)
                  .filter(Boolean);

                if (prevBDates.includes(c.q67_date_B)) {
                  errors.q67BDateEqualPrevError = true;
                }
              }
            }

            if (c.q67_rbl_B != null && c.q67_rbl_B !== 1) {
              // C
              errors.q67CEmptyError = c.q67_rbl_C == null;
              if (!errors.q67CEmptyError && c.q67_rbl_C === 2) {
                if (isStringEmpty(c.q67_date_C)) {
                  errors.q67CDateEmptyError = true;
                } else if (!IsDateValid(c.q67_date_C)) {
                  errors.q67CDateInvalidError = true;
                }
              }

              // D (severity)
              errors.q67DEmptyError = c.q67_severity == null;

              // E (presentation)
              errors.q67EEmptyError =
                !c.q67_chk_E_1 &&
                !c.q67_chk_E_2 &&
                !c.q67_chk_E_3 &&
                !c.q67_chk_E_4 &&
                !c.q67_chk_E_5 &&
                !c.q67_chk_E_6 &&
                !c.q67_chk_E_7 &&
                !c.q67_chk_E_8 &&
                !c.q67_E_unk;

              if (c.q67_chk_E_8 && isStringEmpty(c.q67_chk_E_othTxt)) {
                errors.q67EOtherTxtEmptyError = true;
              }

              // F (interventions)
              errors.q67FEmptyError =
                !c.q67_chk_F_1 &&
                !c.q67_chk_F_2 &&
                !c.q67_chk_F_3 &&
                !c.q67_chk_F_4 &&
                !c.q67_chk_F_5 &&
                !c.q67_chk_F_6 &&
                !c.q67_chk_F_7 &&
                !c.q67_F_unk;

              if (c.q67_chk_F_7 && isStringEmpty(c.q67_chk_F_othTxt)) {
                errors.q67FOtherTxtEmptyError = true;
              }

              // Column [F] alignment with prior questions (Tal only)
              const atxSelection = q67QAtaxiaSelectionList?.find(
                (x) => x.cohortTreatmentId === p.cohortTreatmentId,
              );
              errors.q67F1MismatchError =
                !!c.q67_chk_F_1 && !atxSelection?.q26DiscontinuedSelected;
              errors.q67F2MismatchError =
                !!c.q67_chk_F_2 && !atxSelection?.q382ADelaySelected;
              errors.q67F3MismatchError =
                !!c.q67_chk_F_3 && !atxSelection?.q381AReductionSelected;
            }

            /* Index ≤ B ≤ C ≤ AE End */
            // --- DATE CHAIN VALIDATION ---
            const dateChainErrors = validateQ67Dates(
              c,
              p.cohortIndexDate,
              p.aeEndDate,
            );
            Object.assign(errors, dateChainErrors);

            // Cross-question warning: if Q26/Q26.1 or Q38.1.A/Q38.2.A already
            // flagged "Ataxia" as a reason for this cohort-defining
            // treatment, selecting "No events occurred" here fails
            // validation per the Q67 spec.
            if (cIndex === 0 && c.q67_rbl_B === 1) {
              const selection = q67QAtaxiaSelectionList?.find(
                (x) => x.cohortTreatmentId === p.cohortTreatmentId,
              );
              if (selection?.q26Selected || selection?.q38Selected) {
                isValid = false;
              }
            }

            // mark overall validity
            if (Object.values(errors).some(Boolean)) {
              isValid = false;
            }

            return { ...c, ...errors };
          });
        }

        // Q57.1.B Validations
        const q57RecordsExist = p.q57_infection_list?.some(
          (ep) => ep.rbl_B != null && ep.rbl_B !== 1,
        );
        if (
          isNewEcrfB &&
          p.q571B_immunoglobin_list?.length > 0 &&
          q57RecordsExist
        ) {
          innerArrQ571B = p.q571B_immunoglobin_list.map((c, cIndex) => {
            const errors = {
              q571BEmptyError: false,
              q571BDateEmptyError: false,
              q571BDateInvalidError: false,
              q571BDateEqualPrevError: false,
              q571BDateBeforeIndexDateError: false,
              q571BDateAfterDateError: false,

              q571CEmptyError: false,
              q571CUnitEmptyError: false,
              q571CUnitInvalidError: false,

              q571DEmptyError: false,
            };

            errors.q571BEmptyError = c.q571B_rbl_B == null;

            if (!errors.q571BEmptyError && c.q571B_rbl_B === 2) {
              if (isStringEmpty(c.q571B_date_B)) {
                errors.q571BDateEmptyError = true;
              } else if (!IsDateValid(c.q571B_date_B)) {
                errors.q571BDateInvalidError = true;
              } else {
                errors.q571BDateBeforeIndexDateError = IsOnsetBeforeIndex(
                  c.q571B_date_B,
                  p.cohortIndexDate,
                );
                if (!errors.q571BDateBeforeIndexDateError) {
                  const earliestEnd = GetEarliestDate(
                    p.aeEndDate,
                    endOfFollowUpDate,
                  );

                  if (!isStringEmpty(earliestEnd)) {
                    errors.q571BDateAfterDateError = IsDateAfterFunction(
                      c.q571B_date_B,
                      earliestEnd,
                    );
                  }
                }
              }

              // Duplicate B date
              if (cIndex > 0 && c.q571B_date_B) {
                const prevBDates = p.q571B_immunoglobin_list
                  .slice(0, cIndex)
                  .map((x) => x.q571B_date_B)
                  .filter(Boolean);

                if (prevBDates.includes(c.q571B_date_B)) {
                  errors.q571BDateEqualPrevError = true;
                }
              }
            }

            if (c.q571B_rbl_B != null && c.q571B_rbl_B !== 1) {
              // C
              errors.q571CEmptyError = c.q571B_rbl_C == null;
              if (!errors.q571CEmptyError && c.q571B_rbl_C !== 99) {
                if (isStringEmpty(c.q571B_unit_C)) {
                  errors.q571CUnitEmptyError = true;
                } else {
                  const unitVal = parseFloat(c.q571B_unit_C);
                  const minVal = 0.01;
                  const maxVal = c.q571B_rbl_C === 1 ? 5000 : 50;

                  if (isNaN(unitVal) || unitVal < minVal || unitVal > maxVal) {
                    errors.q571CUnitInvalidError = true;
                  }
                }
              }

              // D
              errors.q571DEmptyError = c.q571B_episode_D == null;
            }

            // mark overall validity
            if (Object.values(errors).some(Boolean)) {
              isValid = false;
            }

            return { ...c, ...errors };
          });
        }

        // Q58.C Validations - forces full validation of every row (ignoring the
        // `touched` flag used for live onChange validation) so clicking
        // Validate/Submit surfaces errors even for rows the abstractor never
        // interacted with.
        if (p.q58_weight_list && p.q58_weight_list.length > 0) {
          const touchedRows = p.q58_weight_list.map((c) => ({
            ...c,
            touched: true,
          }));

          innerArrQ58 = ValidateQ58Rows(touchedRows).map((c) => {
            let D_empty_err = false;
            let D_kg_range_err = false;
            let D_lbs_range_err = false;

            if (c.rbl_C !== 1) {
              D_empty_err = c.rbl_D == null;
              if (c.rbl_D === 1) {
                const kg = parseFloat(c.weightKg_D);
                D_kg_range_err =
                  isStringEmpty(c.weightKg_D) ||
                  isNaN(kg) ||
                  kg < 1 ||
                  kg > 630 ||
                  !/^\d+(\.\d{1,2})?$/.test(String(c.weightKg_D).trim());
              } else if (c.rbl_D === 2) {
                const lbs = parseFloat(c.weightLbs_D);
                D_lbs_range_err =
                  isStringEmpty(c.weightLbs_D) ||
                  isNaN(lbs) ||
                  lbs < 1 ||
                  lbs > 1400 ||
                  !/^\d+(\.\d{1,2})?$/.test(String(c.weightLbs_D).trim());
              }
            }

            const updated = {
              ...c,
              D_empty_err,
              D_kg_range_err,
              D_lbs_range_err,
            };

            if (
              Object.values({
                C_empty_err: updated.C_empty_err,
                C_date_empty_err: updated.C_date_empty_err,
                C_date_invalid: updated.C_date_invalid,
                C_date_before_index_err: updated.C_date_before_index_err,
                C_date_after_err: updated.C_date_after_err,
                C_date_outside_window_err: updated.C_date_outside_window_err,
                C_date_not_increasing_err: updated.C_date_not_increasing_err,
                D_empty_err,
                D_kg_range_err,
                D_lbs_range_err,
              }).some(Boolean)
            ) {
              isValid = false;
            }

            return updated;
          });
        }

        return {
          ...p,
          q53_crs_list: innerArrQ53,
          q54_icans_list: innerArrQ54,

          q55_neutropenia_list: innerArrQ55,
          q56_cytopenia_list: innerArrQ56,
          q57_infection_list: innerArrQ57,
          q59_dysgeusia_list: innerArrQ59,
          q60_nail_list: innerArrQ60,
          q61_skin_list: innerArrQ61,
          q62_spm_list: innerArrQ62,
          q63_neurotoxicity_list: innerArrQ63,
          q64_enterocolitis_list: innerArrQ64,
          q65_lymphopenia_list: innerArrQ65,
          q66_dizziness_list: innerArrQ66,
          q67_ataxia_list: innerArrQ67,
          q571B_immunoglobin_list: innerArrQ571B,
          q58_weight_list: innerArrQ58 ?? p.q58_weight_list,
        };
      });

      if (isNewEcrfB) {
        const updatedWarnings = validateQ65Warnings(
          updatedArr,
          q65QLymphopheniaSelectionList,
        );
        setQ65Warnings(updatedWarnings);

        //Q57.1.B
        const qQ571BWarnings = validateQ571BWarnings(updatedArr);
        setQ571BWarnings(qQ571BWarnings);
      }

      if (isNewEcrfC) {
        setQ66Warnings(
          validateQ66Warnings(updatedArr, q66QDizzinessSelectionList),
        );
        setQ67Warnings(
          validateQ67Warnings(updatedArr, q67QAtaxiaSelectionList),
        );
      }

      // QAQC-040 - AE-vs-discontinuation-reason consistency
      const aeQ26WarningsForValidate = validateAllAeQ26Warnings(
        updatedArr,
        aeQ26SelectionLists,
      );
      setAeQ26Warnings(aeQ26WarningsForValidate);

      // QAQC-040 / QAQC-041 / QAQC-042 - the eCRF specifies "do not permit
      // validation of this question" for each of these, so an unresolved
      // warning blocks the page rather than only being displayed.
      //   q26Discontinuation - QAQC-040 (Q26/Q26.1 discontinuation reason)
      //   q38DoseChange      - QAQC-041 (Q38.1.A/Q38.2.A dose change)
      //   q43ManagementUse   - QAQC-042 (Q43.2.A AE management)
      const hasUnresolvedAeQ26Warning = Object.values(
        aeQ26WarningsForValidate ?? {},
      ).some((perCohort) =>
        Object.values(perCohort ?? {}).some(
          (flags) =>
            flags?.q26Discontinuation === true ||
            flags?.q38DoseChange === true ||
            flags?.q43ManagementUse === true,
        ),
      );
      if (hasUnresolvedAeQ26Warning) {
        isValid = false;
      }

      // QAQC-045 Rule B - recomputed on every Validate() pass so it can't
      // go stale.
      const q63SubtypeWarningsForValidate = validateQ63SubtypeWarnings(
        updatedArr,
        Q63NeurotoxicitySubtypeSelectionList,
      );
      setQ63SubtypeWarnings(q63SubtypeWarningsForValidate);

      // QAQC-045 Rule B - the eCRF specifies "do not permit validation of this
      // question", so any subtype without a matching Q63 row blocks the page.
      const hasUnmatchedQ63Subtype = Object.values(
        q63SubtypeWarningsForValidate ?? {},
      ).some((subtypeCodes) => (subtypeCodes ?? []).length > 0);
      if (hasUnmatchedQ63Subtype) {
        isValid = false;
      }

      // QAQC-045 Rule C - recomputed on every Validate() pass so it can't go
      // stale.
      const q63SubtypeWarningsFromQ26ForValidate = validateQ63SubtypeWarnings(
        updatedArr,
        Q63NeurotoxicitySubtypeSelectionListFromQ26,
      );
      setQ63SubtypeWarningsFromQ26(q63SubtypeWarningsFromQ26ForValidate);

      // QAQC-045 Rule C - "the neurologic event subtype must match Q26/Q26.1"
      // - any subtype named there without a matching Q63 row blocks the page.
      const hasUnmatchedQ63SubtypeFromQ26 = Object.values(
        q63SubtypeWarningsFromQ26ForValidate ?? {},
      ).some((subtypeCodes) => (subtypeCodes ?? []).length > 0);
      if (hasUnmatchedQ63SubtypeFromQ26) {
        isValid = false;
      }

      // QAQC-045 Rule D - recomputed on every Validate() pass so it can't go
      // stale.
      const q63SubtypeWarningsFromQ38ForValidate = validateQ63SubtypeWarnings(
        updatedArr,
        Q63NeurotoxicitySubtypeSelectionListFromQ38,
      );
      setQ63SubtypeWarningsFromQ38(q63SubtypeWarningsFromQ38ForValidate);

      // QAQC-045 Rule D - "the neurologic event subtype must match
      // Q38.1.A/Q38.2.A" - any subtype named there without a matching Q63
      // row blocks the page.
      const hasUnmatchedQ63SubtypeFromQ38 = Object.values(
        q63SubtypeWarningsFromQ38ForValidate ?? {},
      ).some((subtypeCodes) => (subtypeCodes ?? []).length > 0);
      if (hasUnmatchedQ63SubtypeFromQ38) {
        isValid = false;
      }

      setAEDetails(updatedArr);
    }

    return isValid;
  };

  function validateQ64Dates(row, cohortIndexDate, aeEndDate) {
    const errors = {
      q64BDateBeforeIndexDateError: false,
      q64BDateAfterCDateError: false,
      q64BDateAfterResolutionDateError: false,
      q64BDateAfterAEDateError: false,

      q64CDateBeforeError: false,
      q64CDateAfterResolutionDateError: false,
      q64CDateAfterAEDateError: false,

      q64DDateBeforeError: false,
      q64DDateAfterAEDateError: false,
    };

    function parseDate(d) {
      if (!d) return null;
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? null
        : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    const Index = parseDate(cohortIndexDate);
    const B = parseDate(row.q64_date_B);
    const C = parseDate(row.q64_date_C);
    const D = parseDate(row.q64_date_D);
    const AE = parseDate(aeEndDate);

    function isAfter(a, b) {
      return a && b && a > b;
    }

    // B validations
    if (B) {
      if (isAfter(Index, B)) errors.q64BDateBeforeIndexDateError = true;
      if (C && isAfter(B, C)) errors.q64BDateAfterCDateError = true;
      if (D && isAfter(B, D)) errors.q64BDateAfterResolutionDateError = true;
      if (AE && isAfter(B, AE)) errors.q64BDateAfterAEDateError = true;
    }

    // C validations
    if (C) {
      // C is validated against B and Index
      if ((Index && isAfter(Index, C)) || (B && isAfter(B, C)))
        errors.q64CDateBeforeError = true;
      if (D && isAfter(C, D)) errors.q64CDateAfterResolutionDateError = true;
      if (AE && isAfter(C, AE)) errors.q64CDateAfterAEDateError = true;
    }

    // D validations
    if (D) {
      // D validated against Index, B  and C
      if (
        (Index && isAfter(Index, D)) ||
        (C && isAfter(C, D)) ||
        (B && isAfter(B, D))
      )
        errors.q64DDateBeforeError = true;
      if (AE && isAfter(D, AE)) errors.q64DDateAfterAEDateError = true;
    }

    return errors;
  }

  function validateQ65Dates(row, cohortIndexDate, aeEndDate) {
    const errors = {
      q65BDateBeforeIndexDateError: false,
      q65BDateAfterAEDateError: false,

      // Date of resolution [C] retired
      q65CDateBeforeError: false,
      q65CDateAfterAEDateError: false,
    };

    function parseDate(d) {
      if (!d) return null;
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? null
        : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    const Index = parseDate(cohortIndexDate);
    const B = parseDate(row.q65_date_B);
    // Date of resolution [C] retired
    // const C = parseDate(row.q65_date_C);
    const AE = parseDate(aeEndDate);

    function isAfter(a, b) {
      return a && b && a > b;
    }

    // B validations
    if (B) {
      if (isAfter(Index, B)) errors.q65BDateBeforeIndexDateError = true;
      if (AE && isAfter(B, AE)) errors.q65BDateAfterAEDateError = true;
    }

    // Date of resolution [C] retired
    /*
    // C validations
    if (C) {
      // C is validated against B and Index
      if ((Index && isAfter(Index, C)) || (B && isAfter(B, C)))
        errors.q65CDateBeforeError = true;
      if (AE && isAfter(C, AE)) errors.q65CDateAfterAEDateError = true;
    }
    */

    return errors;
  }

  function validateQ66Dates(row, cohortIndexDate, aeEndDate) {
    const errors = {
      q66BDateBeforeIndexDateError: false,
      q66BDateAfterAEDateError: false,

      q66CDateBeforeError: false,
      q66CDateAfterAEDateError: false,
    };

    function parseDate(d) {
      if (!d) return null;
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? null
        : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    const Index = parseDate(cohortIndexDate);
    const B = parseDate(row.q66_date_B);
    const C = parseDate(row.q66_date_C);
    const AE = parseDate(aeEndDate);

    function isAfter(a, b) {
      return a && b && a > b;
    }

    // B validations
    if (B) {
      if (isAfter(Index, B)) errors.q66BDateBeforeIndexDateError = true;
      if (AE && isAfter(B, AE)) errors.q66BDateAfterAEDateError = true;
    }

    // C validations
    if (C) {
      // C is validated against B and Index
      if ((Index && isAfter(Index, C)) || (B && isAfter(B, C)))
        errors.q66CDateBeforeError = true;
      if (AE && isAfter(C, AE)) errors.q66CDateAfterAEDateError = true;
    }

    return errors;
  }

  function validateQ67Dates(row, cohortIndexDate, aeEndDate) {
    const errors = {
      q67BDateBeforeIndexDateError: false,
      q67BDateAfterAEDateError: false,

      q67CDateBeforeError: false,
      q67CDateAfterAEDateError: false,
    };

    function parseDate(d) {
      if (!d) return null;
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? null
        : new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    const Index = parseDate(cohortIndexDate);
    const B = parseDate(row.q67_date_B);
    const C = parseDate(row.q67_date_C);
    const AE = parseDate(aeEndDate);

    function isAfter(a, b) {
      return a && b && a > b;
    }

    // B validations
    if (B) {
      if (isAfter(Index, B)) errors.q67BDateBeforeIndexDateError = true;
      if (AE && isAfter(B, AE)) errors.q67BDateAfterAEDateError = true;
    }

    // C validations
    if (C) {
      // C is validated against B and Index
      if ((Index && isAfter(Index, C)) || (B && isAfter(B, C)))
        errors.q67CDateBeforeError = true;
      if (AE && isAfter(C, AE)) errors.q67CDateAfterAEDateError = true;
    }

    return errors;
  }

  const sidebar = document.getElementById("mySidebar");
  const main = document.getElementById("main");

  const toggleNav = () => {
    if (isSideMenuActive === true) {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
      setIsSideMenuActive(false);
    } else if (isSideMenuActive === false) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
      setIsSideMenuActive(true);
    }
  };

  useEffect(() => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive === true) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
    } else {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
    }
  });

  const AutoPopulateQ63FromQ54 = (parentIndex, currentArray) => {
    if (!currentArray || !currentArray[parentIndex]) {
      return currentArray;
    }

    const cohortId = currentArray[parentIndex]?.cohortTreatmentId;
    const isCARVYKTI = cohortId === RRMMCohortTreatment.CARVYKTI;
    const isABECMA = cohortId === RRMMCohortTreatment.ABECMA;
    // UPC-020 - Q63 is also asked for "Tal" under eCRF New Elements C, so the
    // Q54 ICANS auto-population has to run for that cohort too.
    const isTALVEYWithQ63 =
      isNewEcrfC && cohortId === RRMMCohortTreatment.TALVEY;

    if (!isCARVYKTI && !isABECMA && !isTALVEYWithQ63) {
      return currentArray;
    }

    const Q54List = currentArray[parentIndex]?.q54_icans_list ?? [];
    let Q63List = [
      ...(currentArray[parentIndex]?.q63_neurotoxicity_list ?? []),
    ];

    // Valid Q54 ICANS events WITH THEIR INDICES
    const validQ54WithIndices = Q54List.map((item, index) => ({
      item,
      index,
    })).filter(({ item }) => {
      return (
        item &&
        item.rbl_B !== null &&
        item.rbl_B !== 1 &&
        (item.rbl_B === 2 || item.rbl_B === 99) &&
        (item.date_B || item.rbl_B === 99)
      );
    });

    // Track which Q63 rows we've already updated
    const matchedQ63Indices = new Set();

    // Build sets for cleanup
    const validQ54Indices = new Set(
      validQ54WithIndices.map(({ index }) => index),
    );
    const validQ54Ids = new Set(
      validQ54WithIndices
        .map(({ item }) => item.icansId)
        .filter((id) => id != null && id !== undefined && id !== 0),
    );

    // Process each valid Q54 item WITH ITS INDEX
    validQ54WithIndices.forEach(({ item: q54Item, index: q54Index }) => {
      let resolutionRadio = null;
      let resolutionDate = null;
      const rblResolution = q54Item?.rblresolution;

      if (rblResolution != null) {
        if (!isNewEcrfA) {
          resolutionRadio = 99;
        } else {
          resolutionRadio = rblResolution === 4 ? 99 : rblResolution;

          if (rblResolution === 2) {
            resolutionDate = q54Item?.dateresolution ?? null;
          }
        }
      } else if (!isNewEcrfA) {
        resolutionRadio = 99;
      }

      const sourceId = q54Item.icansId ?? null;
      const hasSavedId = sourceId && sourceId !== 0;

      // FIRST: Try to match by Q54 array index (works for unsaved & saved rows)
      let existingQ63Index = Q63List.findIndex(
        (q63, idx) =>
          q63.neurotoxicityId === 0 && // UNSAVED row (newly created)
          q63.isAutoPopulated === true &&
          q63.rbl_D === 2 &&
          q63.sourceQ54Index === q54Index && // Match by index (works for unsaved)
          !matchedQ63Indices.has(idx),
      );

      // SECOND: If no match by index, try by saved icansId
      if (existingQ63Index === -1 && hasSavedId) {
        existingQ63Index = Q63List.findIndex(
          (q63, idx) =>
            q63.neurotoxicityId !== 0 && // SAVED row (has ID from DB)
            q63.rbl_D === 2 && // ICANS type
            q63.sourceQ54IcansId === sourceId && // Match by Q54's saved ID
            !matchedQ63Indices.has(idx),
        );
      }
      if (existingQ63Index !== -1) {
        // UPDATE existing row
        matchedQ63Indices.add(existingQ63Index);

        const existing = Q63List[existingQ63Index];

        Q63List[existingQ63Index] = {
          ...existing,
          rbl_B: q54Item.rbl_B,
          date_B: q54Item.date_B, //  Date gets updated
          rbl_C: resolutionRadio,
          date_C: resolutionDate,
          rbl_D: 2,
          rbl_E: !isNewEcrfA ? 99 : (q54Item?.rblC ?? null),
          isAutoPopulated: true,
          sourceQ54IcansId: hasSavedId
            ? sourceId
            : Q63List[existingQ63Index].sourceQ54IcansId,
          sourceQ54Index: q54Index, //  Always track current index
          sourceQ54Date: q54Item.date_B,

          // Clear validation errors
          B_empty_err: false,
          B_date_empty_err: false,
          B_date_before_err: false,
          B_date_after_err: false,
          B_date_equal_prev_ep_err: false,
          B_date_invalid: false,
          B_date_after_eof: false,
          C_empty_err: false,
          C_date_empty_err: false,
          C_date_before_index_err: false,
          C_date_before_onset_err: false,
          C_date_after_err: false,
          C_date_invalid: false,
          C_date_before_eof: false,
          D_empty_err: false,
          D_txt_empty_err: false,
          E_empty_err: false,
        };
      } else {
        // CREATE new Q63 row
        const newQ63Row = {
          ...q63_neurotoxicity_row,
          neurotoxicityId: 0,
          rbl_B: q54Item.rbl_B,
          date_B: q54Item.date_B,
          rbl_C: resolutionRadio,
          date_C: resolutionDate,
          rbl_D: 2,
          rbl_E: !isNewEcrfA ? 99 : (q54Item?.rblC ?? null),
          isAutoPopulated: true,
          sourceQ54IcansId: hasSavedId ? sourceId : null,
          sourceQ54Index: q54Index, // ✅ Track index for new rows
          sourceQ54Date: q54Item.date_B,
        };

        Q63List.push(newQ63Row);
      }
    });

    // Remove orphaned auto-populated ICANS rows
    Q63List = Q63List.filter((q63) => {
      // Keep manual rows and non-ICANS rows
      if (q63.isAutoPopulated !== true || q63.rbl_D !== 2) {
        return true;
      }

      // Keep auto-ICANS if matched by saved ID
      if (q63.sourceQ54IcansId && q63.sourceQ54IcansId !== 0) {
        return validQ54Ids.has(q63.sourceQ54IcansId);
      }

      // Keep auto-ICANS if matched by index
      if (q63.sourceQ54Index !== null && q63.sourceQ54Index !== undefined) {
        return validQ54Indices.has(q63.sourceQ54Index);
      }

      // Remove if no tracking info
      return false;
    });

    const updatedArray = [...currentArray];
    updatedArray[parentIndex] = {
      ...updatedArray[parentIndex],
      q63_neurotoxicity_list: Q63List,
    };

    return updatedArray;
  };

  return (
    <>
      <section className="survey-patient-details-section">
        <div className="row">
          <PatientDetails />
          <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
            <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
          </div>
        </div>
      </section>
      <section className="main-survey-section">
        <div>
          <SideBarMenu activePageId={SideMenuItems.AdverseEvents} />
          <>
            <button
              className={isSideMenuActive ? "openbtn" : "openbtn active"}
              // onclick="toggleNav()"
              onClick={() => toggleNav()}
            >
              <i
                id="arrowNavigation"
                className="fa fa-arrow-left"
                aria-hidden="true"
              ></i>
            </button>
          </>
          <div className="col-pad-left" id="main">
            <div className="survey-section-content">
              <div className="survey-section-content-header">
                <h4>ADVERSE EVENTS (AEs)</h4>
              </div>
              {showTopErrMsg && (
                <div className="clean-error ">
                  <ErrorField
                    show={showTopErrMsg}
                    message={CommonError_Msgs.Hard_Empty}
                  />
                </div>
              )}
              <div className="survey-question-section">
                <div className="survey-question-content">
                  <div
                    className={isPatientLocked ? "patient-page-disabled" : ""}
                    inert={isPatientLocked}
                  >
                    <div className="question-answer">
                      <div style={{ paddingBottom: "10px" }}>
                        As you review and abstract the patient’s chart, please
                        remember the requirements on AE reporting according to
                        the Study Protocol, Section 5. See{" "}
                        <span
                          data-tooltip-id="encounterTip"
                          className="modal-popup"
                          onClick={() =>
                            setShowHideHyperlinkModalAEReporting(true)
                          }
                        >
                          AE Reporting
                        </span>
                        <span style={{ fontSize: "14px" }}>
                          {showHideHyperlinkModalAEReporting && (
                            <InfoPopUp
                              description={hyperlinkTextAEReporting}
                              OkClick={() =>
                                setShowHideHyperlinkModalAEReporting(false)
                              }
                            />
                          )}{" "}
                        </span>
                        for more information.
                      </div>

                      <div style={{ paddingBottom: "10px" }}>
                        <b>Note:</b> Please report all observed adverse events
                        and their severity as documented in the patient chart.
                        Applicable severity grading criteria include American
                        Society for Transplantation and Cellular Therapy (ASTCT)
                        for cytokine release syndrome (CRS) and immune
                        cell-associated neurotoxicity syndrome (ICANS), and the
                        Common Terminology Criteria for Adverse Events (CTCAE)
                        for all other adverse events, including infections.
                      </div>

                      {aeDetails &&
                        aeDetails.length > 0 &&
                        aeDetails.map((row, idx) => (
                          <div className="dependent-section" key={idx}>
                            <CollapsibleSectionHeader
                              title={
                                <>
                                  {row.lineOfTherapy > 0 &&
                                    `${row.lineOfTherapy}L – `}
                                  {row.cohortTreatmentName}
                                  {!isStringEmpty(row.superScriptText) && (
                                    <>
                                      <sup className="supscript">
                                        {row.superScriptText.slice(0, 2)}
                                      </sup>
                                      {row.superScriptText.slice(2)}
                                    </>
                                  )}
                                </>
                              }
                              isExpanded={!isCohortCollapsed(idx)}
                              onToggle={() => toggleCohortCollapse(idx)}
                              hasError={AeRowHasErrors(row)}
                            />
                            <Collapse in={!isCohortCollapsed(idx)}>
                              <div>
                                <div className="question-bot-sapce">
                                  <span>
                                    Provide information for each adverse event
                                    experienced by the patient during treatment
                                    with{" "}
                                    <span
                                      style={{
                                        color: "steelblue",
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {/* style={{ color: PageDesignConstants.fontColor }} */}
                                      {row.cohortTreatmentName}
                                      {!isStringEmpty(row.superScriptText) && (
                                        <>
                                          <sup className="supscript">
                                            {row.superScriptText.slice(0, 2)}
                                          </sup>
                                          {row.superScriptText.slice(2)}
                                        </>
                                      )}
                                    </span>{" "}
                                    from{" "}
                                    <span
                                      style={{
                                        color: "steelblue",
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {row.cohortIndexDate}
                                    </span>{" "}
                                    to{" "}
                                    <span
                                      style={{
                                        color: "steelblue",
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {row.aeEndDate}
                                    </span>
                                    .
                                    <b>
                                      {" "}
                                      <u>
                                        If the patient experienced &gt;1 episode
                                        of the same type of adverse event,
                                        please add more rows to enter the data
                                        for each episode.
                                      </u>
                                    </b>
                                  </span>
                                </div>

                                {row.q53_crs_list?.some(
                                  (item) => item.ae_ManagementAeWarningTec,
                                ) && (
                                  <div className="question-bot-sapce">
                                    <ErrorField
                                      message={AdverseEvents_Msgs.AEMessages}
                                    />
                                  </div>
                                )}

                                {/* Q53. */}
                                {row.q53_crs_list &&
                                  row.q53_crs_list.length > 0 && (
                                    <>
                                      {/* {row.q53_crs_list && row.q53_crs_list.length > 0 && */}

                                      <div className="question question-weight">
                                        <span>Q53.</span>
                                        <span className="quest-text-pad">
                                          Cytokine release syndrome (CRS)
                                        </span>
                                      </div>

                                      <div>
                                        {" "}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              !!aeQ26Warnings?.[53]?.[
                                                AeKey(row)
                                              ]?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.CrsQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              !!aeQ26Warnings?.[53]?.[
                                                AeKey(row)
                                              ]?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.CrsQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              !!aeQ26Warnings?.[53]?.[
                                                AeKey(row)
                                              ]?.q43ManagementUse
                                            }
                                            message={
                                              AdverseEvents_Msgs.CrsQ43WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q53_crs_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ53Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {isNewEcrfA && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Severity
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q53_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div className="long-text-option">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q53_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding text-nowrap">
                                                                    Event
                                                                    occurred
                                                                    on:{" "}
                                                                  </span>
                                                                </label>
                                                                <span className="date-field-padding">
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "q53_date_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={
                                                                      childIdx
                                                                    }
                                                                    date={
                                                                      childRow.date_B
                                                                    }
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                  />
                                                                </span>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q53_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {isNewEcrfA &&
                                                          childRow.rbl_B !==
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div>
                                                                {/* Radio buttons remain the same */}
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q53rblresolution_${childIdx}_${idx}`}
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q53rblresolution_${childIdx}_${idx}`}
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding text-nowrap">
                                                                      Event
                                                                      resolved
                                                                      on:
                                                                    </span>
                                                                    <span className="date-field-padding">
                                                                      <DatePickerComponent
                                                                        ctrlId={`q53dateresolution_${childIdx}_${idx}`}
                                                                        HandleDateChange={
                                                                          HandleRepeaterDateChange
                                                                        }
                                                                        optionId={
                                                                          childIdx
                                                                        }
                                                                        date={
                                                                          childRow.date_resolution
                                                                        }
                                                                        minDate={dayjs(
                                                                          "01-01-1920",
                                                                        )}
                                                                        maxDate={dayjs()}
                                                                        locale="en"
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q53rblresolution_${childIdx}_${idx}`}
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q53rblresolution_${childIdx}_${idx}`}
                                                                      value={4}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>

                                                              {/* SIMPLIFIED ERROR MESSAGES - ONLY 4 */}
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutionemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DysResDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateinvalid
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondaterangeerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.chronologicalOrder
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          ) : (
                                                            isNewEcrfA && (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            )
                                                          )}

                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 1
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 2
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 3
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          4
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 4
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          5
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 5
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q53_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    childRow.Q53q36WarningMsg
                                                  }
                                                  message={
                                                    AdverseEvents_Msgs.Q53q36CRS
                                                  }
                                                />
                                                <ErrorField
                                                  show={
                                                    childRow.Q53q39WarningMsg
                                                  }
                                                  message={AdverseEvents_Msgs.Q53q39CRS(
                                                    isNewEcrfC,
                                                  )}
                                                />
                                              </div>
                                            ),
                                          )}
                                          {/* )} */}

                                          {/* {row.q53_crs_list && row.q53_crs_list.length < 500 && row.q53_crs_list[row.q53_crs_list.length - 1].rbl_B != null && row.q53_crs_list[row.q53_crs_list.length - 1].rbl_B !== 1 && */}
                                          {row.q53_crs_list.length < 500 &&
                                            row.q53_crs_list[
                                              row.q53_crs_list.length - 1
                                            ].rbl_B != null &&
                                            row.q53_crs_list[
                                              row.q53_crs_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ53Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q54. */}
                                {row.q54_icans_list &&
                                  row.q54_icans_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q54.</span>
                                        <span className="quest-text-pad">
                                          Immune effector cell-associated
                                          neurotoxicity syndrome (ICANS)
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[54]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.IcansQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[54]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.IcansQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[54]?.[AeKey(row)]
                                                ?.q43ManagementUse
                                            }
                                            message={
                                              AdverseEvents_Msgs.IcansQ43WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q54_icans_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ54Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>

                                                          {isNewEcrfA && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}

                                                          {isNewEcrfA && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Severity
                                                            </th>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q54_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div className="long-text-option">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q54_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding text-nowrap">
                                                                    Event
                                                                    occurred
                                                                    on:{" "}
                                                                  </span>
                                                                </label>
                                                                <span className="date-field-padding">
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "q54_date_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={
                                                                      childIdx
                                                                    }
                                                                    date={
                                                                      childRow.date_B
                                                                    }
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                  />
                                                                </span>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q54_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {isNewEcrfA &&
                                                          childRow.rbl_B !==
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblresolution_${childIdx}_${idx}`}
                                                                      value="1"
                                                                      checked={
                                                                        childRow.rblresolution ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblresolution_${childIdx}_${idx}`}
                                                                      value="2"
                                                                      checked={
                                                                        childRow.rblresolution ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding text-nowrap">
                                                                      Event
                                                                      resolved
                                                                      on:
                                                                    </span>
                                                                    <span className="date-field-padding">
                                                                      <DatePickerComponent
                                                                        ctrlId={`q54dateresolution_${childIdx}_${idx}`}
                                                                        HandleDateChange={
                                                                          HandleRepeaterDateChange
                                                                        }
                                                                        optionId={
                                                                          childIdx
                                                                        }
                                                                        date={
                                                                          childRow.dateresolution
                                                                        }
                                                                        minDate={dayjs(
                                                                          "01-01-1920",
                                                                        )}
                                                                        maxDate={dayjs()}
                                                                        locale="en"
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblresolution_${childIdx}_${idx}`}
                                                                      value="3"
                                                                      checked={
                                                                        childRow.rblresolution ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblresolution_${childIdx}_${idx}`}
                                                                      value="4"
                                                                      checked={
                                                                        childRow.rblresolution ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutionemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ResolutionDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateinvalid
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondaterangeerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.chronologicalOrder
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          ) : (
                                                            isNewEcrfA && (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            )
                                                          )}

                                                          {isNewEcrfA &&
                                                          childRow.rbl_B !==
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 1
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 2
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 3
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={4}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 4
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={5}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 5
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q54rblC_${childIdx}_${idx}`}
                                                                      value={99}
                                                                      checked={
                                                                        childRow.rblC ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.Cemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          ) : (
                                                            isNewEcrfA && (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            )
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    childRow.Q54q36WarningMsg
                                                  }
                                                  message={
                                                    AdverseEvents_Msgs.Q54q36ICANS
                                                  }
                                                />
                                              </div>
                                            ),
                                          )}

                                          {row.q54_icans_list.length < 500 &&
                                            row.q54_icans_list[
                                              row.q54_icans_list.length - 1
                                            ].rbl_B != null &&
                                            row.q54_icans_list[
                                              row.q54_icans_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ54Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q55. */}
                                {row.q55_neutropenia_list &&
                                  row.q55_neutropenia_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q55.</span>
                                        <span className="quest-text-pad">
                                          Neutropenia
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[55]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.NeutQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[55]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.NeutQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q55_neutropenia_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ55Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {/* Date of resolution [C] retired */}
                                                          {false && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}

                                                          {isNewEcrfA && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Severity
                                                            </th>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q55_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                {" "}
                                                                {/* className="long-text-option"*/}
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q55_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q55_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q55_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {/* Date of resolution [C] retired */}
                                                          {false &&
                                                            (childRow.rbl_B !=
                                                              null &&
                                                            childRow.rbl_B !==
                                                              1 ? (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <>
                                                                  <div className="">
                                                                    {/* double-dig-answer */}
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q55_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            1
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            1
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          did
                                                                          not
                                                                          resolve
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      {" "}
                                                                      {/* className="long-text-option"*/}
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q55_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            2
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            2
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          resolved
                                                                          on:{" "}
                                                                          {/* </span>
                                                              </label>
                                                              <span className="radio-text-padding"> */}
                                                                          <DatePickerComponent
                                                                            ctrlId={
                                                                              "q55_date_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            HandleDateChange={
                                                                              HandleRepeaterDateChange
                                                                            }
                                                                            optionId={
                                                                              childIdx
                                                                            }
                                                                            date={
                                                                              childRow.date_C
                                                                            }
                                                                            minDate={dayjs(
                                                                              "01-01-1920",
                                                                            )}
                                                                            maxDate={dayjs()}
                                                                            locale="en"
                                                                          />
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q55_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            3
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            3
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          resolved
                                                                          but
                                                                          date
                                                                          unknown
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q55_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            99
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            99
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Unknown
                                                                          whether
                                                                          event
                                                                          resolved
                                                                          or not
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                  </div>
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.RadioNoSelection
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.ResolutionDtEmpty
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_index_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.DateBeforeIndex
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_onset_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.NeutroResolutionBeforeOnset
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_after_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.DtAfterAEEnd
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_invalid
                                                                    }
                                                                    message={
                                                                      CommonError_Msgs.InvalidDate
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_eof
                                                                    }
                                                                    message={
                                                                      TTCharacteristics_MSGS.DateBeforeEOF
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </>
                                                              </td>
                                                            ) : (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  // alignContent: "flex-start",
                                                                  textAlign:
                                                                    "center",
                                                                  padding:
                                                                    "8px",
                                                                  //textAlign: 'start'
                                                                }}
                                                              >
                                                                <span
                                                                  style={{
                                                                    textAlign:
                                                                      "center",
                                                                  }}
                                                                  className="not-applicable"
                                                                >
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            ))}

                                                          {isNewEcrfA &&
                                                            (childRow.rbl_B !==
                                                              null &&
                                                            childRow.rbl_B !==
                                                              1 ? (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 1
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 2
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 3
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          4
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 4
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          5
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 5
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q55rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.Demptyerr
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </td>
                                                            ) : (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            ))}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q55_neutropenia_list.length <
                                            500 &&
                                            row.q55_neutropenia_list[
                                              row.q55_neutropenia_list.length -
                                                1
                                            ].rbl_B != null &&
                                            row.q55_neutropenia_list[
                                              row.q55_neutropenia_list.length -
                                                1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ55Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q56. */}
                                {row.q56_cytopenia_list &&
                                  row.q56_cytopenia_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q56.</span>
                                        <span className="quest-text-pad">
                                          Cytopenia
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[56]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.CytoQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[56]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.CytoQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[56]?.[AeKey(row)]
                                                ?.q43ManagementUse
                                            }
                                            message={
                                              AdverseEvents_Msgs.CytoQ43WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q56_cytopenia_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ56Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {/* Date of resolution [C] retired */}
                                                          {false && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}

                                                          {isNewEcrfA && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Severity
                                                            </th>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q56_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}

                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q56_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    {/* </span>
                                                          </label>
                                                          <span className="radio-text-padding" > */}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q56_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q56_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {/* Date of resolution [C] retired */}
                                                          {false &&
                                                            (childRow.rbl_B !=
                                                              null &&
                                                            childRow.rbl_B !==
                                                              1 ? (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <>
                                                                  <div className="">
                                                                    {/* double-dig-answer */}
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q56_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            1
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            1
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          did
                                                                          not
                                                                          resolve
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q56_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            2
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            2
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          resolved
                                                                          on:{" "}
                                                                          {/* </span>
                                                              </label>
                                                              <span className="radio-text-padding" > */}
                                                                          <DatePickerComponent
                                                                            ctrlId={
                                                                              "q56_date_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            HandleDateChange={
                                                                              HandleRepeaterDateChange
                                                                            }
                                                                            optionId={
                                                                              childIdx
                                                                            }
                                                                            date={
                                                                              childRow.date_C
                                                                            }
                                                                            minDate={dayjs(
                                                                              "01-01-1920",
                                                                            )}
                                                                            maxDate={dayjs()}
                                                                            locale="en"
                                                                          />
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q56_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            3
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            3
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Event
                                                                          resolved
                                                                          but
                                                                          date
                                                                          unknown{" "}
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q56_rbl_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            99
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_C ===
                                                                            99
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Unknown
                                                                          whether
                                                                          event
                                                                          resolved
                                                                          or not
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                  </div>
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.RadioNoSelection
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.ResolutionDtEmpty
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_index_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.DateBeforeIndex
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_onset_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.CytoResolutionBeforeOnset
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_after_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.DtAfterAEEnd
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_invalid
                                                                    }
                                                                    message={
                                                                      CommonError_Msgs.InvalidDate
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.C_date_before_eof
                                                                    }
                                                                    message={
                                                                      TTCharacteristics_MSGS.DateBeforeEOF
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </>
                                                              </td>
                                                            ) : (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  // alignContent: "flex-start",
                                                                  textAlign:
                                                                    "center",
                                                                  padding:
                                                                    "8px",
                                                                  //textAlign: 'start'
                                                                }}
                                                              >
                                                                <span
                                                                  style={{
                                                                    textAlign:
                                                                      "center",
                                                                  }}
                                                                  className="not-applicable"
                                                                >
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            ))}

                                                          {isNewEcrfA &&
                                                            (childRow.rbl_B !==
                                                              null &&
                                                            childRow.rbl_B !==
                                                              1 ? (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <div className="double-dig-answer">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 1
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 2
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 3
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          4
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 4
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          5
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 5
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={`q56rblD_${childIdx}_${idx}`}
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rblD ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.Demptyerr
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </td>
                                                            ) : (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            ))}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q56_cytopenia_list.length <
                                            500 &&
                                            row.q56_cytopenia_list[
                                              row.q56_cytopenia_list.length - 1
                                            ].rbl_B != null &&
                                            row.q56_cytopenia_list[
                                              row.q56_cytopenia_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ56Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q57. */}
                                {row.q57_infection_list &&
                                  row.q57_infection_list.length > 0 && (
                                    <>
                                      <div className="question question-weight calender-datefix-survey">
                                        <span>Q57.</span>
                                        <span className="quest-text-pad">
                                          Infection
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[57]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.InfQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[57]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.InfQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q57_infection_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ57Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div
                                                    className="sub-question grid-overflow"
                                                    style={{
                                                      overflowX: "auto",
                                                    }}
                                                  >
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {isNewEcrfC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Type of infection
                                                          </th>
                                                          {/* Infection organism [D] retired */}
                                                          {false && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Infection organism
                                                            </th>
                                                          )}
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Severity
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Did infection lead
                                                            to hospital, ER, or
                                                            ICU visit?
                                                          </th>
                                                          {(isNewEcrfB ||
                                                            isNewEcrfC) && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Management care
                                                              setting of AE
                                                            </th>
                                                          )}
                                                          {isNewEcrfC && (
                                                            <>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Date of most
                                                                recent IgG
                                                                assessment prior
                                                                to infection
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                IgG level
                                                              </th>
                                                            </>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q57_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q57_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q57_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q57_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_diagnosis_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeDiagnosis
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>
                                                          {isNewEcrfC &&
                                                          childRow.rbl_B !==
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q57rblresolution_${childIdx}_${idx}`}
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q57rblresolution_${childIdx}_${idx}`}
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding text-nowrap">
                                                                      Event
                                                                      resolved
                                                                      on:{" "}
                                                                    </span>
                                                                    <span className="date-field-padding">
                                                                      <DatePickerComponent
                                                                        ctrlId={`q57dateresolution_${childIdx}_${idx}`}
                                                                        HandleDateChange={
                                                                          HandleRepeaterDateChange
                                                                        }
                                                                        optionId={
                                                                          childIdx
                                                                        }
                                                                        date={
                                                                          childRow.date_resolution
                                                                        }
                                                                        minDate={dayjs(
                                                                          "01-01-1920",
                                                                        )}
                                                                        maxDate={dayjs()}
                                                                        locale="en"
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q57rblresolution_${childIdx}_${idx}`}
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={`q57rblresolution_${childIdx}_${idx}`}
                                                                      value={4}
                                                                      checked={
                                                                        childRow.rbl_resolution ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutionemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ResolutionDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondateinvalid
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.resolutiondaterangeerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.chronologicalOrder
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          ) : (
                                                            isNewEcrfC && (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <span className="not-applicable">
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            )
                                                          )}
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_chk_C_1_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.chk_C_1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Bacterial
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_chk_C_2_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.chk_C_2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Viral
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_chk_C_3_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.chk_C_3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Fungal
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rb_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.rb_C
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {/* Infection organism [D] retired */}
                                                          {false &&
                                                            (childRow.rbl_B !=
                                                              null &&
                                                            childRow.rbl_B !==
                                                              1 ? (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <>
                                                                  <div className="">
                                                                    {/* double-dig-answer */}
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_1_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_1
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Adenovirus
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_2_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_2
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Candida
                                                                          (thrush)
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_3_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_3
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          COVID-19
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_4_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_4
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Cytomegalovirus
                                                                          (CMV)
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_5_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_5
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Escherichia
                                                                          Coli
                                                                          (E
                                                                          coli)
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_6_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_6
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Helicobacter
                                                                          pylori
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_7_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_7
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Hepatitis
                                                                          B
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_8_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_8
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Herpes
                                                                          simplex
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_9_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_9
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Influenza
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_10_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_10
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Pneumocystis
                                                                          jiroveci
                                                                          pneumonia
                                                                          [PJP]
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_11_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_11
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Progressive
                                                                          multifocal
                                                                          leukoencephalopathy
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_12_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_12
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Respiratory
                                                                          syncytial
                                                                          virus
                                                                          (RSV)
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_13_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_13
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Streptococcus
                                                                          pneumoniae
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_14_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_14
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Varicella
                                                                          zoster
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="checkbox"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_chk_D_15_" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.chk_D_15
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Others
                                                                          -
                                                                          please
                                                                          specify:{" "}
                                                                          <textarea
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_txt_D_oth" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            type="text"
                                                                            value={
                                                                              childRow.txt_D_oth
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            maxLength={
                                                                              300
                                                                            }
                                                                            className="input-dash"
                                                                            style={{
                                                                              width:
                                                                                "200px",
                                                                            }}
                                                                          />
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_rb_D" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          checked={
                                                                            childRow.rb_D
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Unknown
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                  </div>
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.D_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.D_txt_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.CheckedNoText
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </>
                                                              </td>
                                                            ) : (
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  // alignContent: "flex-start",
                                                                  textAlign:
                                                                    "center",
                                                                  padding:
                                                                    "8px",
                                                                  //textAlign: 'start'
                                                                }}
                                                              >
                                                                <span
                                                                  style={{
                                                                    textAlign:
                                                                      "center",
                                                                  }}
                                                                  className="not-applicable"
                                                                >
                                                                  <b>
                                                                    Not
                                                                    applicable
                                                                  </b>
                                                                </span>
                                                              </td>
                                                            ))}

                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 1
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 2
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 3
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          4
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 4
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          5
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 5
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_E" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_E ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.E_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_F" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_F ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Yes
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_F" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          0
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_F ===
                                                                          0
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        No
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_F" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_F ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.F_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}

                                                          {(isNewEcrfB ||
                                                            isNewEcrfC) && (
                                                            <>
                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_chk_G_1_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Outpatient
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_chk_G_2_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Inpatient
                                                                            admission
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_chk_G_3_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Emergency
                                                                            room
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_chk_G_4_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_4
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Intensive
                                                                            care
                                                                            unit
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_chk_G_5_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_5
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Other,
                                                                            please
                                                                            specify:{" "}
                                                                            <textarea
                                                                              id={
                                                                                childIdx
                                                                              }
                                                                              name={
                                                                                "q57_txt_G_oth" +
                                                                                childIdx +
                                                                                "_" +
                                                                                idx
                                                                              }
                                                                              type="text"
                                                                              value={
                                                                                childRow.txt_G_oth
                                                                              }
                                                                              onChange={
                                                                                HandleRepeaterFieldChange
                                                                              }
                                                                              maxLength={
                                                                                300
                                                                              }
                                                                              className="input-dash"
                                                                              style={{
                                                                                width:
                                                                                  "200px",
                                                                              }}
                                                                            />
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q57_rb_G_unk" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.rb_G_unk
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.G_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.G_txt_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.q57CheckedNoText
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}
                                                            </>
                                                          )}

                                                          {isNewEcrfC && (
                                                            <>
                                                              {childRow.rbl_B ==
                                                                null ||
                                                              childRow.rbl_B ===
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <span className="not-applicable">
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <div className="">
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_rbl_H" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            1
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_H ===
                                                                            1
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          IgG
                                                                          not
                                                                          assessed
                                                                          prior
                                                                          to
                                                                          infection
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_rbl_H" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            2
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_H ===
                                                                            2
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="date-field-padding">
                                                                          <DatePickerComponent
                                                                            ctrlId={
                                                                              "q57_date_H" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            HandleDateChange={
                                                                              HandleRepeaterDateChange
                                                                            }
                                                                            optionId={
                                                                              childIdx
                                                                            }
                                                                            date={
                                                                              childRow.date_H
                                                                            }
                                                                            minDate={dayjs(
                                                                              "01-01-1920",
                                                                            )}
                                                                            maxDate={dayjs()}
                                                                            locale="en"
                                                                          />
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                    <div>
                                                                      <label className="long-text-option">
                                                                        <input
                                                                          type="radio"
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q57_rbl_H" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            99
                                                                          }
                                                                          checked={
                                                                            childRow.rbl_H ===
                                                                            99
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                        />
                                                                        <span className="radio-text-padding">
                                                                          Date
                                                                          unknown
                                                                        </span>
                                                                      </label>
                                                                    </div>
                                                                  </div>
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.H_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.RadioNoSelection
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.H_date_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.IggAssessmentDateEmpty
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.H_date_invalid
                                                                    }
                                                                    message={
                                                                      CommonError_Msgs.InvalidDate
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.H_date_before_diagnosis_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.IggAssessmentDateBeforeDiagnosisError
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.H_date_after_onset_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.IggAssessmentDateAfterOnsetError
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 &&
                                                              childRow.rbl_H !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <div
                                                                    className="option-without-input"
                                                                    style={{
                                                                      display:
                                                                        "flex",
                                                                    }}
                                                                  >
                                                                    <label className="option-fbox">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_I" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_I ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span
                                                                        style={{
                                                                          paddingLeft:
                                                                            "10px",
                                                                          display:
                                                                            "flex",
                                                                          alignItems:
                                                                            "center",
                                                                          flexWrap:
                                                                            "wrap",
                                                                        }}
                                                                      >
                                                                        <input
                                                                          className="sub-input-dash"
                                                                          id={
                                                                            idx
                                                                          }
                                                                          name={
                                                                            "q57_unit_I" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            childRow.rbl_I ===
                                                                            1
                                                                              ? (childRow.unit_I ??
                                                                                "")
                                                                              : ""
                                                                          }
                                                                          type="text"
                                                                          style={{
                                                                            width:
                                                                              "80px",
                                                                            margin:
                                                                              "0 5px",
                                                                          }}
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ57IggUnitFieldChange(
                                                                              e,
                                                                              1,
                                                                            )
                                                                          }
                                                                          onKeyDown={
                                                                            AllowOnlNumeric
                                                                          }
                                                                          maxLength={
                                                                            10
                                                                          }
                                                                        />
                                                                        <span>
                                                                          &nbsp;mg/dL
                                                                        </span>
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div
                                                                    className="option-without-input"
                                                                    style={{
                                                                      display:
                                                                        "flex",
                                                                    }}
                                                                  >
                                                                    <label className="option-fbox">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_I" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_I ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span
                                                                        style={{
                                                                          paddingLeft:
                                                                            "10px",
                                                                          display:
                                                                            "flex",
                                                                          alignItems:
                                                                            "center",
                                                                          flexWrap:
                                                                            "wrap",
                                                                        }}
                                                                      >
                                                                        <input
                                                                          className="sub-input-dash"
                                                                          id={
                                                                            idx
                                                                          }
                                                                          name={
                                                                            "q57_unit_I" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          value={
                                                                            childRow.rbl_I ===
                                                                            2
                                                                              ? (childRow.unit_I ??
                                                                                "")
                                                                              : ""
                                                                          }
                                                                          type="text"
                                                                          style={{
                                                                            width:
                                                                              "80px",
                                                                            margin:
                                                                              "0 5px",
                                                                          }}
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ57IggUnitFieldChange(
                                                                              e,
                                                                              2,
                                                                            )
                                                                          }
                                                                          onKeyDown={
                                                                            AllowOnlNumeric
                                                                          }
                                                                          maxLength={
                                                                            10
                                                                          }
                                                                        />
                                                                        <span>
                                                                          &nbsp;g/L
                                                                        </span>
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q57_rbl_I" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_I ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.I_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.RadioNoSelection
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.I_unit_empty_err
                                                                    }
                                                                    message={
                                                                      AdverseEvents_Msgs.IggLevelUnitEmpty
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                  <ErrorField
                                                                    show={
                                                                      childRow.I_unit_invalid_err
                                                                    }
                                                                    message={
                                                                      childRow.rbl_I ===
                                                                      1
                                                                        ? AdverseEvents_Msgs.IggLevelC1Range
                                                                        : AdverseEvents_Msgs.IggLevelC2Range
                                                                    }
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <span className="not-applicable">
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}
                                                            </>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q57_infection_list.length <
                                            500 &&
                                            row.q57_infection_list[
                                              row.q57_infection_list.length - 1
                                            ].rbl_B != null &&
                                            row.q57_infection_list[
                                              row.q57_infection_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ57Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                {/* Q57.1.B. */}
                                {row.q571B_immunoglobin_list?.length > 0 &&
                                  isNewEcrfB &&
                                  row.q57_infection_list?.some(
                                    (inf) =>
                                      inf.rbl_B != null && inf.rbl_B !== 1,
                                  ) && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q57.1.B.</span>
                                        <span className="quest-text-pad">
                                          Please provide all immunoglobulin G
                                          (IgG) levels that occurred during all
                                          infection-related AEs while on
                                          treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            {getCohortTreatmentDisplayName(
                                              row.cohortTreatmentId,
                                            )}
                                          </span>
                                          . Add additional rows for each
                                          assessment, as needed.
                                        </span>
                                      </div>
                                      <div className="dependent-section">
                                        {(() => {
                                          const warnings =
                                            q571BWarnings?.[AeKey(row)] || {};

                                          return (
                                            <>
                                              <ErrorField
                                                show={
                                                  warnings?.q571BDateWarning ??
                                                  false
                                                }
                                                message={
                                                  AdverseEvents_Msgs.ImmunoglobinWarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                            </>
                                          );
                                        })()}
                                        {row.q571B_immunoglobin_list.map(
                                          (childRow, childIdx) => (
                                            <div
                                              className="loop-section-ul"
                                              key={childIdx}
                                            >
                                              <div className="loop-head">
                                                <span className="question-weight">
                                                  {GetOrdinal(childIdx + 1)}{" "}
                                                  Episode
                                                </span>
                                                {childIdx !== 0 && (
                                                  <div className="delete-btn-div">
                                                    <img
                                                      width="16px"
                                                      src="../Assets/images/Icon-material-delete.png"
                                                      alt=""
                                                      onClick={() =>
                                                        DeleteQ571BRow(
                                                          idx,
                                                          childIdx,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>

                                              <div className="">
                                                <div
                                                  className="sub-question grid-overflow"
                                                  style={{ overflowX: "auto" }}
                                                >
                                                  <table
                                                    style={{
                                                      border: "1px solid black",
                                                      borderCollapse:
                                                        "collapse",
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Treatment
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Date of IgG assessment
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Dosage
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Which AE infection
                                                          episode is this IgG
                                                          level associated with?
                                                        </th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      <tr>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <span>
                                                            {getCohortTreatmentDisplayName(
                                                              row.cohortTreatmentId,
                                                            )}
                                                          </span>
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "start",
                                                          }}
                                                        >
                                                          <div className="">
                                                            {childIdx === 0 && (
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q571B_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={1}
                                                                    checked={
                                                                      childRow.q571B_rbl_B ===
                                                                      1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    IgG not
                                                                    assessed
                                                                    during any
                                                                    infection-related
                                                                    AEs
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            )}
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q571B_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    childRow.q571B_rbl_B ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "q571B_date_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={
                                                                      childIdx
                                                                    }
                                                                    date={
                                                                      childRow.q571B_date_B
                                                                    }
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                  />
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q571B_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={99}
                                                                  checked={
                                                                    childRow.q571B_rbl_B ===
                                                                    99
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  Date unknown
                                                                </span>
                                                              </label>
                                                            </div>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BEmptyError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.RadioNoSelection
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BDateEmptyError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.ImmonoglobinAssessmentDateEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BDateEqualPrevError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.AssessmentDtSameAsPrevEpisodes
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BDateInvalidError
                                                            }
                                                            message={
                                                              CommonError_Msgs.InvalidDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BDateBeforeIndexDateError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.DateBeforeIndex
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q571BDateAfterDateError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.ImmonoglobinAssessmentDtAfterEndDateError
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </td>

                                                        {childRow.q571B_rbl_B !=
                                                          null &&
                                                        childRow.q571B_rbl_B !==
                                                          1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "start",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                <div
                                                                  className="option-without-input"
                                                                  style={{
                                                                    display:
                                                                      "flex",
                                                                  }}
                                                                >
                                                                  <label className="option-fbox">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q571B_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q571B_rbl_C ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span
                                                                      style={{
                                                                        paddingLeft:
                                                                          "10px",
                                                                        display:
                                                                          "flex",
                                                                        alignItems:
                                                                          "center",
                                                                        flexWrap:
                                                                          "wrap",
                                                                      }}
                                                                    >
                                                                      <input
                                                                        className="sub-input-dash"
                                                                        id={idx}
                                                                        name={
                                                                          "q571B_unit_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          childRow.q571B_rbl_C ===
                                                                          1
                                                                            ? (childRow.q571B_unit_C ??
                                                                              "")
                                                                            : ""
                                                                        }
                                                                        type="text"
                                                                        style={{
                                                                          width:
                                                                            "80px",
                                                                          margin:
                                                                            "0 5px",
                                                                        }}
                                                                        onChange={(
                                                                          e,
                                                                        ) =>
                                                                          HandleRepeaterUnitFieldChange(
                                                                            e,
                                                                            1,
                                                                          )
                                                                        }
                                                                        onKeyDown={
                                                                          AllowNumericWithDecimal
                                                                        }
                                                                        maxLength={
                                                                          10
                                                                        }
                                                                      />
                                                                      <span>
                                                                        &nbsp;mg/dL
                                                                      </span>
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div
                                                                  className="option-without-input"
                                                                  style={{
                                                                    display:
                                                                      "flex",
                                                                  }}
                                                                >
                                                                  <label className="option-fbox">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q571B_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.q571B_rbl_C ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span
                                                                      style={{
                                                                        paddingLeft:
                                                                          "10px",
                                                                        display:
                                                                          "flex",
                                                                        alignItems:
                                                                          "center",
                                                                        flexWrap:
                                                                          "wrap",
                                                                      }}
                                                                    >
                                                                      <input
                                                                        className="sub-input-dash"
                                                                        id={idx}
                                                                        name={
                                                                          "q571B_unit_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          childRow.q571B_rbl_C ===
                                                                          2
                                                                            ? (childRow.q571B_unit_C ??
                                                                              "")
                                                                            : ""
                                                                        }
                                                                        type="text"
                                                                        style={{
                                                                          width:
                                                                            "80px",
                                                                          margin:
                                                                            "0 5px",
                                                                        }}
                                                                        onChange={(
                                                                          e,
                                                                        ) =>
                                                                          HandleRepeaterUnitFieldChange(
                                                                            e,
                                                                            2,
                                                                          )
                                                                        }
                                                                        onKeyDown={
                                                                          AllowNumericWithDecimal
                                                                        }
                                                                        maxLength={
                                                                          10
                                                                        }
                                                                      />
                                                                      <span>
                                                                        &nbsp;g/L
                                                                      </span>
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q571B_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.q571B_rbl_C ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.q571CEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q571CUnitEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ImmonoglobinUnitError
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q571CUnitInvalidError
                                                                }
                                                                message={
                                                                  childRow.q571B_rbl_C ===
                                                                  1
                                                                    ? AdverseEvents_Msgs.ImmonoglobiC1Range
                                                                    : AdverseEvents_Msgs.ImmonoglobinC2Range
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        {childRow.q571B_rbl_B !=
                                                          null &&
                                                        childRow.q571B_rbl_B !==
                                                          1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "start",
                                                            }}
                                                          >
                                                            <>
                                                              {row.infectionEpisodeList?.map(
                                                                (
                                                                  episode,
                                                                  epIndex,
                                                                ) => (
                                                                  <div
                                                                    key={
                                                                      episode.infectionKey
                                                                    }
                                                                  >
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q571B_episode_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          episode.infectionKey
                                                                        }
                                                                        checked={
                                                                          childRow.q571B_episode_D ===
                                                                          episode.infectionKey
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        {
                                                                          episode.episodeLabel
                                                                        }
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                ),
                                                              )}
                                                              <ErrorField
                                                                show={
                                                                  childRow.q571DEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}
                                                      </tr>
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )}

                                        {row.q571B_immunoglobin_list.length <
                                          500 &&
                                          row.q571B_immunoglobin_list[
                                            row.q571B_immunoglobin_list.length -
                                              1
                                          ].q571B_rbl_B != null &&
                                          row.q571B_immunoglobin_list[
                                            row.q571B_immunoglobin_list.length -
                                              1
                                          ].q571B_rbl_B !== 1 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ571BRow(
                                                    idx,
                                                    row.cohortTreatmentId,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  episode
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </>
                                  )}
                                {/* Q58.C - Weight trajectory (Tec/Tal only, Element C).
                              Rows are fully auto-generated from the Bispecifics/CAR-T
                              index date (BuildQ58Meta) - the abstractor cannot add or
                              remove rows, and all rows render in a single table. */}
                                {isNewEcrfC && row.q58_weight_list && (
                                  <>
                                    <div className="question question-weight">
                                      <span>Q58.C</span>
                                      <span className="quest-text-pad">
                                        Weight trajectory
                                      </span>
                                    </div>
                                    <div className="sub-question">
                                      <span className="quest-text-pad">
                                        Please provide the patient weight
                                        assessment closest to the given target
                                        date in each row below based on a{" "}
                                        {row.q58_intervalLabel} for the first 12
                                        months after Bispecific treatment
                                        initiation, as applicable (i.e., from{" "}
                                        <span
                                          style={{
                                            color: "steelblue",
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          {row.q58_indexDate}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color: "steelblue",
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          {row.q58_periodEndDate}
                                        </span>
                                        ). The assessment should be within{" "}
                                        {row.q58_windowDays} days of the target
                                        date.
                                      </span>
                                    </div>
                                    {row.q58_weight_list.length === 0 ? (
                                      <div className="sub-question">
                                        <span className="not-applicable">
                                          <b>
                                            Not applicable: No eligible target
                                            dates for assessment.
                                          </b>
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="sub-question grid-overflow">
                                        <table
                                          style={{
                                            backgroundColor: "#fff",
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Target date for assessment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Date of weight assessment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Weight
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {row.q58_weight_list.map(
                                              (childRow, childIdx) => (
                                                <tr
                                                  key={
                                                    childRow.weightRowKey ??
                                                    childIdx
                                                  }
                                                >
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span>
                                                      {getCohortTreatmentDisplayName(
                                                        row.cohortTreatmentId,
                                                      )}
                                                    </span>
                                                  </td>

                                                  {/* Target date [B] - auto-populated, read-only.
                                                Displays "Weight closest to target date of
                                                [target] (and within [target - windowDays]
                                                to MIN[target + windowDays, period end])". */}
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {(() => {
                                                      const window =
                                                        GetQ58Window(childRow);
                                                      return (
                                                        <span>
                                                          Weight closest to
                                                          target date of{" "}
                                                          {
                                                            childRow.targetDate_B
                                                          }
                                                          {window && (
                                                            <>
                                                              {" "}
                                                              (and within{" "}
                                                              {FormatMDY(
                                                                window.windowStart,
                                                              )}{" "}
                                                              to{" "}
                                                              {FormatMDY(
                                                                window.windowEnd,
                                                              )}
                                                              )
                                                            </>
                                                          )}
                                                        </span>
                                                      );
                                                    })()}
                                                  </td>

                                                  {/* Date of weight assessment [C] */}
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <label className="long-text-option">
                                                        <input
                                                          type="radio"
                                                          id={childIdx}
                                                          name={
                                                            "q58_rbl_C" +
                                                            childIdx +
                                                            "_" +
                                                            idx
                                                          }
                                                          value={1}
                                                          checked={
                                                            childRow.rbl_C === 1
                                                          }
                                                          onChange={() =>
                                                            HandleQ58AssessmentChange(
                                                              idx,
                                                              childIdx,
                                                              1,
                                                            )
                                                          }
                                                        />
                                                        <span className="radio-text-padding">
                                                          Weight not assessed
                                                          within{" "}
                                                          {childRow.windowDays}{" "}
                                                          days of target date
                                                        </span>
                                                      </label>
                                                    </div>
                                                    <div>
                                                      <label className="long-text-option">
                                                        <input
                                                          type="radio"
                                                          id={childIdx}
                                                          name={
                                                            "q58_rbl_C" +
                                                            childIdx +
                                                            "_" +
                                                            idx
                                                          }
                                                          value={2}
                                                          checked={
                                                            childRow.rbl_C === 2
                                                          }
                                                          onChange={() =>
                                                            HandleQ58AssessmentChange(
                                                              idx,
                                                              childIdx,
                                                              2,
                                                            )
                                                          }
                                                        />
                                                        <span className="radio-text-padding">
                                                          <DatePickerComponent
                                                            ctrlId={
                                                              "q58_date_C" +
                                                              childIdx +
                                                              "_" +
                                                              idx
                                                            }
                                                            HandleDateChange={(
                                                              _name,
                                                              date,
                                                            ) =>
                                                              HandleQ58DateChange(
                                                                idx,
                                                                childIdx,
                                                                date,
                                                              )
                                                            }
                                                            optionId={childIdx}
                                                            date={
                                                              childRow.date_C
                                                            }
                                                            minDate={dayjs(
                                                              "01-01-1920",
                                                            )}
                                                            maxDate={dayjs()}
                                                            locale="en"
                                                          />
                                                        </span>
                                                      </label>
                                                    </div>
                                                    <div>
                                                      <label className="long-text-option">
                                                        <input
                                                          type="radio"
                                                          id={childIdx}
                                                          name={
                                                            "q58_rbl_C" +
                                                            childIdx +
                                                            "_" +
                                                            idx
                                                          }
                                                          value={99}
                                                          checked={
                                                            childRow.rbl_C ===
                                                            99
                                                          }
                                                          onChange={() =>
                                                            HandleQ58AssessmentChange(
                                                              idx,
                                                              childIdx,
                                                              99,
                                                            )
                                                          }
                                                        />
                                                        <span className="radio-text-padding">
                                                          Date unknown
                                                        </span>
                                                      </label>
                                                    </div>
                                                    <ErrorField
                                                      show={
                                                        childRow.C_empty_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58AssessmentEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_empty_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_invalid
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateInvalid
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_before_index_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateBeforeIndex
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_after_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateAfterPeriod
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_outside_window_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateOutsideWindow
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        childRow.C_date_not_increasing_err
                                                      }
                                                      message={
                                                        AdverseEvents_Msgs.Q58DateNotIncreasing
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                  </td>

                                                  {/* Weight [D] */}
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    {childRow.rbl_C === 1 ? (
                                                      <span className="not-applicable">
                                                        <b>Not applicable</b>
                                                      </span>
                                                    ) : (
                                                      <div>
                                                        <div className="option-without-input">
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              id={childIdx}
                                                              name={
                                                                "q58_rbl_D" +
                                                                childIdx +
                                                                "_" +
                                                                idx
                                                              }
                                                              value={1}
                                                              checked={
                                                                childRow.rbl_D ===
                                                                1
                                                              }
                                                              onChange={() =>
                                                                HandleQ58WeightUnitChange(
                                                                  idx,
                                                                  childIdx,
                                                                  1,
                                                                )
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                                display: "flex",
                                                              }}
                                                            >
                                                              <input
                                                                className="input-dash-ttcustom"
                                                                style={{
                                                                  backgroundColor:
                                                                    "transparent",
                                                                }}
                                                                id={childIdx}
                                                                name={
                                                                  "q58_weightKg_D" +
                                                                  childIdx +
                                                                  "_" +
                                                                  idx
                                                                }
                                                                value={
                                                                  childRow.weightKg_D
                                                                }
                                                                type="text"
                                                                onChange={(e) =>
                                                                  HandleQ58WeightValueChange(
                                                                    idx,
                                                                    childIdx,
                                                                    "weightKg_D",
                                                                    e.target
                                                                      .value,
                                                                  )
                                                                }
                                                                onKeyDown={
                                                                  AllowNumericWithDecimal
                                                                }
                                                                maxLength={10}
                                                              />
                                                              <span>
                                                                &nbsp;kg
                                                              </span>
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="option-without-input">
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              id={childIdx}
                                                              name={
                                                                "q58_rbl_D" +
                                                                childIdx +
                                                                "_" +
                                                                idx
                                                              }
                                                              value={2}
                                                              checked={
                                                                childRow.rbl_D ===
                                                                2
                                                              }
                                                              onChange={() =>
                                                                HandleQ58WeightUnitChange(
                                                                  idx,
                                                                  childIdx,
                                                                  2,
                                                                )
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                                display: "flex",
                                                              }}
                                                            >
                                                              <input
                                                                className="input-dash-ttcustom"
                                                                style={{
                                                                  backgroundColor:
                                                                    "transparent",
                                                                }}
                                                                id={childIdx}
                                                                name={
                                                                  "q58_weightLbs_D" +
                                                                  childIdx +
                                                                  "_" +
                                                                  idx
                                                                }
                                                                value={
                                                                  childRow.weightLbs_D
                                                                }
                                                                type="text"
                                                                onChange={(e) =>
                                                                  HandleQ58WeightValueChange(
                                                                    idx,
                                                                    childIdx,
                                                                    "weightLbs_D",
                                                                    e.target
                                                                      .value,
                                                                  )
                                                                }
                                                                onKeyDown={
                                                                  AllowNumericWithDecimal
                                                                }
                                                                maxLength={10}
                                                              />
                                                              <span>
                                                                &nbsp;lbs
                                                              </span>
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="option-without-input">
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              id={childIdx}
                                                              name={
                                                                "q58_rbl_D" +
                                                                childIdx +
                                                                "_" +
                                                                idx
                                                              }
                                                              value={99}
                                                              checked={
                                                                childRow.rbl_D ===
                                                                99
                                                              }
                                                              onChange={() =>
                                                                HandleQ58WeightUnitChange(
                                                                  idx,
                                                                  childIdx,
                                                                  99,
                                                                )
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <ErrorField
                                                          show={
                                                            childRow.D_empty_err
                                                          }
                                                          message={
                                                            AdverseEvents_Msgs.Q58WeightEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            childRow.D_kg_range_err
                                                          }
                                                          message={
                                                            AdverseEvents_Msgs.Q58WeightKgRange
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            childRow.D_lbs_range_err
                                                          }
                                                          message={
                                                            AdverseEvents_Msgs.Q58WeightLbsRange
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </>
                                )}
                                {/* Q59. */}
                                {row.q59_dysgeusia_list &&
                                  row.q59_dysgeusia_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q59.</span>
                                        <span className="quest-text-pad">
                                          Dysgeusia (including ageusia,
                                          dysgeusia, hypogeusia, and other taste
                                          disorders)
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[59]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.DysQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[59]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.DysQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q59_dysgeusia_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ59Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Intervention
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Earliest date of
                                                            improvement
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of resolution
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q59_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q59_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q59_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_1_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Dexamethasone
                                                                      mouthwash
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_1 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_1_"
                                                                      pChecked={
                                                                        childRow.chk_CC_1_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_1_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_1_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_2_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Nystatin
                                                                      mouthwash
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_2 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_2_"
                                                                      pChecked={
                                                                        childRow.chk_CC_2_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_2_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_2_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_3_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Saline
                                                                      mouthwash
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_3 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_3_"
                                                                      pChecked={
                                                                        childRow.chk_CC_3_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_3_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_3_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_4_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Biotene
                                                                      mouthwash/spray
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_4 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_4_"
                                                                      pChecked={
                                                                        childRow.chk_CC_4_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_4_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_4_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_5_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Zinc
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_5 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_5_"
                                                                      pChecked={
                                                                        childRow.chk_CC_5_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_5_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_5_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_6_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_6
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Vitamin B
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_6 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_6_"
                                                                      pChecked={
                                                                        childRow.chk_CC_6_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_6_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_6_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q59_chk_CC_7_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_CC_7
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Other,
                                                                      please
                                                                      specify:{" "}
                                                                      <textarea
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_txt_CC_oth" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        type="text"
                                                                        value={
                                                                          childRow.txt_CC_oth
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                        maxLength={
                                                                          300
                                                                        }
                                                                        className="input-dash"
                                                                        style={{
                                                                          width:
                                                                            "200px",
                                                                        }}
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                  {childRow.chk_CC_7 && (
                                                                    <Q59ProphylacticTherapeutic
                                                                      parentIndex={
                                                                        idx
                                                                      }
                                                                      childIdx={
                                                                        childIdx
                                                                      }
                                                                      name="q59_chk_CC_7_"
                                                                      pChecked={
                                                                        childRow.chk_CC_7_P
                                                                      }
                                                                      sChecked={
                                                                        childRow.chk_CC_7_T
                                                                      }
                                                                      HandleRepeaterFieldChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      error={
                                                                        childRow.CC_7_empty_err
                                                                      }
                                                                    />
                                                                  )}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rb_CC" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.rb_CC
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.CC_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.CC_txt_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.q59CheckedNoText
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}

                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        The
                                                                        patient
                                                                        did not
                                                                        experience
                                                                        improvement
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Earliest
                                                                        improvement
                                                                        occurred
                                                                        on:{" "}
                                                                        {/* </span>
                                                              </label>
                                                              <span className="radio-text-padding"> */}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q59_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Improvement
                                                                        occurred
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        the
                                                                        patient
                                                                        experienced
                                                                        improvement
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EarlyImpDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DysResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        did not
                                                                        resolve
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q59_date_D" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_D
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q59_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        event
                                                                        resolved
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ResolutionDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DysResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_impr_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DysResBeforeEarlyImp
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q59_dysgeusia_list.length <
                                            500 &&
                                            row.q59_dysgeusia_list[
                                              row.q59_dysgeusia_list.length - 1
                                            ].rbl_B != null &&
                                            row.q59_dysgeusia_list[
                                              row.q59_dysgeusia_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ59Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q60. */}
                                {row.q60_nail_list &&
                                  row.q60_nail_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q60.</span>
                                        <span className="quest-text-pad">
                                          Nail-related disorder
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[60]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.NailQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[60]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.NailQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q60_nail_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ60Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Earliest date of
                                                            improvement
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of resolution
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q60_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q60_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q60_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q60_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        The
                                                                        patient
                                                                        did not
                                                                        experience
                                                                        improvement
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Earliest
                                                                        improvement
                                                                        occurred
                                                                        on:{" "}
                                                                        {/* </span>
                                                              </label>
                                                              <span className="radio-text-padding"> */}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q60_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Improvement
                                                                        occurred
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        the
                                                                        patient
                                                                        experienced
                                                                        improvement
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EarlyImpDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.NailResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        did not
                                                                        resolve
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q60_date_D" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_D
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q60_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        event
                                                                        resolved
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ResolutionDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.NailResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_impr_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.NailResBeforeEarlyImp
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q60_nail_list.length < 500 &&
                                            row.q60_nail_list[
                                              row.q60_nail_list.length - 1
                                            ].rbl_B != null &&
                                            row.q60_nail_list[
                                              row.q60_nail_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ60Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                {/* Q61. */}
                                {row.q61_skin_list &&
                                  row.q61_skin_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q61.</span>
                                        <span className="quest-text-pad">
                                          Skin-related disorder
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        {/*className="question-bot-sapce" */}
                                        <div className="dependent-section">
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[61]?.[AeKey(row)]
                                                ?.q26Discontinuation
                                            }
                                            message={
                                              AdverseEvents_Msgs.SkinQ26WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[61]?.[AeKey(row)]
                                                ?.q38DoseChange
                                            }
                                            message={
                                              AdverseEvents_Msgs.SkinQ38WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q61_skin_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ61Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  {/* question-bot-sapce */}
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Earliest date of
                                                            improvement
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of resolution
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {/* double-dig-answer */}

                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q61_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q61_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q61_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q61_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        The
                                                                        patient
                                                                        did not
                                                                        experience
                                                                        improvement
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Earliest
                                                                        improvement
                                                                        occurred
                                                                        on:{" "}
                                                                        {/* </span>
                                                              </label>
                                                              <span className="radio-text-padding"> */}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q61_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Improvement
                                                                        occurred
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        the
                                                                        patient
                                                                        experienced
                                                                        improvement
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EarlyImpDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.SkinResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.C_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {childRow.rbl_B !=
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        did not
                                                                        resolve
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q61_date_D" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_D
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q61_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_D ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        event
                                                                        resolved
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ResolutionDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_index_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DateBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_onset_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.SkinResolutionBeforeOnset
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_impr_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.SkinResBeforeEarlyImp
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.D_date_before_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q61_skin_list.length < 500 &&
                                            row.q61_skin_list[
                                              row.q61_skin_list.length - 1
                                            ].rbl_B != null &&
                                            row.q61_skin_list[
                                              row.q61_skin_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() => AddQ61Row(idx)}
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                {row.q62_spm_list &&
                                  row.q62_spm_list.length > 0 && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q62.</span>
                                        <span className="quest-text-pad">
                                          Secondary primary malignancy (other
                                          than MM)
                                        </span>
                                      </div>
                                      <div>
                                        {" "}
                                        <div className="dependent-section">
                                          {/* Per the eCRF, Q62 is only cross-checked
                                      against Q43.2.A - the Q26/Q26.1 and
                                      Q38.1.A/Q38.2.A warnings are not
                                      applicable here. */}
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[62]?.[AeKey(row)]
                                                ?.q43ManagementUse
                                            }
                                            message={
                                              AdverseEvents_Msgs.SpmQ43WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q62_spm_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx != 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ62Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "40%",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "60%",
                                                            }}
                                                          >
                                                            Date of
                                                            occurrence/recurrence
                                                          </th>
                                                          {isNewEcrfA && (
                                                            <>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Date of
                                                                resolution
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Severity
                                                              </th>
                                                            </>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q62_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred/recurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}

                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q62_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    onset/recurrence
                                                                    on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q62_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q62_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred/recurred
                                                                    but date of
                                                                    onset/recurrence
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.B_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_empty_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_before_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtAfterAEEnd
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_equal_prev_ep_err
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_invalid
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.B_date_after_eof
                                                              }
                                                              message={
                                                                TTCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>
                                                          {isNewEcrfA && (
                                                            <>
                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              1
                                                                            }
                                                                            checked={
                                                                              childRow.q62_rbl_C ===
                                                                              1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Event
                                                                            did
                                                                            not
                                                                            resolve
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              2
                                                                            }
                                                                            checked={
                                                                              childRow.q62_rbl_C ===
                                                                              2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Event
                                                                            resolved
                                                                            on:{" "}
                                                                            <DatePickerComponent
                                                                              ctrlId={
                                                                                "q62_date_C" +
                                                                                childIdx +
                                                                                "_" +
                                                                                idx
                                                                              }
                                                                              HandleDateChange={
                                                                                HandleRepeaterDateChange
                                                                              }
                                                                              optionId={
                                                                                childIdx
                                                                              }
                                                                              date={
                                                                                childRow.q62_date_C
                                                                              }
                                                                              minDate={dayjs(
                                                                                "01-01-1920",
                                                                              )}
                                                                              maxDate={dayjs()}
                                                                              locale="en"
                                                                            />
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              3
                                                                            }
                                                                            checked={
                                                                              childRow.q62_rbl_C ===
                                                                              3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Event
                                                                            resolved
                                                                            but
                                                                            date
                                                                            unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              99
                                                                            }
                                                                            checked={
                                                                              childRow.q62_rbl_C ===
                                                                              99
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                            whether
                                                                            event
                                                                            resolved
                                                                            or
                                                                            not
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CEmptyError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.RadioNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CDateEmptyError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.ResolutionDtEmpty
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CDateInvalidError
                                                                      }
                                                                      message={
                                                                        CommonError_Msgs.InvalidDate
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CDateBeforeIndexError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.DateBeforeIndex
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CDateBeforeOnsetError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.SPMResolutionBeforeOnset
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62CDateAfterAEDateError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.SPMResolutionBeforeOnset
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              1
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            1
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              2
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            2
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              3
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            3
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              4
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              4
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            4
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              5
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              5
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            5
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q62_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              99
                                                                            }
                                                                            checked={
                                                                              childRow.q62_severity ===
                                                                              99
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.q62DEmptyError
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.RadioNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}
                                                            </>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q62_spm_list.length < 500 &&
                                            row.q62_spm_list[
                                              row.q62_spm_list.length - 1
                                            ].rbl_B != null &&
                                            row.q62_spm_list[
                                              row.q62_spm_list.length - 1
                                            ].rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() =>
                                                    AddQ62Row(
                                                      idx,
                                                      row.cohortTreatmentId,
                                                    )
                                                  }
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                {/* Q63. if A slected */}
                                {row.q63_neurotoxicity_list &&
                                row.q63_neurotoxicity_list.length > 0 &&
                                isNewEcrfA ? (
                                  <>
                                    <div className="question question-weight">
                                      <span>Q63.</span>
                                      <span className="quest-text-pad">
                                        Neurologic event
                                      </span>
                                    </div>

                                    <div>
                                      <b>If ICANS reported above:</b> for any
                                      ICANS events reported in Q54, we have
                                      auto-populated information to allow for
                                      providing additional details on the
                                      management setting, interventions
                                      received, and dates of earliest
                                      improvement.
                                    </div>

                                    <div>
                                      <b>Note:</b> in addition to any ICANS
                                      events, if a patient experienced multiple
                                      other neurologic event subtypes, please
                                      enter one row per event for each subtype
                                      that occurred. Multiple rows of the same
                                      subtype are permitted for unique AE event
                                      episodes.{" "}
                                    </div>
                                    <div>
                                      {" "}
                                      <div className="dependent-section">
                                        <ErrorField
                                          show={
                                            !!aeQ26Warnings?.[63]?.[AeKey(row)]
                                              ?.q26Discontinuation
                                          }
                                          message={
                                            AdverseEvents_Msgs.NeurotoxQ26WarningMessage
                                          }
                                          style={{ padding: "0 0 10px" }}
                                        />
                                        <ErrorField
                                          show={
                                            !!aeQ26Warnings?.[63]?.[AeKey(row)]
                                              ?.q38DoseChange
                                          }
                                          message={
                                            AdverseEvents_Msgs.NeurotoxQ38WarningMessage
                                          }
                                          style={{ padding: "0 0 10px" }}
                                        />
                                        <ErrorField
                                          show={
                                            !!aeQ26Warnings?.[63]?.[AeKey(row)]
                                              ?.q43ManagementUse
                                          }
                                          message={
                                            AdverseEvents_Msgs.NeurotoxQ43WarningMessage
                                          }
                                          style={{ padding: "0 0 10px" }}
                                        />
                                        {row.q63_neurotoxicity_list.length >
                                          0 && (
                                          <ErrorField
                                            show={
                                              row.q63_neurotoxicity_list[0]
                                                ?.Q63q39WarningMsg
                                            }
                                            message={AdverseEvents_Msgs.Q63q39Neurotoxicity(
                                              isNewEcrfC,
                                            )}
                                            style={{ padding: "0 0 10px" }}
                                          />
                                        )}
                                        {(
                                          Q63SubtypeWarnings?.[AeKey(row)] || []
                                        ).map((subtypeCode) => (
                                          <ErrorField
                                            key={"q432a-" + subtypeCode}
                                            show={true}
                                            message={AdverseEvents_Msgs.Q63q432ASubtypeWarning(
                                              Q63SubtypeLabels[subtypeCode],
                                            )}
                                            style={{ padding: "0 0 10px" }}
                                          />
                                        ))}
                                        {(
                                          Q63SubtypeWarningsFromQ26?.[
                                            AeKey(row)
                                          ] || []
                                        ).map((subtypeCode) => (
                                          <ErrorField
                                            key={"q26-" + subtypeCode}
                                            show={true}
                                            message={AdverseEvents_Msgs.Q63q26SubtypeWarning(
                                              Q63SubtypeLabels[subtypeCode],
                                            )}
                                            style={{ padding: "0 0 10px" }}
                                          />
                                        ))}
                                        {(
                                          Q63SubtypeWarningsFromQ38?.[
                                            AeKey(row)
                                          ] || []
                                        ).map((subtypeCode) => (
                                          <ErrorField
                                            key={"q38-" + subtypeCode}
                                            show={true}
                                            message={AdverseEvents_Msgs.Q63q38SubtypeWarning(
                                              Q63SubtypeLabels[subtypeCode],
                                            )}
                                            style={{ padding: "0 0 10px" }}
                                          />
                                        ))}
                                        {row.q63_neurotoxicity_list.map(
                                          (childRow, childIdx) => (
                                            <div
                                              className="loop-section-ul"
                                              key={childIdx}
                                            >
                                              <div className="loop-head">
                                                <span className="question-weight">
                                                  {GetOrdinal(childIdx + 1)}{" "}
                                                  Episode
                                                  {childRow.isAutoPopulated && (
                                                    <span
                                                      style={{
                                                        fontWeight: "normal",
                                                        color: "#1890ff",
                                                      }}
                                                    >
                                                      {
                                                        " - populated from Q54 response"
                                                      }
                                                    </span>
                                                  )}
                                                </span>
                                                {childIdx != 0 &&
                                                  childRow.isAutoPopulated ==
                                                    false && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ63Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                              </div>

                                              <div className="">
                                                {/* question-bot-sapce */}
                                                <div
                                                  className="sub-question grid-overflow"
                                                  style={{ overflowX: "auto" }}
                                                >
                                                  <table
                                                    style={{
                                                      border: "1px solid black",
                                                      borderCollapse:
                                                        "collapse",
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Treatment
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Date of onset
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Date of resolution
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Subtype of neurologic
                                                          event
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Severity
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Management setting of
                                                          neurologic event
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Interventions for
                                                          managing neurologic
                                                          event
                                                        </th>

                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Earliest date of
                                                          improvement
                                                        </th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      <tr>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <span>
                                                            {getCohortTreatmentDisplayName(
                                                              row.cohortTreatmentId,
                                                            )}
                                                          </span>
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <div className="">
                                                            {/* double-dig-answer */}

                                                            {childIdx === 0 && (
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q63_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={1}
                                                                    checked={
                                                                      childRow.rbl_B ===
                                                                      1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                    disabled={
                                                                      childRow.isAutoPopulated
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    No events
                                                                    occurred
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            )}
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q63_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    childRow.rbl_B ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                  disabled={
                                                                    childRow.isAutoPopulated
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  Event occurred
                                                                  on:{" "}
                                                                  <span
                                                                    style={{
                                                                      pointerEvents:
                                                                        childRow.isAutoPopulated
                                                                          ? "none"
                                                                          : "auto",
                                                                      opacity:
                                                                        childRow.isAutoPopulated
                                                                          ? 0.6
                                                                          : 1,
                                                                    }}
                                                                  >
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q63_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q63_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={99}
                                                                  checked={
                                                                    childRow.rbl_B ===
                                                                    99
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                  disabled={
                                                                    childRow.isAutoPopulated
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  Event occurred
                                                                  but onset
                                                                  unknown
                                                                </span>
                                                              </label>
                                                            </div>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              childRow.B_empty_err
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.RadioNoSelection
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_empty_err
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.OnsetDtEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_before_err
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_after_err
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.DtAfterAEEnd
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_equal_prev_ep_err
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_invalid
                                                            }
                                                            message={
                                                              CommonError_Msgs.InvalidDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.B_date_after_eof
                                                            }
                                                            message={
                                                              TTCharacteristics_MSGS.DateBeforeEOF
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </td>

                                                        {childRow.rbl_B !=
                                                          null &&
                                                        childRow.rbl_B !== 1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                {/* double-dig-answer */}
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_C ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rbl_C ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      on:{" "}
                                                                      <span
                                                                        style={{
                                                                          pointerEvents:
                                                                            childRow.isAutoPopulated
                                                                              ? "none"
                                                                              : "auto",
                                                                          opacity:
                                                                            childRow.isAutoPopulated
                                                                              ? 0.6
                                                                              : 1,
                                                                        }}
                                                                      >
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q63_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rbl_C ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.rbl_C ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ResolutionDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_before_index_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DateBeforeIndex
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_before_onset_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.NeuroResolutionBeforeOnset
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_after_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DtAfterAEEnd
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_invalid
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.C_date_before_eof
                                                                }
                                                                message={
                                                                  TTCharacteristics_MSGS.DateBeforeEOF
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              // alignContent: "flex-start",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                              //textAlign: 'start'
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        {childRow.rbl_B !=
                                                          null &&
                                                        childRow.rbl_B !== 1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                {/* double-dig-answer */}
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Peripheral
                                                                      neuropathy
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Immune
                                                                      effector
                                                                      cell-associated
                                                                      neurotoxicity
                                                                      syndrome
                                                                      (ICANS)
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Fever
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={4}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Delayed
                                                                      neurotoxicity
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={8}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        8
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Guillain-Barre
                                                                      Syndrome
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={5}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Parkinsonism
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={6}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        6
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Cranial
                                                                      nerve
                                                                      palsy
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={7}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        7
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Other,
                                                                      please
                                                                      specify:
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div
                                                                  style={{
                                                                    marginLeft:
                                                                      "30px",
                                                                    marginTop:
                                                                      "5px",
                                                                  }}
                                                                >
                                                                  <textarea
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q63_txt_D_oth" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    type="text"
                                                                    value={
                                                                      childRow.txt_D_oth
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                    maxLength={
                                                                      300
                                                                    }
                                                                    className="input-dash"
                                                                    style={{
                                                                      width:
                                                                        "200px",
                                                                    }}
                                                                    disabled={
                                                                      childRow.isAutoPopulated
                                                                    }
                                                                  />

                                                                  <ErrorField
                                                                    show={
                                                                      childRow.D_txt_empty_err
                                                                    }
                                                                    message="Please specify the other neurologic event"
                                                                    style={{
                                                                      paddingLeft:
                                                                        "0px",
                                                                    }}
                                                                  />
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_D" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.rbl_D ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.D_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              // alignContent: "flex-start",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                              //textAlign: 'start'
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        {childRow.rbl_B !=
                                                          null &&
                                                        childRow.rbl_B !== 1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                {/* double-dig-answer */}
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 1
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 2
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 3
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={4}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 4
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={5}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 5
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rbl_E" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.rbl_E ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      disabled={
                                                                        childRow.isAutoPopulated
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.E_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              // alignContent: "flex-start",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                              //textAlign: 'start'
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        {childRow.rbl_B !=
                                                          null &&
                                                        childRow.rbl_B !== 1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                {/* double-dig-answer */}

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63chkFInpatient_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chkFInpatient
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Inpatient
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63chkFEmergencyRoom_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chkFEmergencyRoom
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Emergency
                                                                      Room
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63chkFIntensiveCareUnit_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chkFIntensiveCareUnit
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Intensive
                                                                      Care Unit
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_F_1_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_F_1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Outpatient
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63chkFUrgentCareVisit_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chkFUrgentCareVisit
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Urgent
                                                                      Care Visit
                                                                    </span>
                                                                  </label>
                                                                </div>

                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63chkFOther_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chkFOther
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Other
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rb_F" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.rb_F
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.F_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              // alignContent: "flex-start",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                              //textAlign: 'start'
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        {childRow.rbl_B !=
                                                          null &&
                                                        childRow.rbl_B !== 1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                {/* double-dig-answer */}
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_G_1_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_G_1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Corticosteroids
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_G_2_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_G_2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Anakinra
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_G_3_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_G_3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Tocilizumab
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_G_4_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_G_4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Siltuximab
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="checkbox"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_chk_G_5_" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.chk_G_5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Other
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q63_rb_G" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      checked={
                                                                        childRow.rb_G
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.G_empty_err
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              // alignContent: "flex-start",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                              //textAlign: 'start'
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}

                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "start",
                                                          }}
                                                        >
                                                          {childRow.rbl_B !==
                                                            null &&
                                                          childRow.rbl_B !==
                                                            1 ? (
                                                            <>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={`q63rblH_${childIdx}_${idx}`}
                                                                    value="1"
                                                                    checked={
                                                                      childRow.rblH ===
                                                                      1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    The patient
                                                                    did not
                                                                    experience
                                                                    improvement
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={`q63rblH_${childIdx}_${idx}`}
                                                                    value="2"
                                                                    checked={
                                                                      childRow.rblH ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Earliest
                                                                    improvement
                                                                    occurred on:
                                                                  </span>
                                                                </label>
                                                                <span className="radio-text-padding">
                                                                  <DatePickerComponent
                                                                    ctrlId={`q63dateH_${childIdx}_${idx}`}
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={
                                                                      childIdx
                                                                    }
                                                                    date={
                                                                      childRow.dateH
                                                                    }
                                                                  />
                                                                </span>
                                                              </div>

                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={`q63rblH_${childIdx}_${idx}`}
                                                                    value="3"
                                                                    checked={
                                                                      childRow.rblH ===
                                                                      3
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Improvement
                                                                    occurred but
                                                                    date unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={`q63rblH_${childIdx}_${idx}`}
                                                                    value="99"
                                                                    checked={
                                                                      childRow.rblH ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Unknown
                                                                    whether the
                                                                    patient
                                                                    experienced
                                                                    improvement
                                                                    or not
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <ErrorField
                                                                show={
                                                                  childRow.Hemptyerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdateemptyerr
                                                                }
                                                                message={
                                                                  "Please enter the earliest date of improvement"
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdateinvalid
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdatebeforeindexerr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DateBeforeIndex
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdatebeforeonseterr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.NeuroResolutionBeforeOnset
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdatebeforereserr
                                                                }
                                                                message={`Date should be on or before the ${DateDisplayFormat(
                                                                  childRow.date_C,
                                                                )} (Date of resolution)`}
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdateaftererr
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DtAfterAEEnd
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.Hdatebeforeeof
                                                                }
                                                                message={
                                                                  TTCharacteristics_MSGS.DateBeforeEOF
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          ) : (
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )}

                                        {row.q63_neurotoxicity_list.length <
                                          500 &&
                                          row.q63_neurotoxicity_list[
                                            row.q63_neurotoxicity_list.length -
                                              1
                                          ].rbl_B != null &&
                                          row.q63_neurotoxicity_list[
                                            row.q63_neurotoxicity_list.length -
                                              1
                                          ].rbl_B !== 1 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() => AddQ63Row(idx)}
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  episode
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {" "}
                                    {/* Q63. */}
                                    {row.q63_neurotoxicity_list &&
                                      row.q63_neurotoxicity_list.length > 0 &&
                                      !isNewEcrfA && (
                                        <>
                                          <div className="question question-weight">
                                            <span>Q63.</span>
                                            <span className="quest-text-pad">
                                              Neurotoxicity
                                            </span>
                                          </div>
                                          <div>
                                            <b>If ICANS reported above:</b> for
                                            any ICANS events reported in Q54, we
                                            have auto-populated information to
                                            allow for providing additional
                                            details on the management setting,
                                            interventions received, and dates of
                                            earliest improvement.
                                          </div>

                                          <div>
                                            <b>Note:</b> in addition to any
                                            ICANS events, if a patient
                                            experienced multiple other
                                            neurotoxity subtypes, please enter
                                            one row per event for each subtype
                                            that occurred. Multiple rows of the
                                            same subtype are permitted for
                                            unique AE event episodes.{" "}
                                          </div>
                                          <div>
                                            {" "}
                                            <div className="dependent-section">
                                              <ErrorField
                                                show={
                                                  !!aeQ26Warnings?.[63]?.[
                                                    AeKey(row)
                                                  ]?.q26Discontinuation
                                                }
                                                message={
                                                  AdverseEvents_Msgs.NeurotoxQ26WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  !!aeQ26Warnings?.[63]?.[
                                                    AeKey(row)
                                                  ]?.q38DoseChange
                                                }
                                                message={
                                                  AdverseEvents_Msgs.NeurotoxQ38WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  !!aeQ26Warnings?.[63]?.[
                                                    AeKey(row)
                                                  ]?.q43ManagementUse
                                                }
                                                message={
                                                  AdverseEvents_Msgs.NeurotoxQ43WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                              {row.q63_neurotoxicity_list
                                                .length > 0 && (
                                                <ErrorField
                                                  show={
                                                    row
                                                      .q63_neurotoxicity_list[0]
                                                      ?.Q63q39WarningMsg
                                                  }
                                                  message={AdverseEvents_Msgs.Q63q39Neurotoxicity(
                                                    isNewEcrfC,
                                                  )}
                                                  style={{
                                                    padding: "0 0 10px",
                                                  }}
                                                />
                                              )}
                                              {(
                                                Q63SubtypeWarnings?.[
                                                  AeKey(row)
                                                ] || []
                                              ).map((subtypeCode) => (
                                                <ErrorField
                                                  key={"q432a-" + subtypeCode}
                                                  show={true}
                                                  message={AdverseEvents_Msgs.Q63q432ASubtypeWarning(
                                                    Q63SubtypeLabels[
                                                      subtypeCode
                                                    ],
                                                  )}
                                                  style={{
                                                    padding: "0 0 10px",
                                                  }}
                                                />
                                              ))}
                                              {(
                                                Q63SubtypeWarningsFromQ26?.[
                                                  AeKey(row)
                                                ] || []
                                              ).map((subtypeCode) => (
                                                <ErrorField
                                                  key={"q26-" + subtypeCode}
                                                  show={true}
                                                  message={AdverseEvents_Msgs.Q63q26SubtypeWarning(
                                                    Q63SubtypeLabels[
                                                      subtypeCode
                                                    ],
                                                  )}
                                                  style={{
                                                    padding: "0 0 10px",
                                                  }}
                                                />
                                              ))}
                                              {(
                                                Q63SubtypeWarningsFromQ38?.[
                                                  AeKey(row)
                                                ] || []
                                              ).map((subtypeCode) => (
                                                <ErrorField
                                                  key={"q38-" + subtypeCode}
                                                  show={true}
                                                  message={AdverseEvents_Msgs.Q63q38SubtypeWarning(
                                                    Q63SubtypeLabels[
                                                      subtypeCode
                                                    ],
                                                  )}
                                                  style={{
                                                    padding: "0 0 10px",
                                                  }}
                                                />
                                              ))}
                                              {row.q63_neurotoxicity_list.map(
                                                (childRow, childIdx) => (
                                                  <div
                                                    className="loop-section-ul"
                                                    key={childIdx}
                                                  >
                                                    <div className="loop-head">
                                                      <span className="question-weight">
                                                        {GetOrdinal(
                                                          childIdx + 1,
                                                        )}{" "}
                                                        Episode
                                                        {childRow.isAutoPopulated && (
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                "normal",
                                                              color: "#1890ff",
                                                            }}
                                                          >
                                                            {
                                                              " - populated from Q54 response"
                                                            }
                                                          </span>
                                                        )}
                                                      </span>
                                                      {childIdx != 0 &&
                                                        childRow.isAutoPopulated ==
                                                          false && (
                                                          <div className="delete-btn-div">
                                                            <img
                                                              width="16px"
                                                              src="../Assets/images/Icon-material-delete.png"
                                                              alt=""
                                                              onClick={() =>
                                                                DeleteQ63Row(
                                                                  idx,
                                                                  childIdx,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                        )}
                                                    </div>

                                                    <div className="">
                                                      {/* question-bot-sapce */}
                                                      <div
                                                        className="sub-question"
                                                        style={{
                                                          overflowX: "auto",
                                                        }}
                                                      >
                                                        <table
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            borderCollapse:
                                                              "collapse",
                                                            width: "100%",
                                                          }}
                                                        >
                                                          <thead>
                                                            <tr>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Treatment
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Date of
                                                                neurotoxicity
                                                                onset
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Date of
                                                                neurotoxicity
                                                                resolution
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Subtype of
                                                                neurotoxicity
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Severity
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Management
                                                                setting of
                                                                neurotoxicity
                                                              </th>
                                                              <th
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                Interventions
                                                                for managing
                                                                neurotoxicity
                                                              </th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            <tr>
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <span>
                                                                  {getCohortTreatmentDisplayName(
                                                                    row.cohortTreatmentId,
                                                                  )}
                                                                </span>
                                                              </td>
                                                              <td
                                                                style={{
                                                                  border:
                                                                    "1px solid black",
                                                                  padding:
                                                                    "8px",
                                                                }}
                                                              >
                                                                <div className="">
                                                                  {/* double-dig-answer */}
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q63_rbl_B" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_B ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                        disabled={
                                                                          childRow.isAutoPopulated
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        The
                                                                        patient
                                                                        did not
                                                                        experience
                                                                        neurotoxicity
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q63_rbl_B" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_B ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                        disabled={
                                                                          childRow.isAutoPopulated
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Neurotoxicity
                                                                        onset
                                                                        occurred
                                                                        on:{" "}
                                                                        <span
                                                                          style={{
                                                                            pointerEvents:
                                                                              childRow.isAutoPopulated
                                                                                ? "none"
                                                                                : "auto",
                                                                            opacity:
                                                                              childRow.isAutoPopulated
                                                                                ? 0.6
                                                                                : 1,
                                                                          }}
                                                                        >
                                                                          <DatePickerComponent
                                                                            ctrlId={
                                                                              "q63_date_B" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            HandleDateChange={
                                                                              HandleRepeaterDateChange
                                                                            }
                                                                            optionId={
                                                                              childIdx
                                                                            }
                                                                            date={
                                                                              childRow.date_B
                                                                            }
                                                                            minDate={dayjs(
                                                                              "01-01-1920",
                                                                            )}
                                                                            maxDate={dayjs()}
                                                                            locale="en"
                                                                          />
                                                                        </span>
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q63_rbl_B" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.rbl_B ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                        disabled={
                                                                          childRow.isAutoPopulated
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Neurotoxicity
                                                                        occurred
                                                                        but date
                                                                        of onset
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_empty_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.OnsetDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_before_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.OnsetDtBeforeIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_after_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtAfterAEEnd
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_equal_prev_ep_err
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_invalid
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.B_date_after_eof
                                                                  }
                                                                  message={
                                                                    TTCharacteristics_MSGS.DateBeforeEOF
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </td>

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      {/* double-dig-answer */}
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              1
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_C ===
                                                                              1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            The
                                                                            patient’s
                                                                            neurotoxicity
                                                                            did
                                                                            not
                                                                            resolve
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              2
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_C ===
                                                                              2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Resolution
                                                                            occurred
                                                                            on:{" "}
                                                                            <span
                                                                              style={{
                                                                                pointerEvents:
                                                                                  childRow.isAutoPopulated
                                                                                    ? "none"
                                                                                    : "auto",
                                                                                opacity:
                                                                                  childRow.isAutoPopulated
                                                                                    ? 0.6
                                                                                    : 1,
                                                                              }}
                                                                            >
                                                                              <DatePickerComponent
                                                                                ctrlId={
                                                                                  "q63_date_C" +
                                                                                  childIdx +
                                                                                  "_" +
                                                                                  idx
                                                                                }
                                                                                HandleDateChange={
                                                                                  HandleRepeaterDateChange
                                                                                }
                                                                                optionId={
                                                                                  childIdx
                                                                                }
                                                                                date={
                                                                                  childRow.date_C
                                                                                }
                                                                                minDate={dayjs(
                                                                                  "01-01-1920",
                                                                                )}
                                                                                maxDate={dayjs()}
                                                                                locale="en"
                                                                              />
                                                                            </span>
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              3
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_C ===
                                                                              3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Resolution
                                                                            occurred
                                                                            but
                                                                            date
                                                                            unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_C" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              99
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_C ===
                                                                              99
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                            whether
                                                                            the
                                                                            neurotoxicity
                                                                            event
                                                                            resolved
                                                                            or
                                                                            not
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.RadioNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.ResolutionDtEmpty
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_before_index_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.DateBeforeIndex
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_before_onset_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.NeuroResolutionBeforeOnset
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_after_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.DtAfterAEEnd
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_invalid
                                                                      }
                                                                      message={
                                                                        CommonError_Msgs.InvalidDate
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.C_date_before_eof
                                                                      }
                                                                      message={
                                                                        TTCharacteristics_MSGS.DateBeforeEOF
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    // alignContent: "flex-start",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                    //textAlign: 'start'
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      {/* double-dig-answer */}
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              1
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Peripheral
                                                                            neuropathy
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              2
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Immune
                                                                            effector
                                                                            cell-associated
                                                                            neurotoxicity
                                                                            syndrome
                                                                            (ICANS)
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              3
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Fever
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              4
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              4
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Delayed
                                                                            neurotoxicity
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              8
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              8
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Guillain-Barre
                                                                            Syndrome
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              5
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              5
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Parkinsonism
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              6
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              6
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Cranial
                                                                            nerve
                                                                            palsy
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              7
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              7
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Other,
                                                                            please
                                                                            specify:
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div
                                                                        style={{
                                                                          marginLeft:
                                                                            "30px",
                                                                          marginTop:
                                                                            "5px",
                                                                        }}
                                                                      >
                                                                        <textarea
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q63_txt_D_oth" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          type="text"
                                                                          value={
                                                                            childRow.txt_D_oth
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                          maxLength={
                                                                            300
                                                                          }
                                                                          className="input-dash"
                                                                          style={{
                                                                            width:
                                                                              "200px",
                                                                          }}
                                                                          disabled={
                                                                            childRow.isAutoPopulated
                                                                          }
                                                                        />

                                                                        <ErrorField
                                                                          show={
                                                                            childRow.D_txt_empty_err
                                                                          }
                                                                          message="Please specify the other neurologic event"
                                                                          style={{
                                                                            paddingLeft:
                                                                              "0px",
                                                                          }}
                                                                        />
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_D" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              99
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_D ===
                                                                              99
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.D_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.RadioNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    // alignContent: "flex-start",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                    //textAlign: 'start'
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      {/* double-dig-answer */}
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              1
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            1
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              2
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            2
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              3
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            3
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              4
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              4
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            4
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              5
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              5
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Grade
                                                                            5
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rbl_E" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              99
                                                                            }
                                                                            checked={
                                                                              childRow.rbl_E ===
                                                                              99
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                            disabled={
                                                                              childRow.isAutoPopulated
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.E_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.RadioNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    // alignContent: "flex-start",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                    //textAlign: 'start'
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      {/* double-dig-answer */}
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63chkFInpatient_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chkFInpatient
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Inpatient
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63chkFEmergencyRoom_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chkFEmergencyRoom
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Emergency
                                                                            Room
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63chkFIntensiveCareUnit_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chkFIntensiveCareUnit
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Intensive
                                                                            Care
                                                                            Unit
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_F_1_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_F_1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Outpatient
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63chkFUrgentCareVisit_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chkFUrgentCareVisit
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Urgent
                                                                            Care
                                                                            Visit
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63chkFOther_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chkFOther
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Other
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rb_F" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.rb_F
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.F_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    // alignContent: "flex-start",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                    //textAlign: 'start'
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}

                                                              {childRow.rbl_B !=
                                                                null &&
                                                              childRow.rbl_B !==
                                                                1 ? (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    padding:
                                                                      "8px",
                                                                  }}
                                                                >
                                                                  <>
                                                                    <div className="">
                                                                      {/* double-dig-answer */}
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_G_1_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_1
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Corticosteroids
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_G_2_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_2
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Anakinra
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_G_3_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_3
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Tocilizumab
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_G_4_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_4
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Siltuximab
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_chk_G_5_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.chk_G_5
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Other
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                      <div>
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q63_rb_G" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow.rb_G
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            Unknown
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    </div>
                                                                    <ErrorField
                                                                      show={
                                                                        childRow.G_empty_err
                                                                      }
                                                                      message={
                                                                        AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                      }
                                                                      style={{
                                                                        paddingLeft:
                                                                          "0px",
                                                                      }}
                                                                    />
                                                                  </>
                                                                </td>
                                                              ) : (
                                                                <td
                                                                  style={{
                                                                    border:
                                                                      "1px solid black",
                                                                    // alignContent: "flex-start",
                                                                    textAlign:
                                                                      "center",
                                                                    padding:
                                                                      "8px",
                                                                    //textAlign: 'start'
                                                                  }}
                                                                >
                                                                  <span
                                                                    style={{
                                                                      textAlign:
                                                                        "center",
                                                                    }}
                                                                    className="not-applicable"
                                                                  >
                                                                    <b>
                                                                      Not
                                                                      applicable
                                                                    </b>
                                                                  </span>
                                                                </td>
                                                              )}
                                                            </tr>
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    </div>
                                                  </div>
                                                ),
                                              )}

                                              {row.q63_neurotoxicity_list
                                                .length < 500 &&
                                                row.q63_neurotoxicity_list[
                                                  row.q63_neurotoxicity_list
                                                    .length - 1
                                                ].rbl_B != null &&
                                                row.q63_neurotoxicity_list[
                                                  row.q63_neurotoxicity_list
                                                    .length - 1
                                                ].rbl_B !== 1 && (
                                                  <div className="sub-question-bot-sapce-ul mt-2">
                                                    <div
                                                      className="answer-list-text"
                                                      onClick={() =>
                                                        AddQ63Row(idx)
                                                      }
                                                    >
                                                      <a>
                                                        <img
                                                          width="18px"
                                                          src="../Assets/images/plus.png"
                                                          alt=""
                                                        />
                                                      </a>
                                                      <a className="radio-text-padding">
                                                        Click here to add
                                                        another episode
                                                      </a>
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                  </>
                                )}

                                {/* Q64. */}
                                {row.q64_enterocolitis_list?.length > 0 &&
                                  isNewEcrfA && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q64.</span>
                                        <span className="quest-text-pad">
                                          Enterocolitis
                                        </span>
                                      </div>
                                      <div>
                                        <div className="dependent-section">
                                          {/* Per the eCRF, Q64 is only cross-checked
                                        against Q43.2.A - the Q26/Q26.1 and
                                        Q38.1.A/Q38.2.A warnings are not
                                        applicable here. */}
                                          <ErrorField
                                            show={
                                              aeQ26Warnings?.[64]?.[AeKey(row)]
                                                ?.q43ManagementUse
                                            }
                                            message={
                                              AdverseEvents_Msgs.EntQ43WarningMessage
                                            }
                                            style={{ padding: "0 0 10px" }}
                                          />
                                          {row.q64_enterocolitis_list.map(
                                            (childRow, childIdx) => (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx !== 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ64Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  <div
                                                    className="sub-question grid-overflow"
                                                    style={{
                                                      overflowX: "auto",
                                                    }}
                                                  >
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Earliest date of
                                                            improvement
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of resolution
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Interventions
                                                            received
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Severity
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q64_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q64_rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q64_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.q64_rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q64_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.q64_date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q64_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.q64_rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.OnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateEqualPrevError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateInvalidError
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateBeforeIndexDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DateBeforeIndex
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateAfterCDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.EnterocolitiesDateAfterEarlyImp
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateAfterResolutionDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.EntOnsetAfterResolution
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q64BDateAfterAEDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.EntOnsetAfterAEEndDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {childRow.q64_rbl_B !=
                                                            null &&
                                                          childRow.q64_rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        The
                                                                        patient
                                                                        did not
                                                                        experience
                                                                        improvement
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Earliest
                                                                        improvement
                                                                        occurred
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q64_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.q64_date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Improvement
                                                                        occurred
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        the
                                                                        patient
                                                                        experienced
                                                                        improvement
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CDateEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EarlyImpDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CDateInvalidError
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CDateBeforeError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EntImpDateAfterOnsetAndIndex
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CDateAfterResolutionDateError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EntOnsetAfterResolution
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64CDateAfterAEDateError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EntOnsetAfterAEEndDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                          {childRow.q64_rbl_B !=
                                                            null &&
                                                          childRow.q64_rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_D ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        did not
                                                                        resolve
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_D ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q64_date_D" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.q64_date_D
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_D ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_rbl_D" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.q64_rbl_D ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        event
                                                                        resolved
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64DEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64DDateEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ResolutionDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64DDateInvalidError
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64DDateBeforeError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EntResDateAfterOnsetIndexImp
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64DDateAfterAEDateError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.EntOnsetAfterAEEndDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}

                                                          {childRow.q64_rbl_B !=
                                                            null &&
                                                          childRow.q64_rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_chk_E_1" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_chk_E_1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Supportive
                                                                        care
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_chk_E_2" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_chk_E_2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Antibiotics
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_chk_E_3" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_chk_E_3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Surgery
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_chk_E_4" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_chk_E_4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        G-CSF
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_chk_E_5" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_chk_E_5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Other,
                                                                        please
                                                                        specify:
                                                                        <textarea
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q64_chk_E_othTxt" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          type="text"
                                                                          value={
                                                                            childRow.q64_chk_E_othTxt
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                          maxLength={
                                                                            300
                                                                          }
                                                                          className="input-dash"
                                                                          style={{
                                                                            width:
                                                                              "200px",
                                                                          }}
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_E_unk" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q64_E_unk
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64EEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64EOtherTxtEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.q59CheckedNoText
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}

                                                          {childRow.q64_rbl_B !=
                                                            null &&
                                                          childRow.q64_rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 1
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 2
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 3
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          4
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          4
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 4
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          5
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          5
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Grade 5
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q64_severity" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.q64_severity ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q64FEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                // alignContent: "flex-start",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                                //textAlign: 'start'
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          {row.q64_enterocolitis_list.length <
                                            500 &&
                                            row.q64_enterocolitis_list[
                                              row.q64_enterocolitis_list
                                                .length - 1
                                            ].q64_rbl_B != null &&
                                            row.q64_enterocolitis_list[
                                              row.q64_enterocolitis_list
                                                .length - 1
                                            ].q64_rbl_B !== 1 && (
                                              <div className="sub-question-bot-sapce-ul mt-2">
                                                <div
                                                  className="answer-list-text"
                                                  onClick={() =>
                                                    AddQ64Row(
                                                      idx,
                                                      row.cohortTreatmentId,
                                                    )
                                                  }
                                                >
                                                  <a>
                                                    <img
                                                      width="18px"
                                                      src="../Assets/images/plus.png"
                                                      alt=""
                                                    />
                                                  </a>
                                                  <a className="radio-text-padding">
                                                    Click here to add another
                                                    episode
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                {/* Q65. */}
                                {row.q65_lymphopenia_list?.length > 0 &&
                                  isNewEcrfB && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q65.</span>
                                        <span className="quest-text-pad">
                                          Lymphopenia
                                        </span>
                                      </div>
                                      <div className="dependent-section">
                                        {(() => {
                                          const warnings =
                                            q65Warnings?.[AeKey(row)] || {};

                                          return (
                                            <>
                                              <ErrorField
                                                show={
                                                  warnings?.q26Discontinuation
                                                }
                                                message={
                                                  AdverseEvents_Msgs.LymQ26WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />

                                              <ErrorField
                                                show={warnings?.q38DoseChange}
                                                message={
                                                  AdverseEvents_Msgs.LymQ381AWarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />

                                              <ErrorField
                                                show={warnings?.q43SteroidUse}
                                                message={
                                                  AdverseEvents_Msgs.LymQ432AWarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                            </>
                                          );
                                        })()}
                                        {row.q65_lymphopenia_list.map(
                                          (childRow, childIdx) => (
                                            <div
                                              className="loop-section-ul"
                                              key={childIdx}
                                            >
                                              <div className="loop-head">
                                                <span className="question-weight">
                                                  {GetOrdinal(childIdx + 1)}{" "}
                                                  Episode
                                                </span>
                                                {childIdx !== 0 && (
                                                  <div className="delete-btn-div">
                                                    <img
                                                      width="16px"
                                                      src="../Assets/images/Icon-material-delete.png"
                                                      alt=""
                                                      onClick={() =>
                                                        DeleteQ65Row(
                                                          idx,
                                                          childIdx,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )}
                                              </div>

                                              <div className="">
                                                <div
                                                  className="sub-question grid-overflow"
                                                  style={{ overflowX: "auto" }}
                                                >
                                                  <table
                                                    style={{
                                                      border: "1px solid black",
                                                      borderCollapse:
                                                        "collapse",
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Treatment
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Date of onset
                                                        </th>
                                                        {/* Date of resolution [C] retired */}
                                                        {false && (
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of resolution
                                                          </th>
                                                        )}
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          Severity
                                                        </th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      <tr>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <span>
                                                            {getCohortTreatmentDisplayName(
                                                              row.cohortTreatmentId,
                                                            )}
                                                          </span>
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <div className="">
                                                            {childIdx === 0 && (
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q65_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={1}
                                                                    checked={
                                                                      childRow.q65_rbl_B ===
                                                                      1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    No events
                                                                    occurred
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            )}
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q65_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    childRow.q65_rbl_B ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  Event occurred
                                                                  on:{" "}
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "q65_date_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={
                                                                      childIdx
                                                                    }
                                                                    date={
                                                                      childRow.q65_date_B
                                                                    }
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                  />
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div>
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={childIdx}
                                                                  name={
                                                                    "q65_rbl_B" +
                                                                    childIdx +
                                                                    "_" +
                                                                    idx
                                                                  }
                                                                  value={99}
                                                                  checked={
                                                                    childRow.q65_rbl_B ===
                                                                    99
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span className="radio-text-padding">
                                                                  Event occurred
                                                                  but onset
                                                                  unknown
                                                                </span>
                                                              </label>
                                                            </div>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BEmptyError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.RadioNoSelection
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BDateEmptyError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.OnsetDtEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BDateEqualPrevError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BDateInvalidError
                                                            }
                                                            message={
                                                              CommonError_Msgs.InvalidDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BDateBeforeIndexDateError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.DateBeforeIndex
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              childRow.q65BDateAfterAEDateError
                                                            }
                                                            message={
                                                              AdverseEvents_Msgs.LymOnsetAfterAEEndDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </td>

                                                        {/* Date of resolution [C] retired */}
                                                        {false &&
                                                          (childRow.q65_rbl_B !=
                                                            null &&
                                                          childRow.q65_rbl_B !==
                                                            1 ? (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q65_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          1
                                                                        }
                                                                        checked={
                                                                          childRow.q65_rbl_C ===
                                                                          1
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        did not
                                                                        resolve
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q65_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          2
                                                                        }
                                                                        checked={
                                                                          childRow.q65_rbl_C ===
                                                                          2
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        on:{" "}
                                                                        <DatePickerComponent
                                                                          ctrlId={
                                                                            "q65_date_C" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          HandleDateChange={
                                                                            HandleRepeaterDateChange
                                                                          }
                                                                          optionId={
                                                                            childIdx
                                                                          }
                                                                          date={
                                                                            childRow.q65_date_C
                                                                          }
                                                                          minDate={dayjs(
                                                                            "01-01-1920",
                                                                          )}
                                                                          maxDate={dayjs()}
                                                                          locale="en"
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q65_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          3
                                                                        }
                                                                        checked={
                                                                          childRow.q65_rbl_C ===
                                                                          3
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Event
                                                                        resolved
                                                                        but date
                                                                        unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q65_rbl_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        value={
                                                                          99
                                                                        }
                                                                        checked={
                                                                          childRow.q65_rbl_C ===
                                                                          99
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                        whether
                                                                        event
                                                                        resolved
                                                                        or not
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q65CEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q65CDateEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ResolutionDtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q65CDateInvalidError
                                                                  }
                                                                  message={
                                                                    CommonError_Msgs.InvalidDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q65CDateBeforeError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.LymResDateAfterOnsetIndexImp
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q65CDateAfterAEDateError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.LymOnsetAfterAEEndDate
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          ) : (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                textAlign:
                                                                  "center",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                                className="not-applicable"
                                                              >
                                                                <b>
                                                                  Not applicable
                                                                </b>
                                                              </span>
                                                            </td>
                                                          ))}

                                                        {childRow.q65_rbl_B !=
                                                          null &&
                                                        childRow.q65_rbl_B !==
                                                          1 ? (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <>
                                                              <div className="">
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 1
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 2
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 3
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={4}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        4
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 4
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={5}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        5
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Grade 5
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q65_severity" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.q65_severity ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.q65DEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </>
                                                          </td>
                                                        ) : (
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              textAlign:
                                                                "center",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                              className="not-applicable"
                                                            >
                                                              <b>
                                                                Not applicable
                                                              </b>
                                                            </span>
                                                          </td>
                                                        )}
                                                      </tr>
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )}

                                        {row.q65_lymphopenia_list.length <
                                          500 &&
                                          row.q65_lymphopenia_list[
                                            row.q65_lymphopenia_list.length - 1
                                          ].q65_rbl_B != null &&
                                          row.q65_lymphopenia_list[
                                            row.q65_lymphopenia_list.length - 1
                                          ].q65_rbl_B !== 1 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ65Row(
                                                    idx,
                                                    row.cohortTreatmentId,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  episode
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </>
                                  )}

                                {/* Q66. Dizziness - Element C, "Tal" only */}
                                {row.q66_dizziness_list?.length > 0 &&
                                  isNewEcrfC &&
                                  row.cohortTreatmentId ===
                                    RRMMCohortTreatment.TALVEY && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q66.</span>
                                        <span className="quest-text-pad">
                                          Dizziness
                                        </span>
                                      </div>
                                      <div className="dependent-section">
                                        {(() => {
                                          const warnings =
                                            q66Warnings?.[AeKey(row)] || {};

                                          return (
                                            <>
                                              <ErrorField
                                                show={
                                                  warnings?.q26Discontinuation
                                                }
                                                message={
                                                  AdverseEvents_Msgs.DizQ26WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />

                                              <ErrorField
                                                show={warnings?.q38DoseChange}
                                                message={
                                                  AdverseEvents_Msgs.DizQ381AWarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                            </>
                                          );
                                        })()}
                                        {row.q66_dizziness_list.map(
                                          (childRow, childIdx) => {
                                            //Column [C] is not displayed when "No events occurred" is
                                            //selected (or nothing is selected yet) for this row.
                                            const showColumnC =
                                              childRow.q66_rbl_B != null &&
                                              childRow.q66_rbl_B !== 1;

                                            return (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx !== 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ66Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  <div
                                                    className="sub-question grid-overflow"
                                                    style={{
                                                      overflowX: "auto",
                                                    }}
                                                  >
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {showColumnC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q66_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q66_rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q66_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.q66_rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q66_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.q66_date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q66_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.q66_rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.q66BEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q66BDateEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DizOnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q66BDateEqualPrevError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q66BDateInvalidError
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q66BDateBeforeIndexDateError ||
                                                                childRow.q66BDateAfterAEDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DizOnsetOutOfRange
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {showColumnC && (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div className="">
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q66_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q66_rbl_C ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q66_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.q66_rbl_C ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      on:{" "}
                                                                      <DatePickerComponent
                                                                        ctrlId={
                                                                          "q66_date_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        HandleDateChange={
                                                                          HandleRepeaterDateChange
                                                                        }
                                                                        optionId={
                                                                          childIdx
                                                                        }
                                                                        date={
                                                                          childRow.q66_date_C
                                                                        }
                                                                        minDate={dayjs(
                                                                          "01-01-1920",
                                                                        )}
                                                                        maxDate={dayjs()}
                                                                        locale="en"
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q66_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.q66_rbl_C ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q66_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.q66_rbl_C ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.q66CEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q66CDateEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ResolutionDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q66CDateInvalidError
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q66CDateBeforeError ||
                                                                  childRow.q66CDateAfterAEDateError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.DizResolutionBeforeOnset
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          },
                                        )}

                                        {row.q66_dizziness_list.length < 500 &&
                                          row.q66_dizziness_list[
                                            row.q66_dizziness_list.length - 1
                                          ].q66_rbl_B != null &&
                                          row.q66_dizziness_list[
                                            row.q66_dizziness_list.length - 1
                                          ].q66_rbl_B !== 1 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ66Row(
                                                    idx,
                                                    row.cohortTreatmentId,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  episode
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </>
                                  )}

                                {/* Q67. Ataxia - Element C, "Tal" only */}
                                {row.q67_ataxia_list?.length > 0 &&
                                  isNewEcrfC &&
                                  row.cohortTreatmentId ===
                                    RRMMCohortTreatment.TALVEY && (
                                    <>
                                      <div className="question question-weight">
                                        <span>Q67.</span>
                                        <span className="quest-text-pad">
                                          Ataxia
                                        </span>
                                      </div>
                                      <div className="dependent-section">
                                        {(() => {
                                          const warnings =
                                            q67Warnings?.[AeKey(row)] || {};

                                          return (
                                            <>
                                              <ErrorField
                                                show={
                                                  warnings?.q26Discontinuation
                                                }
                                                message={
                                                  AdverseEvents_Msgs.AtxQ26WarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />

                                              <ErrorField
                                                show={warnings?.q38DoseChange}
                                                message={
                                                  AdverseEvents_Msgs.AtxQ381AWarningMessage
                                                }
                                                style={{ padding: "0 0 10px" }}
                                              />
                                            </>
                                          );
                                        })()}
                                        {row.q67_ataxia_list.map(
                                          (childRow, childIdx) => {
                                            //Column [C] is not displayed when "No events occurred" is
                                            //selected (or nothing is selected yet) for this row.
                                            const showColumnC =
                                              childRow.q67_rbl_B != null &&
                                              childRow.q67_rbl_B !== 1;

                                            return (
                                              <div
                                                className="loop-section-ul"
                                                key={childIdx}
                                              >
                                                <div className="loop-head">
                                                  <span className="question-weight">
                                                    {GetOrdinal(childIdx + 1)}{" "}
                                                    Episode
                                                  </span>
                                                  {childIdx !== 0 && (
                                                    <div className="delete-btn-div">
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        onClick={() =>
                                                          DeleteQ67Row(
                                                            idx,
                                                            childIdx,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="">
                                                  <div
                                                    className="sub-question grid-overflow"
                                                    style={{
                                                      overflowX: "auto",
                                                    }}
                                                  >
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            Date of onset
                                                          </th>
                                                          {showColumnC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Date of resolution
                                                            </th>
                                                          )}
                                                          {showColumnC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Severity
                                                            </th>
                                                          )}
                                                          {showColumnC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Presentation of
                                                              ataxia
                                                            </th>
                                                          )}
                                                          {showColumnC && (
                                                            <th
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              Interventions for
                                                              managing ataxia
                                                              event
                                                            </th>
                                                          )}
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span>
                                                              {getCohortTreatmentDisplayName(
                                                                row.cohortTreatmentId,
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div className="">
                                                              {childIdx ===
                                                                0 && (
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q67_rbl_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q67_rbl_B ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      No events
                                                                      occurred
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              )}
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q67_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      childRow.q67_rbl_B ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred on:{" "}
                                                                    <DatePickerComponent
                                                                      ctrlId={
                                                                        "q67_date_B" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      HandleDateChange={
                                                                        HandleRepeaterDateChange
                                                                      }
                                                                      optionId={
                                                                        childIdx
                                                                      }
                                                                      date={
                                                                        childRow.q67_date_B
                                                                      }
                                                                      minDate={dayjs(
                                                                        "01-01-1920",
                                                                      )}
                                                                      maxDate={dayjs()}
                                                                      locale="en"
                                                                    />
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div>
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={
                                                                      childIdx
                                                                    }
                                                                    name={
                                                                      "q67_rbl_B" +
                                                                      childIdx +
                                                                      "_" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      childRow.q67_rbl_B ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Event
                                                                    occurred but
                                                                    onset
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                childRow.q67BEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.RadioNoSelection
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q67BDateEmptyError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.AtxOnsetDtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q67BDateEqualPrevError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.DtSameAsPrevEpisodes
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q67BDateInvalidError
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                childRow.q67BDateBeforeIndexDateError ||
                                                                childRow.q67BDateAfterAEDateError
                                                              }
                                                              message={
                                                                AdverseEvents_Msgs.AtxOnsetOutOfRange
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </td>

                                                          {showColumnC && (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <div className="">
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q67_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={1}
                                                                      checked={
                                                                        childRow.q67_rbl_C ===
                                                                        1
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event did
                                                                      not
                                                                      resolve
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q67_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={2}
                                                                      checked={
                                                                        childRow.q67_rbl_C ===
                                                                        2
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      on:{" "}
                                                                      <DatePickerComponent
                                                                        ctrlId={
                                                                          "q67_date_C" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        HandleDateChange={
                                                                          HandleRepeaterDateChange
                                                                        }
                                                                        optionId={
                                                                          childIdx
                                                                        }
                                                                        date={
                                                                          childRow.q67_date_C
                                                                        }
                                                                        minDate={dayjs(
                                                                          "01-01-1920",
                                                                        )}
                                                                        maxDate={dayjs()}
                                                                        locale="en"
                                                                      />
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q67_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={3}
                                                                      checked={
                                                                        childRow.q67_rbl_C ===
                                                                        3
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Event
                                                                      resolved
                                                                      but date
                                                                      unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                                <div>
                                                                  <label className="long-text-option">
                                                                    <input
                                                                      type="radio"
                                                                      id={
                                                                        childIdx
                                                                      }
                                                                      name={
                                                                        "q67_rbl_C" +
                                                                        childIdx +
                                                                        "_" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        childRow.q67_rbl_C ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                      whether
                                                                      event
                                                                      resolved
                                                                      or not
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  childRow.q67CEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.RadioNoSelection
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q67CDateEmptyError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.ResolutionDtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q67CDateInvalidError
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  childRow.q67CDateBeforeError ||
                                                                  childRow.q67CDateAfterAEDateError
                                                                }
                                                                message={
                                                                  AdverseEvents_Msgs.AtxResolutionBeforeOnset
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </td>
                                                          )}

                                                          {showColumnC && (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {[
                                                                    [
                                                                      1,
                                                                      "Grade 1",
                                                                    ],
                                                                    [
                                                                      2,
                                                                      "Grade 2",
                                                                    ],
                                                                    [
                                                                      3,
                                                                      "Grade 3",
                                                                    ],
                                                                    [
                                                                      4,
                                                                      "Grade 4",
                                                                    ],
                                                                    [
                                                                      5,
                                                                      "Grade 5",
                                                                    ],
                                                                    [
                                                                      99,
                                                                      "Unknown",
                                                                    ],
                                                                  ].map(
                                                                    ([
                                                                      gradeVal,
                                                                      gradeLabel,
                                                                    ]) => (
                                                                      <div
                                                                        key={
                                                                          gradeVal
                                                                        }
                                                                      >
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="radio"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              "q67_severity" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            value={
                                                                              gradeVal
                                                                            }
                                                                            checked={
                                                                              childRow.q67_severity ===
                                                                              gradeVal
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            {
                                                                              gradeLabel
                                                                            }
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    ),
                                                                  )}
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67DEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.RadioNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          )}

                                                          {showColumnC && (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {[
                                                                    [
                                                                      "q67_chk_E_1",
                                                                      "Dizziness",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_2",
                                                                      "Broad-based/unsteady gait",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_3",
                                                                      "Falls or near-falls",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_4",
                                                                      "Ataxia without clear cerebellar signs",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_5",
                                                                      "Dysarthria (slurred or altered speech)",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_6",
                                                                      "Abnormal eye movements or nystagmus",
                                                                    ],
                                                                    [
                                                                      "q67_chk_E_7",
                                                                      "Paresthesia with imbalance",
                                                                    ],
                                                                  ].map(
                                                                    ([
                                                                      fieldName,
                                                                      label,
                                                                    ]) => (
                                                                      <div
                                                                        key={
                                                                          fieldName
                                                                        }
                                                                      >
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              fieldName +
                                                                              "_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow[
                                                                                fieldName
                                                                              ]
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            {
                                                                              label
                                                                            }
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    ),
                                                                  )}

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q67_chk_E_8" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q67_chk_E_8
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Other,
                                                                        please
                                                                        specify:
                                                                        <textarea
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q67_chk_E_othTxt" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          type="text"
                                                                          value={
                                                                            childRow.q67_chk_E_othTxt
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                          maxLength={
                                                                            300
                                                                          }
                                                                          className="input-dash"
                                                                          style={{
                                                                            width:
                                                                              "200px",
                                                                          }}
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q67_E_unk" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q67_E_unk
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67EEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67EOtherTxtEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.AtxPresentationOtherTxtEmpty
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          )}

                                                          {showColumnC && (
                                                            <td
                                                              style={{
                                                                border:
                                                                  "1px solid black",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              <>
                                                                <div className="">
                                                                  {[
                                                                    [
                                                                      "q67_chk_F_1",
                                                                      "Talquetamab discontinuation",
                                                                    ],
                                                                    [
                                                                      "q67_chk_F_2",
                                                                      "Talquetamab interruption/delay",
                                                                    ],
                                                                    [
                                                                      "q67_chk_F_3",
                                                                      "Talquetamab dose strength or frequency reduction",
                                                                    ],
                                                                    [
                                                                      "q67_chk_F_4",
                                                                      "Supportive medications (e.g., steroids)",
                                                                    ],
                                                                    [
                                                                      "q67_chk_F_5",
                                                                      "Fall-prevention measures",
                                                                    ],
                                                                    [
                                                                      "q67_chk_F_6",
                                                                      "Neurological evaluation",
                                                                    ],
                                                                  ].map(
                                                                    ([
                                                                      fieldName,
                                                                      label,
                                                                    ]) => (
                                                                      <div
                                                                        key={
                                                                          fieldName
                                                                        }
                                                                      >
                                                                        <label className="long-text-option">
                                                                          <input
                                                                            type="checkbox"
                                                                            id={
                                                                              childIdx
                                                                            }
                                                                            name={
                                                                              fieldName +
                                                                              "_" +
                                                                              childIdx +
                                                                              "_" +
                                                                              idx
                                                                            }
                                                                            checked={
                                                                              childRow[
                                                                                fieldName
                                                                              ]
                                                                            }
                                                                            onChange={
                                                                              HandleRepeaterFieldChange
                                                                            }
                                                                          />
                                                                          <span className="radio-text-padding">
                                                                            {
                                                                              label
                                                                            }
                                                                          </span>
                                                                        </label>
                                                                      </div>
                                                                    ),
                                                                  )}

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="checkbox"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q67_chk_F_7" +
                                                                          "_" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q67_chk_F_7
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Other,
                                                                        please
                                                                        specify:
                                                                        <textarea
                                                                          id={
                                                                            childIdx
                                                                          }
                                                                          name={
                                                                            "q67_chk_F_othTxt" +
                                                                            childIdx +
                                                                            "_" +
                                                                            idx
                                                                          }
                                                                          type="text"
                                                                          value={
                                                                            childRow.q67_chk_F_othTxt
                                                                          }
                                                                          onChange={
                                                                            HandleRepeaterFieldChange
                                                                          }
                                                                          maxLength={
                                                                            300
                                                                          }
                                                                          className="input-dash"
                                                                          style={{
                                                                            width:
                                                                              "200px",
                                                                          }}
                                                                        />
                                                                      </span>
                                                                    </label>
                                                                  </div>

                                                                  <div>
                                                                    <label className="long-text-option">
                                                                      <input
                                                                        type="radio"
                                                                        id={
                                                                          childIdx
                                                                        }
                                                                        name={
                                                                          "q67_F_unk" +
                                                                          childIdx +
                                                                          "_" +
                                                                          idx
                                                                        }
                                                                        checked={
                                                                          childRow.q67_F_unk
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                      />
                                                                      <span className="radio-text-padding">
                                                                        Unknown
                                                                      </span>
                                                                    </label>
                                                                  </div>
                                                                </div>
                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67FEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.ChkBoxRadNoSelection
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67FOtherTxtEmptyError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.q59CheckedNoText
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67F1MismatchError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.AtxFDiscontinuationMismatch
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67F2MismatchError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.AtxFInterruptionMismatch
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />

                                                                <ErrorField
                                                                  show={
                                                                    childRow.q67F3MismatchError
                                                                  }
                                                                  message={
                                                                    AdverseEvents_Msgs.AtxFDoseReductionMismatch
                                                                  }
                                                                  style={{
                                                                    paddingLeft:
                                                                      "0px",
                                                                  }}
                                                                />
                                                              </>
                                                            </td>
                                                          )}
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          },
                                        )}

                                        {row.q67_ataxia_list.length < 500 &&
                                          row.q67_ataxia_list[
                                            row.q67_ataxia_list.length - 1
                                          ].q67_rbl_B != null &&
                                          row.q67_ataxia_list[
                                            row.q67_ataxia_list.length - 1
                                          ].q67_rbl_B !== 1 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ67Row(
                                                    idx,
                                                    row.cohortTreatmentId,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  episode
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </>
                                  )}
                              </div>
                            </Collapse>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="survey-section-content-foot">
                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdverseEvents;

const Q59ProphylacticTherapeutic = ({
  parentIndex,
  childIdx,
  name,
  pChecked,
  sChecked,
  HandleRepeaterFieldChange,
  error,
}) => {
  return (
    <div className="radio-text-padding marginLeft15">
      <label className="long-text-option">
        <input
          type="checkbox"
          id={childIdx}
          name={name + "P_" + childIdx + "_" + parentIndex}
          checked={pChecked}
          onChange={HandleRepeaterFieldChange}
        />
        <span className="radio-text-padding">Prophylactic</span>
      </label>
      <label className="long-text-option">
        <input
          type="checkbox"
          id={childIdx}
          name={name + "T_" + childIdx + "_" + parentIndex}
          checked={sChecked}
          onChange={HandleRepeaterFieldChange}
        />
        <span className="radio-text-padding">Therapeutic</span>
      </label>
      <ErrorField
        show={error}
        message={AdverseEvents_Msgs.ChkBoxRadNoSelection}
        style={{ paddingLeft: "0px" }}
      />
    </div>
  );
};
